'use client';

import { readEntitlementFailure, isProjectQuotaError } from '@/lib/entitlement-failure';
import { getUserFacingErrorMessage } from '@/lib/errors/user-facing-error';

import { create, type StoreApi } from 'zustand';
import { useAuthStore } from '@/stores/authStore';
import { getCanvasBindingRevision } from '@/lib/canvas-write-contract';
import {
  VisualEditorState,
  DEFAULT_VISUAL_EDITOR_STATE,
  DEFAULT_SMART_EDIT_BRUSH_SIZE,
  DEFAULT_SMART_EDIT_BRUSH_COLOR,
  CanvasNode,
  CanvasTemplateInstance,
  CanvasTemplateSlotValue,
  Shot,
  Layer,
  CanvasObject,
  ToolType,
  ShotBackground,
  Artboard,
  DEFAULT_ARTBOARD,
  FreeNode,
  CanvasEdge,
  CanvasPromptNode,
  TimelinePanelState,
  SequenceData,
  TimelineState,
  TimelineSegment,
  PendingVideoEntry,
} from '@/types/visual-editor';
import {
  isFloatingNode,
  isPromptNode,
  isSequenceNode,
  getFloatingNodes,
  getPromptNodes,
  findNode,
} from '@/lib/utils/canvas-node-helpers';
import {
  createCanvasActivityTimestamp,
  ensureEnteredCanvasAt,
  ensureLastActivityAt,
} from '@/lib/utils/canvas-node-entry';
import { getTemplateInstanceSlots } from '@/components/visual-editor/workflow/template-instance-runtime';
import {
  buildTemplateWorkflowInputHandleId,
  buildTemplateWorkflowOutputHandleId,
  parseTemplateWorkflowInputHandleId,
  parseTemplateWorkflowOutputIndex,
} from '@/components/visual-editor/workflow/template-workflow-handles';
import { toDisplayMediaUrl } from '@/lib/media-url';
import { getDisplayThumbnailUrl, getPersistableThumbnailUrl } from '@/lib/task-media';
import { normalizeOptionalAssetId } from '@/lib/utils/asset-id';
import { buildCanonicalCanvasEdgeId, buildCanvasEdgeSemanticKey, buildCanvasEdgesSignature } from '@/lib/utils/canvas-edge-identity';
import { buildCanvasNodeMaterializationKey } from '@/lib/utils/canvas-node-materialization';
import { expandCanvasGroupDeletionNodeIds } from '@/lib/utils/canvas-group-deletion';
import { buildCanvasGroupTopology } from '@/lib/utils/canvas-group-topology';
import { loadGroups, saveGroup, removeGroup, updateGroupCollapsed } from '@/lib/utils/group-storage';
import canvasNodeBatchPersistenceRegression from '@/lib/canvas-node-batch-persistence-regression';
import canvasEdgePersistenceRegression from '@/lib/canvas-edge-persistence-regression';
import canvasHistoryUndoRegression from '@/lib/canvas-history-undo-regression';
import storyboardPanelCacheRegression from '@/lib/storyboard-panel-cache-regression';
import { toast, useToastStore } from '@/lib/stores/toast-store';
import type { CanvasNodeRow } from '@/lib/api/canvas-nodes';

const {
  getBatchNodeUpdateRetryDelayMs,
  isCanvasNodeBatchUpdateRetryable,
  mergeCanonicalStoryboardData,
  mergeQueuedCanvasNodeUpdate,
  transitionCanvasNodeBatchQueueAfterFailure,
} = canvasNodeBatchPersistenceRegression;
const {
  buildMissingCanvasEdgeNodeRecoveryPlan,
  resolveCanvasEdgeQueueReset,
} = canvasEdgePersistenceRegression;
const {
  invalidateStoryboardPanelImageCacheForBoardImage,
  normalizeStoryboardMediaUrlForCacheComparison,
} = storyboardPanelCacheRegression;
const {
  mergeCanvasHistoryEdgesForUndo,
  restoreCanvasHistoryChangedFields,
} = canvasHistoryUndoRegression;

const UUID_LIKE_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const _persistedCanvasNodeIds = new Set<string>();
const _confirmedCanvasDeleteTombstones = new Map<string, Set<string>>();

function _recordConfirmedCanvasDeleteTombstones(projectId: string, nodeIds: Iterable<string>) {
  const tombstones = _confirmedCanvasDeleteTombstones.get(projectId) ?? new Set<string>();
  for (const nodeId of nodeIds) {
    if (nodeId) tombstones.add(nodeId);
  }
  if (tombstones.size > 0) _confirmedCanvasDeleteTombstones.set(projectId, tombstones);
}

function _clearConfirmedCanvasDeleteTombstones(projectId: string, nodeIds: Iterable<string>) {
  const tombstones = _confirmedCanvasDeleteTombstones.get(projectId);
  if (!tombstones) return;
  for (const nodeId of nodeIds) tombstones.delete(nodeId);
  if (tombstones.size === 0) _confirmedCanvasDeleteTombstones.delete(projectId);
}

function _filterConfirmedCanvasDeleteTombstones(projectId: string | null | undefined, nodes: CanvasNode[]) {
  if (!projectId) return nodes;
  const tombstones = _confirmedCanvasDeleteTombstones.get(projectId);
  if (!tombstones || tombstones.size === 0) return nodes;
  return nodes.filter((node) => !tombstones.has(node.id));
}

function _markPersistedCanvasNodeIds(nodeIds: Iterable<string | null | undefined>) {
  for (const nodeId of nodeIds) {
    if (typeof nodeId === 'string' && nodeId.length > 0) {
      _persistedCanvasNodeIds.add(nodeId);
    }
  }
}

function _unmarkPersistedCanvasNodeIds(nodeIds: Iterable<string | null | undefined>) {
  for (const nodeId of nodeIds) {
    if (typeof nodeId === 'string' && nodeId.length > 0) {
      _persistedCanvasNodeIds.delete(nodeId);
    }
  }
}

function _resetPersistedCanvasNodeIds() {
  _persistedCanvasNodeIds.clear();
}

function _extractMissingNodeIdsFromEdgeSyncError(error: unknown): string[] {
  const message = error instanceof Error ? error.message : String(error ?? '');
  const jsonStart = message.indexOf('{');
  if (jsonStart === -1) {
    return [];
  }

  try {
    const parsed = JSON.parse(message.slice(jsonStart)) as {
      detail?: { missing_node_ids?: unknown };
    };
    const missingNodeIds = parsed.detail?.missing_node_ids;
    return Array.isArray(missingNodeIds)
      ? missingNodeIds.filter((value): value is string => typeof value === 'string' && value.length > 0)
      : [];
  } catch {
  }

  const missingNodeIdsIndex = message.indexOf('missing_node_ids');
  if (missingNodeIdsIndex === -1) {
    return [];
  }

  return message
    .slice(missingNodeIdsIndex)
    .match(/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi) ?? [];
}

function hasSameSelectedObjectIds(currentIds: string[], nextIds: string[]): boolean {
  if (currentIds.length !== nextIds.length) {
    return false;
  }

  const nextIdSet = new Set(nextIds);
  return currentIds.every((id) => nextIdSet.has(id));
}

// ==========================================
// Store 类型
// ==========================================

interface VisualEditorActions {
  // 初始化
  initialize: (projectId: string, options?: { chatSidebarOpen?: boolean }) => void;
  reset: () => void;
  setProjectName: (projectName: string) => void;
  setProjectRole: (projectRole: VisualEditorState['projectRole']) => void;
  setPendingVideoEntry: (entry: PendingVideoEntry | null) => void;
  registerFocusNewNodesCallback: (fn: ((nodes: CanvasNode[]) => void) | null) => void;
  captureCanvasHistorySnapshot: () => CanvasHistorySnapshot;
  pushCanvasHistorySnapshot: (label: string, snapshot: CanvasHistorySnapshot) => void;
  commitCanvasHistorySnapshot: (label: string, snapshot: CanvasHistorySnapshot) => boolean;
  canUndoCanvasHistory: () => boolean;
  canRedoCanvasHistory: () => boolean;
  undoCanvasHistory: () => Promise<boolean>;
  redoCanvasHistory: () => Promise<boolean>;
  
  // ★ 统一节点操作（替代 setShots/setFreeNodes/setPromptNodes）
  setNodes: (nodes: CanvasNode[]) => void;
  addNodes: (nodes: CanvasNode[], options?: AddNodesOptions) => Promise<void>;
  acknowledgePersistedCanvasNodes: (projectId: string, rows: CanvasNodeRow[]) => boolean;
  removeNode: (nodeId: string) => Promise<void>;
  deleteNodeWithUndo: (nodeId: string, options?: CanvasNodeDeleteOptions) => Promise<boolean>;
  updateNode: (nodeId: string, updates: Partial<CanvasNode>) => void;
  touchNodesActivity: (nodeIds: string[], activityAt?: string) => void;
  flushPendingNodeUpdates: (options?: { keepalive?: boolean }) => Promise<void>;
  flushPendingCanvasEdges: (options?: { keepalive?: boolean }) => Promise<void>;
  updateNodePosition: (nodeId: string, position: { x: number; y: number }) => void;
  persistNodePosition: (nodeId: string, position: { x: number; y: number }) => void;

  // ★ 便捷 getters（从 nodes 派生）
  getFloatingNodes: () => CanvasNode[];
  getPromptNodes: () => CanvasNode[];

  // 序列节点特有操作
  setCurrentShot: (shotId: string) => void;
  updateShotBackground: (shotId: string, background: Partial<ShotBackground>) => void;
  updateShotArtboard: (shotId: string, artboard: Partial<Artboard>) => void;
  updateShotViewport: (shotId: string, viewportTransform: number[]) => void;
  updateShotMask: (shotId: string, foregroundMaskUrl: string) => void;
  replaceShotVideo: (shotId: string, assetId: string) => Promise<void>;
  insertShotsAfter: (afterShotId: string, newShots: CanvasNode[], options?: InsertShotsAfterOptions) => Promise<void>;

  // ★ 批量删除节点
  batchDeleteNodes: (nodeIds: string[], options?: CanvasNodeDeleteOptions) => Promise<boolean>;
  deleteNodesWithUndo: (nodeIds: string[], options?: CanvasNodeDeleteOptions) => Promise<boolean>;
  undoLastNodeDeletion: () => Promise<boolean>;

  // ★ 节点分组（折叠/展开）
  groupNodes: (nodeIds: string[], label?: string) => Promise<boolean>;
  ungroupNodes: (groupNodeId: string) => Promise<boolean>;
  toggleGroupCollapse: (groupNodeId: string) => Promise<boolean>;

  // ★ 节点锁定
  isNodeLocked: (nodeId: string) => boolean;
  toggleNodeLock: (nodeId: string, locked: boolean) => void;
  
  // ★ 画布连线操作
  setCanvasEdges: (edges: CanvasEdge[]) => void;
  waitForCanvasEdgeWrites: (projectId: string, signal?: AbortSignal) => Promise<string | null>;
  isCanvasEdgeHydrationCurrent: (projectId: string, hydrationToken: string) => boolean;
  completeCanvasEdgeHydration: (projectId: string, hydrationToken: string) => boolean;
  cancelCanvasEdgeHydration: (projectId: string, hydrationToken: string) => boolean;
  replaceCanvasEdges: (edges: CanvasEdge[], options?: { sync?: boolean; forceSync?: boolean; extraPersistedNodeIds?: Iterable<string> }) => Promise<void>;
  addCanvasEdge: (edge: CanvasEdge) => void;
  removeCanvasEdge: (edgeId: string) => void;
  // 图层操作
  addLayer: (shotId: string, layer: Layer) => void;
  removeLayer: (shotId: string, layerId: string) => void;
  updateLayer: (shotId: string, layerId: string, updates: Partial<Layer>) => void;
  reorderLayers: (shotId: string, layerIds: string[]) => void;
  
  // 画布对象操作
  addObject: (shotId: string, layerId: string, object: CanvasObject) => void;
  removeObject: (shotId: string, layerId: string, objectId: string) => void;
  updateObject: (shotId: string, layerId: string, objectId: string, updates: Partial<CanvasObject>) => void;
  
  // 选择操作
  selectObjects: (objectIds: string[]) => void;
  clearSelection: () => void;
  
  // 工具操作
  setActiveTool: (tool: ToolType) => void;
  setBrushColor: (color: string) => void;
  setBrushSize: (size: number) => void;
  setFillColor: (color: string) => void;
  setStrokeColor: (color: string) => void;
  setStrokeWidth: (width: number) => void;
  
  // 画布操作
  setZoom: (zoom: number) => void;
  setPan: (x: number, y: number) => void;
  resetView: () => void;
  
  // UI 状态
  setLeftPanelTab: (tab: 'layers') => void;
  setRightPanelTab: (tab: 'background' | 'properties') => void;
  setIsPlaying: (playing: boolean) => void;
  setTimelineState: (timeline: TimelineState) => void;
  setTimelinePanelState: (panelState: TimelinePanelState) => void;
  buildTimelineFromSequence: (sequenceId: string, sequenceData: SequenceData) => void;
  
  // ★ Chat 侧边栏 — 独立开关，不被 activeSidebar 影响
  openChatSidebar: () => void;
  closeChatSidebar: () => void;
  
  // ★ 侧边栏统一管理（非 Chat 面板）
  openSidebar: (sidebar: 'taskHistory' | 'aiCapability' | 'materialPicker' | 'chat' | 'smartEdit', clipId?: string) => void;
  closeSidebar: () => void;
  openTemplateInspector: (nodeId: string) => void;
  exitSmartEditToChat: () => void;
  beginDedicatedSmartEditSession: (nodeId: string) => void;
  closeDedicatedSmartEditSession: () => Promise<boolean>;
  toggleSidebar: (sidebar: 'taskHistory' | 'aiCapability' | 'materialPicker' | 'chat' | 'smartEdit', clipId?: string) => void;
  openSmartEdit: (nodeId: string) => void;
  setSmartEditTool: (tool: 'brush' | 'eraser' | null) => void;
  setSmartEditBrushSize: (size: number) => void;
  setSmartEditBrushColor: (color: string) => void;
  setSmartEditMaskDataUrl: (url: string | null) => void;
  setSmartEditPrompt: (prompt: string) => void;
  setSmartEditGlobalInstruction: (prompt: string) => void;
  syncSmartEditComposerState: (state: {
    pendingCount: number;
    hasLocalChanges: boolean;
    localEditsDone: boolean;
    submitting: boolean;
  }) => void;
  registerSmartEditSubmitHandler: (handler: (() => void | Promise<void>) | null) => void;
  registerSmartEditCloseHandler: (handler: (() => boolean | Promise<boolean>) | null) => void;
  submitSmartEdit: () => void;
  setDraggingToChat: (dragging: boolean) => void;

  // ★ Sequence 聚焦模式
  openSequenceEditor: (nodeId: string) => void;
  closeSequenceEditor: () => void;
  enterSequenceFocus: () => void;
  exitSequenceFocus: () => void;

  // ★ 画布交互模式
  setCanvasInteractionMode: (mode: 'select' | 'pan') => void;
  
  // 加载状态
  setIsLoading: (loading: boolean) => void;
  setIsAnalyzing: (analyzing: boolean) => void;
  setIsSaving: (saving: boolean) => void;
  setError: (error: string | null) => void;
}

interface CanvasNodeDeleteOptions {
  recordDeleteIntent?: boolean;
  historyLabel?: string;
}

type VisualEditorStore = VisualEditorState & VisualEditorActions;
type VisualEditorSetState = StoreApi<VisualEditorStore>['setState'];

interface AddNodesOptions {
  /** Background hydration records rejection facts without opening checkout. */
  background?: boolean;
  focus?: boolean;
  persist?: boolean;
  /** Node row was atomically reserved by another API in this workflow. */
  alreadyPersisted?: boolean;
  /** Explicit all-or-nothing local creation: rollback and reject even task-backed nodes on failure. */
  strictPersistence?: boolean;
  historyLabel?: string;
}

interface InsertShotsAfterOptions extends AddNodesOptions {
  persist?: boolean;
}

// ==========================================
// 辅助函数
// ==========================================

interface DeletedNodeSnapshot {
  nodeIds: string[];
  nodes: CanvasNode[];
  selectedClipIdForAI?: string | null;
  templateInspectorNodeId?: string | null;
  smartEditNodeId?: string | null;
  lockedNodeIds: string[];
}

export interface CanvasHistorySnapshot {
  nodes: CanvasNode[];
  canvasEdges: CanvasEdge[];
  currentShotId: string | null;
  activeSidebar: VisualEditorState['activeSidebar'];
  selectedClipIdForAI: string | null;
  templateInspectorNodeId: string | null;
  smartEditNodeId: string | null;
  lockedNodeIds: string[];
}

interface CanvasHistoryEntry {
  id: string;
  label: string;
  snapshot: CanvasHistorySnapshot;
  afterSnapshot?: CanvasHistorySnapshot;
  pending?: boolean;
}

type CanvasHistoryRestoreResult = 'success' | 'failure' | 'stale';

interface CanvasHistoryRestoreContext {
  projectId: string | null;
  epoch: number;
}

const MAX_CANVAS_HISTORY_ENTRIES = 50;

function generateUuidLike(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = Math.floor(Math.random() * 16);
    const v = ch === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

let _lastDeletedNodeSnapshot: DeletedNodeSnapshot | null = null;
let _canvasHistoryPast: CanvasHistoryEntry[] = [];
let _canvasHistoryFuture: CanvasHistoryEntry[] = [];
let _canvasHistoryEpoch = 0;

/** A project ID can be entered more than once; async readers must bind its entry. */
export function getCanvasProjectEpoch(): number {
  return _canvasHistoryEpoch;
}
let _canvasEdgeQueueContext: CanvasHistoryRestoreContext | null = null;
let _canvasEdgeHydrationLease: (CanvasHistoryRestoreContext & { token: string; active: boolean }) | null = null;
const _canvasEdgeWriteTails = new Map<string, Promise<void>>();
let _focusNewNodesCallback: ((nodes: CanvasNode[]) => void) | null = null;

function _sequenceCanvasEdgeWrite<T>(projectId: string, write: () => Promise<T>): Promise<T> {
  const previousTail = _canvasEdgeWriteTails.get(projectId) ?? Promise.resolve();
  const result = previousTail.then(write, write);
  const settledTail = result.then(() => undefined, () => undefined);
  // Attach synchronously, before `write` can reach a dynamic import or network await.
  _canvasEdgeWriteTails.set(projectId, settledTail);
  void settledTail.then(() => {
    if (_canvasEdgeWriteTails.get(projectId) === settledTail) {
      _canvasEdgeWriteTails.delete(projectId);
    }
  });
  return result;
}

async function _waitForCanvasEdgeWriteDrain(projectId: string, signal?: AbortSignal): Promise<void> {
  while (true) {
    if (signal?.aborted) throw new DOMException('Canvas edge hydration cancelled', 'AbortError');
    const tail = _canvasEdgeWriteTails.get(projectId);
    if (!tail) return;
    await new Promise<void>((resolve, reject) => {
      const handleAbort = () => reject(new DOMException('Canvas edge hydration cancelled', 'AbortError'));
      signal?.addEventListener('abort', handleAbort, { once: true });
      void tail.then(() => {
        signal?.removeEventListener('abort', handleAbort);
        resolve();
      });
    });
    if (_canvasEdgeWriteTails.get(projectId) === tail) return;
  }
}

function _isCanvasEdgeHydrationActive(context: CanvasHistoryRestoreContext): boolean {
  return Boolean(
    _canvasEdgeHydrationLease
    && _canvasEdgeHydrationLease.active
    && areCanvasProjectEpochContextsEqual(_canvasEdgeHydrationLease, context),
  );
}

function _isCanvasEdgeHydrationTokenCurrent(projectId: string, hydrationToken: string): boolean {
  const lease = _canvasEdgeHydrationLease;
  return Boolean(
    lease
    && lease.projectId === projectId
    && lease.token === hydrationToken
    && useVisualEditorStore.getState().projectId === projectId
    && _canvasHistoryEpoch === lease.epoch,
  );
}

function _notifyNewNodesAdded(nodes: CanvasNode[], options?: AddNodesOptions) {
  if (options?.focus === false || nodes.length === 0) {
    return;
  }
  _focusNewNodesCallback?.(nodes);
}

/** Focus a persisted result without adding a second copy or changing its binding. */
export function focusExistingCanvasNodes(projectId: string, nodeIds: string[]): void {
  const state = useVisualEditorStore.getState();
  if (state.projectId !== projectId) return;
  const nodes = state.nodes.filter(node => nodeIds.includes(node.id));
  if (!nodes.length) return;
  state.selectObjects(nodes.map(node => node.id));
  _focusNewNodesCallback?.(nodes);
}

function cloneCanvasHistoryValue<T>(value: T): T {
  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }
  if (value === undefined || value === null) {
    return value;
  }
  return JSON.parse(JSON.stringify(value)) as T;
}

function clearRestoredPersistenceRetryState(node: CanvasNode): CanvasNode {
  if (node.uploadRetryMode !== 'persistence') return node;
  // 删除会释放内存中的 retry action。Undo 通过 history 的 batchCreate 重新落库，
  // 因此不能恢复一个没有可执行 action 的“持久化重试”按钮或把错误写回 metadata。
  return {
    ...node,
    isUploading: false,
    uploadProgress: undefined,
    uploadStage: undefined,
    uploadEtaSeconds: undefined,
    generatingError: undefined,
    generatingErrorKind: undefined,
    canRetryUpload: undefined,
    uploadRetryMode: undefined,
  };
}

function captureCanvasHistorySnapshot(state: VisualEditorStore): CanvasHistorySnapshot {
  return {
    nodes: cloneCanvasHistoryValue(state.nodes.map(node => ({ ...node,
      mediaBindingRevision: node.mediaBindingRevision ?? getCanvasBindingRevision(node.id),
    }))),
    canvasEdges: cloneCanvasHistoryValue(state.canvasEdges),
    currentShotId: state.currentShotId,
    activeSidebar: state.activeSidebar,
    selectedClipIdForAI: state.selectedClipIdForAI,
    templateInspectorNodeId: state.templateInspectorNodeId,
    smartEditNodeId: state.smartEditNodeId,
    lockedNodeIds: [...state.lockedNodeIds],
  };
}

function rememberCanvasHistoryEntry(entry: CanvasHistoryEntry) {
  _canvasHistoryPast = [..._canvasHistoryPast, entry].slice(-MAX_CANVAS_HISTORY_ENTRIES);
  _canvasHistoryFuture = [];
}

function areCanvasHistorySnapshotsEqual(left: CanvasHistorySnapshot, right: CanvasHistorySnapshot): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function areCanvasHistoryValuesEqual<T>(left: T, right: T): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function areViewportTransformsEqual(leftTransform: number[] | undefined, rightTransform: number[] | undefined): boolean {
  if (leftTransform === rightTransform) {
    return true;
  }
  if (!leftTransform || !rightTransform || leftTransform.length !== rightTransform.length) {
    return false;
  }
  return leftTransform.every((value, index) => Object.is(value, rightTransform[index]));
}

function areArtboardsEqual(leftArtboard: Artboard | undefined, rightArtboard: Artboard | undefined): boolean {
  if (leftArtboard === rightArtboard) {
    return true;
  }
  if (!leftArtboard || !rightArtboard) {
    return false;
  }
  return Object.is(leftArtboard.x, rightArtboard.x)
    && Object.is(leftArtboard.y, rightArtboard.y)
    && Object.is(leftArtboard.width, rightArtboard.width)
    && Object.is(leftArtboard.height, rightArtboard.height);
}

function rememberCanvasHistorySnapshotIfChanged(
  label: string,
  snapshot: CanvasHistorySnapshot,
  currentState: VisualEditorStore,
): boolean {
  const afterSnapshot = captureCanvasHistorySnapshot(currentState);
  if (areCanvasHistorySnapshotsEqual(snapshot, afterSnapshot)) {
    return false;
  }

  rememberCanvasHistoryEntry({
    id: generateUuidLike(),
    label,
    snapshot: cloneCanvasHistoryValue(snapshot),
    afterSnapshot: cloneCanvasHistoryValue(afterSnapshot),
  });
  return true;
}

function clearCanvasHistory() {
  _canvasHistoryPast = [];
  _canvasHistoryFuture = [];
  _canvasHistoryEpoch += 1;
}

function isCanvasHistoryRestoreContextCurrent(
  get: () => VisualEditorStore,
  context: CanvasHistoryRestoreContext,
): boolean {
  return _canvasHistoryEpoch === context.epoch && get().projectId === context.projectId;
}

function areCanvasProjectEpochContextsEqual(
  left: CanvasHistoryRestoreContext | null,
  right: CanvasHistoryRestoreContext,
): boolean {
  return Boolean(left && left.projectId === right.projectId && left.epoch === right.epoch);
}

function isCanvasEdgeQueueOwnedBy(context: CanvasHistoryRestoreContext): boolean {
  return areCanvasProjectEpochContextsEqual(_canvasEdgeQueueContext, context);
}

function buildCanvasNodePersistenceMetadata(node: CanvasNode): Record<string, unknown> {
  const metadata: Record<string, unknown> = {
    entered_canvas_at: node.enteredCanvasAt || null,
    last_activity_at: node.lastActivityAt || null,
    aspect_ratio: node.aspectRatio || null,
    wh_ratio: typeof node.whRatio === 'number' && node.whRatio > 0 ? node.whRatio : null,
    original_image_url: node.originalImageUrl || null,
    generating_task_id: node.generatingTaskId || null,
    generating_capability: node.generatingCapability || null,
    generating_error: node.generatingError || null,
    entitlement_failure: node.entitlementFailure || null,
    chain_pending: node._isChainPending === true,
    preparing_created_at: node._preparingCreatedAt || null,
    generated_by_task_id: node.generatedByTaskId || null,
    generation_prompt: node.generationPrompt || null,
    generation_capability: node.generationCapability || null,
    generation_group_id: node.generationGroupId || null,
    output_index: typeof node.outputIndex === 'number' ? node.outputIndex : null,
    source_label: node.sourceLabel || null,
    reference_evidence: node.referenceEvidence || null,
    skip_style_analysis: node.skipStyleAnalysis || null,
    lineage_source_node_ids: node.lineageSourceNodeIds || null,
    lineage_relation_type: node.lineageRelationType || null,
    lineage_relation_label: node.lineageRelationLabel || null,
    template_instance: node.templateInstance || null,
    director_board: node.directorBoard || null,
    model_url: node.modelUrl || null,
    is_empty: node.isEmpty || false,
    source_duration: node.sourceDuration ?? null,
    source_start: node.sourceStart ?? 0,
    source_end: node.sourceEnd ?? Math.round((node.duration || 0) * 1000),
    rotation: node.rotation ?? 0,
    flip_h: !!node.flipH,
    flip_v: !!node.flipV,
    color_adj: node.colorAdj || null,
    background: node.background || null,
    layers: node.layers || null,
    artboard: node.artboard || null,
    viewport_transform: node.viewportTransform || null,
    foreground_mask_url: node.foregroundMaskUrl || null,
    replaced_video_url: node.replacedVideoUrl || null,
    transcript: node.transcript || null,
    group_id: node.groupId || null,
    group_child_ids: node.groupChildIds || null,
    group_collapsed: typeof node.groupCollapsed === 'boolean' ? node.groupCollapsed : null,
    group_label: node.groupLabel || null,
    workflow_area_group_id: node.workflowAreaGroupId || null,
  };

  if (node.decompositionParentId) {
    metadata.decomposition_parent_id = node.decompositionParentId;
  }
  if (typeof node._elementIndex === 'number') {
    metadata.element_index = node._elementIndex;
  }
  metadata.sticker_overlays = node.stickerOverlays ?? [];
  if (node.textOverlays?.length) {
    metadata.text_overlays = node.textOverlays;
  }
  if (node.smartEditHistory) {
    metadata.smart_edit_history = node.smartEditHistory;
  }
  if (node.styleAnalysis) {
    metadata.style_analysis = {
      status: node.styleAnalysis.status,
      analyzed_at: node.styleAnalysis.analyzedAt ?? null,
      styles: node.styleAnalysis.styles ?? [],
      moods: node.styleAnalysis.moods ?? [],
      colors: node.styleAnalysis.colors ?? [],
      scene: node.styleAnalysis.scene ?? null,
      lighting: node.styleAnalysis.lighting ?? null,
      body_framing: node.styleAnalysis.bodyFraming ?? null,
      quality_score: node.styleAnalysis.qualityScore ?? null,
      description: node.styleAnalysis.description ?? null,
      content_type: node.styleAnalysis.contentType ?? null,
      face_visibility: node.styleAnalysis.faceVisibility ?? null,
      is_isolated_object: node.styleAnalysis.isIsolatedObject ?? null,
      use_tags: node.styleAnalysis.useTags ?? [],
      advise_tags: node.styleAnalysis.adviseTags ?? [],
      error: node.styleAnalysis.error ?? null,
    };
  }
  if (isPromptNode(node)) {
    metadata.variant = node.promptVariant || null;
    metadata.text = node.promptText || null;
    metadata.prompt_metadata = node.promptMetadata || null;
  }
  if (node.storyboardData) {
    metadata.storyboard = node.storyboardData;
  }
  if (node.gridPanelEditData) {
    metadata.grid_panel_edit_data = node.gridPanelEditData;
  }
  if (isSequenceNode(node)) {
    metadata.sequence_data = node.sequenceData || null;
  }

  return metadata;
}

function buildCanvasNodeCreatePayload(node: CanvasNode): Parameters<typeof import('@/lib/api/canvas-nodes').batchCreateCanvasNodes>[1][number] {
  const nodeType = isPromptNode(node)
    ? 'prompt' as const
    : isSequenceNode(node)
      ? 'sequence' as const
      : 'free' as const;
  const persistableThumbnailUrl = getPersistableThumbnailUrl(
    node.thumbnailUrl || node.thumbnail || null,
    node.mediaType,
  ) ?? null;

  return {
    id: node.id,
    asset_id: node.assetId || null,
    node_type: nodeType,
    media_type: (node.mediaType === 'prompt' || node.mediaType === 'sequence' ? 'image' : node.mediaType) as 'video' | 'image' | '3d_model',
    order_index: 0,
    start_time: node.startTime ?? 0,
    end_time: node.endTime ?? node.duration,
    duration: node.duration,
    source_start: node.sourceStart ?? 0,
    source_end: node.sourceEnd ?? Math.round((node.duration || 0) * 1000),
    canvas_position: node.position,
    video_url: node.videoUrl || null,
    thumbnail_url: persistableThumbnailUrl,
    metadata: buildCanvasNodePersistenceMetadata(node),
  };
}

function buildCanvasHistoryNodeUpdate(node: CanvasNode): CanvasNodeBatchUpdate {
  const nodeType = isPromptNode(node) ? 'prompt'
    : isSequenceNode(node) ? 'sequence'
    : 'free';
  const persistableThumbnailUrl = getPersistableThumbnailUrl(
    node.thumbnailUrl || node.thumbnail || null,
    node.mediaType,
  ) ?? null;

  return {
    id: node.id,
    asset_id: node.assetId || null,
    node_type: nodeType,
    media_type: node.mediaType === 'prompt' || node.mediaType === 'sequence' || node.mediaType === 'audio'
      ? 'image'
      : node.mediaType,
    start_time: node.startTime ?? 0,
    end_time: node.endTime ?? node.duration,
    duration: node.duration,
    canvas_position: node.position,
    video_url: node.videoUrl || null,
    thumbnail_url: persistableThumbnailUrl,
    metadata: buildCanvasNodePersistenceMetadata(node),
  };
}

function mergeCanvasHistoryNodeState(currentNode: CanvasNode, snapshotNode: CanvasNode): CanvasNode {
  const restoredNode = cloneCanvasHistoryValue(snapshotNode);
  return ensureNodePersistenceFields({
    ...restoredNode,
    createdAt: currentNode.createdAt,
    updatedAt: currentNode.updatedAt,
  } as CanvasNode, 'mergeCanvasHistoryNodeState');
}

function syncGroupStorageFromNodes(projectId: string, nodes: CanvasNode[]) {
  const existingGroups = loadGroups(projectId);
  const nextGroups = nodes.filter((node) => node.groupChildIds?.length);
  const nextGroupIdSet = new Set(nextGroups.map((node) => node.id));

  Object.keys(existingGroups).forEach((groupId) => {
    if (!nextGroupIdSet.has(groupId)) {
      removeGroup(projectId, groupId);
    }
  });

  nextGroups.forEach((node) => {
    saveGroup(projectId, node.id, {
      childIds: [...(node.groupChildIds || [])],
      collapsed: node.groupCollapsed === true,
      label: node.groupLabel || `${node.groupChildIds?.length || 0} items`,
      position: { ...node.position },
      thumbnail: node.thumbnail,
    });
  });
}

function clearPendingCanvasPersistence() {
  _clearScheduledBatchUpdateFlush();
  _pendingNodeUpdates.clear();
  _pendingNodeUpdatesProjectId = null;
  _batchUpdateLastError = null;
  _batchUpdateNeedsFlush = false;
  _batchUpdateNeedsKeepalive = false;
  _batchUpdateRetryProjectId = null;
  _batchUpdateRetryCount = 0;

  _promptSaveTimers.forEach((timer) => clearTimeout(timer));
  _promptSaveTimers.clear();

  if (_edgePatchTimer) {
    clearTimeout(_edgePatchTimer);
    _edgePatchTimer = null;
  }
  _pendingEdgeAdds.length = 0;
  _pendingEdgeRemoves.length = 0;
  _deferredEdgeAdds.clear();

  if (_edgeSaveTimer) {
    clearTimeout(_edgeSaveTimer);
    _edgeSaveTimer = null;
  }
  _scheduledFullEdgeSyncContext = null;
  _scheduledFullEdgeSyncSignature = null;
  _scheduledFullEdgeSyncPayload = null;
  _scheduledFullEdgeSyncMutationCutoff = null;
  _scheduledFullEdgeSyncIntentId = null;

  if (_edgePatchRetryTimer) {
    clearTimeout(_edgePatchRetryTimer);
    _edgePatchRetryTimer = null;
  }

  if (_edgeFullSyncRetryTimer) {
    clearTimeout(_edgeFullSyncRetryTimer);
    _edgeFullSyncRetryTimer = null;
  }

  _pendingFullEdgeSyncProjectId = null;
  _pendingFullEdgeSyncPayload = null;
  _pendingFullEdgeSyncSignature = null;
}

function clearPendingCanvasEdgePersistence(
  options?: { preserveQueuedMutations?: boolean; preserveScheduledFullSync?: boolean },
  context?: CanvasHistoryRestoreContext,
) {
  if (context && !isCanvasEdgeQueueOwnedBy(context)) {
    return;
  }
  const nextQueueState = resolveCanvasEdgeQueueReset({
    pendingEdgeAdds: _pendingEdgeAdds,
    pendingEdgeRemoves: _pendingEdgeRemoves,
    deferredEdgeAdds: _deferredEdgeAdds,
  }, options);

  if (_edgePatchTimer) {
    clearTimeout(_edgePatchTimer);
    _edgePatchTimer = null;
  }

  _pendingEdgeAdds.length = 0;
  _pendingEdgeAdds.push(...nextQueueState.pendingEdgeAdds);

  _pendingEdgeRemoves.length = 0;
  _pendingEdgeRemoves.push(...nextQueueState.pendingEdgeRemoves);

  _deferredEdgeAdds.clear();
  nextQueueState.deferredEdgeAdds.forEach(([edgeId, edge]) => {
    _deferredEdgeAdds.set(edgeId, edge);
  });

  if (!options?.preserveScheduledFullSync) {
    if (_edgeSaveTimer) {
      clearTimeout(_edgeSaveTimer);
      _edgeSaveTimer = null;
    }
    _scheduledFullEdgeSyncContext = null;
    _scheduledFullEdgeSyncSignature = null;
    _scheduledFullEdgeSyncPayload = null;
    _scheduledFullEdgeSyncMutationCutoff = null;
    _scheduledFullEdgeSyncIntentId = null;
  }
}

const EDGE_PATCH_RETRY_DELAY_MS = 1500;
const EDGE_FULL_SYNC_RETRY_DELAY_MS = 2000;
let _edgePatchRetryTimer: ReturnType<typeof setTimeout> | null = null;
let _edgeFullSyncRetryTimer: ReturnType<typeof setTimeout> | null = null;
let _pendingFullEdgeSyncProjectId: string | null = null;
let _pendingFullEdgeSyncPayload: CanvasEdgeSyncPayloadItem[] | null = null;
let _pendingFullEdgeSyncSignature: string | null = null;
let _pendingFullEdgeSyncContext: CanvasHistoryRestoreContext | null = null;
let _pendingFullEdgeSyncMutationCutoff: number | null = null;
let _pendingFullEdgeSyncDesiredEdgeIds: string[] | null = null;
let _pendingFullEdgeSyncIntentSeq: number | null = null;

function _buildEdgePayloadSignature(edges: CanvasEdgeSyncPayloadItem[]): string {
  return buildCanvasEdgesSignature(edges.map((edge) => ({
    id: edge.id,
    source: edge.source_node_id,
    target: edge.target_node_id,
    sourceHandle: edge.source_handle,
    targetHandle: edge.target_handle,
    relationType: edge.relation_type,
    relationLabel: edge.relation_label,
  })));
}

function _clearScheduledEdgePatchRetry() {
  if (_edgePatchRetryTimer) {
    clearTimeout(_edgePatchRetryTimer);
    _edgePatchRetryTimer = null;
  }
}

function _scheduleEdgePatchRetry(projectId: string, context?: CanvasHistoryRestoreContext) {
  const retryContext = context ?? {
    projectId,
    epoch: _canvasHistoryEpoch,
  };
  if (!_claimCanvasEdgeQueue(retryContext)) {
    return;
  }
  if (_edgePatchRetryTimer) {
    return;
  }

  _edgePatchRetryTimer = setTimeout(() => {
    _edgePatchRetryTimer = null;
    const getStore = () => useVisualEditorStore.getState();
    if (
      !isCanvasHistoryRestoreContextCurrent(getStore, retryContext)
      || !isCanvasEdgeQueueOwnedBy(retryContext)
    ) {
      return;
    }
    _scheduleEdgePatch(projectId, retryContext);
  }, EDGE_PATCH_RETRY_DELAY_MS);
}

function _clearPendingFullEdgeSyncRetry() {
  if (_edgeFullSyncRetryTimer) {
    clearTimeout(_edgeFullSyncRetryTimer);
    _edgeFullSyncRetryTimer = null;
  }

  _pendingFullEdgeSyncProjectId = null;
  _pendingFullEdgeSyncPayload = null;
  _pendingFullEdgeSyncSignature = null;
  _pendingFullEdgeSyncContext = null;
  _pendingFullEdgeSyncMutationCutoff = null;
  _pendingFullEdgeSyncDesiredEdgeIds = null;
  _pendingFullEdgeSyncIntentSeq = null;
}

function _clearPendingFullEdgeSyncRetryThrough(
  context: CanvasHistoryRestoreContext,
  mutationCutoff: number,
  fullIntentSeq: number,
) {
  if (
    areCanvasProjectEpochContextsEqual(_pendingFullEdgeSyncContext, context)
    && _pendingFullEdgeSyncMutationCutoff !== null
    && _pendingFullEdgeSyncMutationCutoff <= mutationCutoff
    && _pendingFullEdgeSyncIntentSeq !== null
    && _pendingFullEdgeSyncIntentSeq <= fullIntentSeq
  ) {
    _clearPendingFullEdgeSyncRetry();
  }
}

function _scheduleFullEdgeSyncRetry(
  projectId: string,
  edges: CanvasEdgeSyncPayloadItem[],
  context: CanvasHistoryRestoreContext,
  mutationCutoff: number,
  desiredEdgeIds: string[],
  fullIntentSeq: number,
) {
  if (!_claimCanvasEdgeQueue(context)) {
    return;
  }
  if ((_pendingFullEdgeSyncIntentSeq ?? -1) > fullIntentSeq) {
    return;
  }
  _pendingFullEdgeSyncProjectId = projectId;
  _pendingFullEdgeSyncPayload = edges;
  _pendingFullEdgeSyncSignature = _buildEdgePayloadSignature(edges);
  _pendingFullEdgeSyncContext = context;
  _pendingFullEdgeSyncMutationCutoff = mutationCutoff;
  _pendingFullEdgeSyncDesiredEdgeIds = desiredEdgeIds;
  _pendingFullEdgeSyncIntentSeq = fullIntentSeq;

  if (_edgeFullSyncRetryTimer) {
    clearTimeout(_edgeFullSyncRetryTimer);
  }

  _edgeFullSyncRetryTimer = setTimeout(() => {
    _edgeFullSyncRetryTimer = null;

    const retryProjectId = _pendingFullEdgeSyncProjectId;
    const retryPayload = _pendingFullEdgeSyncPayload;
    const retrySignature = _pendingFullEdgeSyncSignature;
    const retryContext = _pendingFullEdgeSyncContext;
    const retryMutationCutoff = _pendingFullEdgeSyncMutationCutoff;
    const retryDesiredEdgeIds = _pendingFullEdgeSyncDesiredEdgeIds;
    const retryFullIntentSeq = _pendingFullEdgeSyncIntentSeq;
    _clearPendingFullEdgeSyncRetry();

    if (!retryProjectId || !retryPayload || !retrySignature || !retryContext || retryMutationCutoff === null || !retryDesiredEdgeIds || retryFullIntentSeq === null) {
      return;
    }

    const latestState = useVisualEditorStore.getState();
    const getStore = () => useVisualEditorStore.getState();
    if (
      latestState.projectId !== retryProjectId
      || !isCanvasHistoryRestoreContextCurrent(getStore, retryContext)
      || !isCanvasEdgeQueueOwnedBy(retryContext)
    ) {
      return;
    }

    const latestSignature = buildCanvasEdgesSignature(latestState.canvasEdges);
    void _runFullCanvasEdgeSyncForState(retryProjectId, latestState.nodes, latestState.canvasEdges, {
      context: retryContext,
      fullIntentSeq: retryFullIntentSeq,
      ...(latestSignature === retrySignature
        ? { mutationCutoff: retryMutationCutoff, desiredEdgeIds: retryDesiredEdgeIds }
        : {}),
    }).catch((error) => {
      if (!isCanvasHistoryRestoreContextCurrent(getStore, retryContext)) {
        return;
      }
      console.error('[VisualEditorStore] 连线全量同步重试失败:', error);
    });
  }, EDGE_FULL_SYNC_RETRY_DELAY_MS);
}

let _edgeSyncInFlight = false;
let _edgeSyncOwner: symbol | null = null;
let _edgeSyncContext: CanvasHistoryRestoreContext | null = null;
let _edgeSyncMutationCutoff: number | null = null;
let _edgeSyncIntentSeq: number | null = null;

function _runFullCanvasEdgeSync(
  projectId: string,
  edges: CanvasEdgeSyncPayloadItem[],
  context: CanvasHistoryRestoreContext,
  mutationCutoff: number,
  desiredEdgeIds: string[],
  fullIntentSeq: number,
  options?: { keepalive?: boolean },
): Promise<void> {
  return _sequenceCanvasEdgeWrite(projectId, () => (
    _runFullCanvasEdgeSyncNow(projectId, edges, context, mutationCutoff, desiredEdgeIds, fullIntentSeq, options)
  ));
}

async function _runFullCanvasEdgeSyncNow(
  projectId: string,
  edges: CanvasEdgeSyncPayloadItem[],
  context: CanvasHistoryRestoreContext,
  mutationCutoff: number,
  desiredEdgeIds: string[],
  fullIntentSeq: number,
  options?: { keepalive?: boolean },
) {
  const getStore = () => useVisualEditorStore.getState();
  if (
    !isCanvasHistoryRestoreContextCurrent(getStore, context)
    || !_claimCanvasEdgeQueue(context)
    || !_isCanvasEdgeHydrationActive(context)
  ) {
    _suspendCanvasFullSyncRetry(context, edges, mutationCutoff, desiredEdgeIds, fullIntentSeq);
    return;
  }
  _clearPendingFullEdgeSyncRetryThrough(context, mutationCutoff, fullIntentSeq);
  _clearScheduledEdgePatchRetry();
  // Full sync payload omits edges whose endpoints are not persisted yet, so queued
  // incremental mutations must survive the sync boundary instead of being dropped.
  _clearScheduledFullCanvasEdgeSyncThrough(context, mutationCutoff, fullIntentSeq);
  clearPendingCanvasEdgePersistence({
    preserveQueuedMutations: true,
    preserveScheduledFullSync: true,
  }, context);
  const syncOwner = Symbol(projectId);
  _edgeSyncOwner = syncOwner;
  _edgeSyncContext = context;
  _edgeSyncMutationCutoff = mutationCutoff;
  _edgeSyncIntentSeq = fullIntentSeq;
  _edgeSyncInFlight = true;

  try {
    const { syncCanvasEdges } = await import('@/lib/api/canvas-nodes');
    if (
      !isCanvasHistoryRestoreContextCurrent(getStore, context)
      || !isCanvasEdgeQueueOwnedBy(context)
    ) {
      _suspendCanvasFullSyncRetry(context, edges, mutationCutoff, desiredEdgeIds, fullIntentSeq);
      return;
    }
    await syncCanvasEdges(projectId, edges, options?.keepalive ? { keepalive: true } : undefined);
    _consumeQueuedEdgeMutationsCoveredByFullSync(projectId, context, mutationCutoff, fullIntentSeq);
    if (!isCanvasHistoryRestoreContextCurrent(getStore, context)) {
      return;
    }
  } catch (error) {
    if (
      !isCanvasHistoryRestoreContextCurrent(getStore, context)
      || !isCanvasEdgeQueueOwnedBy(context)
    ) {
      _suspendCanvasFullSyncRetry(context, edges, mutationCutoff, desiredEdgeIds, fullIntentSeq);
      return;
    }
    const missingNodeIds = _extractMissingNodeIdsFromEdgeSyncError(error);
    if (missingNodeIds.length > 0) {
      await _recoverFullCanvasEdgeSyncMissingNodes(
        projectId,
        edges,
        missingNodeIds,
        context,
        mutationCutoff,
        desiredEdgeIds,
        fullIntentSeq,
        options,
      );
      return;
    }
    _scheduleFullEdgeSyncRetry(projectId, edges, context, mutationCutoff, desiredEdgeIds, fullIntentSeq);
    throw error;
  } finally {
    if (_edgeSyncOwner === syncOwner) {
      _edgeSyncOwner = null;
      _edgeSyncContext = null;
      _edgeSyncMutationCutoff = null;
      _edgeSyncIntentSeq = null;
      _edgeSyncInFlight = false;
      if (
        isCanvasHistoryRestoreContextCurrent(getStore, context)
        && isCanvasEdgeQueueOwnedBy(context)
        && (_pendingEdgeAdds.length > 0 || _pendingEdgeRemoves.length > 0 || _deferredEdgeAdds.size > 0)
      ) {
        _scheduleEdgePatch(projectId, context);
      }
    }
  }
}

async function restoreCanvasHistorySnapshot(
  get: () => VisualEditorStore,
  set: VisualEditorSetState,
  snapshot: CanvasHistorySnapshot,
  afterSnapshot: CanvasHistorySnapshot | undefined,
  restoreContext: CanvasHistoryRestoreContext,
): Promise<CanvasHistoryRestoreResult> {
  if (!isCanvasHistoryRestoreContextCurrent(get, restoreContext)) {
    return 'stale';
  }
  const currentState = get();
  const { projectId } = restoreContext;
  const currentNodeById = new Map(currentState.nodes.map((node) => [node.id, node] as const));
  const remappedIds = new Map(snapshot.nodes.filter(node =>
    !currentNodeById.has(node.id) && node.mediaBindingRevision === undefined
      && getCanvasBindingRevision(node.id) === undefined,
  ).map(node => [node.id, generateUuidLike()]));
  if (remappedIds.size) {
    // A delete intent without a persisted row cannot be undone by ordinary upsert.
    // Remap all exact node references together, including groups/edges/selection.
    const remap = (value: unknown): unknown => {
      if (typeof value === 'string') return remappedIds.get(value) ?? value;
      if (Array.isArray(value)) return value.map(remap);
      if (value && typeof value === 'object') return Object.fromEntries(
        Object.entries(value).map(([key, child]) => [key, remap(child)]));
      return value;
    };
    snapshot = remap(snapshot) as CanvasHistorySnapshot;
  }

  const snapshotNodeIdSet = new Set(snapshot.nodes.map((node) => node.id));
  const afterNodeById = afterSnapshot
    ? new Map(afterSnapshot.nodes.map((node) => [node.id, node] as const))
    : null;
  const actionCreatedNodeIds = afterSnapshot
    ? new Set(afterSnapshot.nodes.filter((node) => !snapshotNodeIdSet.has(node.id)).map((node) => node.id))
    : null;

  const restoredSnapshotNodes = snapshot.nodes.flatMap((snapshotNode, index) => {
    const currentNode = currentNodeById.get(snapshotNode.id);
    if (!currentNode) {
      // Byte-upload retries own an in-memory File/blob URL that is disposed on
      // deletion. History JSON cannot recreate that capability, so do not revive
      // a spinner/dead Retry button. Persistence retries already have durable media
      // and can be safely restored as a clean node below.
      if (snapshotNode.uploadRetryMode === 'upload' || (snapshotNode.isUploading && !snapshotNode.assetId)) {
        return [];
      }
      return [ensureNodePersistenceFields(
        clearRestoredPersistenceRetryState(cloneCanvasHistoryValue(snapshotNode)),
        `restoreCanvasHistorySnapshot.new[${index}]`,
      )];
    }
    const afterNode = afterNodeById?.get(snapshotNode.id);
    if (afterNode && areCanvasHistoryValuesEqual(snapshotNode, afterNode)) {
      return [currentNode];
    }
    if (afterNode) {
      const restoredNode = restoreCanvasHistoryChangedFields(currentNode, snapshotNode, afterNode) as CanvasNode;
      return [ensureNodePersistenceFields({
        ...restoredNode,
        createdAt: currentNode.createdAt ?? restoredNode.createdAt,
        updatedAt: currentNode.updatedAt ?? restoredNode.updatedAt,
      }, `restoreCanvasHistorySnapshot.changed[${index}]`)];
    }
    return [mergeCanvasHistoryNodeState(currentNode, snapshotNode)];
  });

  const preservedCurrentNodes = actionCreatedNodeIds
    ? currentState.nodes.filter((node) => !snapshotNodeIdSet.has(node.id) && !actionCreatedNodeIds.has(node.id))
    : [];
  const mergedNodes = [...restoredSnapshotNodes, ...preservedCurrentNodes];

  const groupNormalization = normalizeCanvasGroupTopology(mergedNodes);
  const rawNextEdges = afterSnapshot
    ? mergeCanvasHistoryEdgesForUndo(currentState.canvasEdges, snapshot.canvasEdges, afterSnapshot.canvasEdges)
    : cloneCanvasHistoryValue(snapshot.canvasEdges);
  const { edges: nextEdges } = filterCanvasEdgesWithExistingNodes(groupNormalization.nodes, rawNextEdges);
  const reconciliation = reconcileTemplateWorkflowBindings(groupNormalization.nodes, nextEdges);
  const nextNodes = reconciliation.nodes;
  const nextNodeIdSet = new Set(nextNodes.map((node) => node.id));

  // A history entry can replace the image itself (for example, merging sticker
  // layers). Ordinary batch updates intentionally cannot write media fields.
  // Restore that binding with the same revision authority as the forward edit,
  // before exposing the old editable overlays again.
  const mediaRestores = nextNodes.flatMap((node, index) => {
    const before = afterNodeById?.get(node.id);
    const current = currentNodeById.get(node.id);
    if (!before || !current || before.assetId === node.assetId) return [];
    return [{ node, index, before, current }];
  });
  if (mediaRestores.length > 0) {
    if (!projectId || mediaRestores.length > 1 || mediaRestores.some(({ node, before, current }) =>
      !node.assetId || current.assetId !== before.assetId
      || current.mediaBindingRevision !== before.mediaBindingRevision)) return 'failure';
    try {
      const { commandCanvasNodeBinding } = await import('@/lib/api/canvas-nodes');
      for (const { node, index, current } of mediaRestores) {
        if (!isCanvasHistoryRestoreContextCurrent(get, restoreContext)) return 'stale';
        const row = await commandCanvasNodeBinding(node.id, 'replace-media', current.mediaBindingRevision!, {
          asset_id: node.assetId,
          metadata: buildCanvasNodePersistenceMetadata(node),
        }, projectId);
        if (!isCanvasHistoryRestoreContextCurrent(get, restoreContext)) return 'stale';
        nextNodes[index] = applyCanvasBindingRow(node, row);
      }
    } catch (error) {
      console.error('[VisualEditorStore] 画布撤销媒体同步失败:', error);
      return 'failure';
    }
  }

  if (!isCanvasHistoryRestoreContextCurrent(get, restoreContext)) {
    return 'stale';
  }
  clearPendingCanvasPersistence();

  if (!isCanvasHistoryRestoreContextCurrent(get, restoreContext)) {
    return 'stale';
  }
  set({
    nodes: nextNodes,
    canvasEdges: nextEdges,
    currentShotId: snapshot.currentShotId && nextNodeIdSet.has(snapshot.currentShotId)
      ? snapshot.currentShotId
      : nextNodes[0]?.id || null,
    activeSidebar:
      snapshot.activeSidebar === 'templateInspector' && (!snapshot.templateInspectorNodeId || !nextNodeIdSet.has(snapshot.templateInspectorNodeId))
        ? null
        : snapshot.activeSidebar === 'smartEdit' && (!snapshot.smartEditNodeId || !nextNodeIdSet.has(snapshot.smartEditNodeId))
          ? null
          : snapshot.activeSidebar,
    selectedClipIdForAI: snapshot.selectedClipIdForAI && nextNodeIdSet.has(snapshot.selectedClipIdForAI)
      ? snapshot.selectedClipIdForAI
      : null,
    templateInspectorNodeId: snapshot.templateInspectorNodeId && nextNodeIdSet.has(snapshot.templateInspectorNodeId)
      ? snapshot.templateInspectorNodeId
      : null,
    smartEditNodeId: snapshot.smartEditNodeId && nextNodeIdSet.has(snapshot.smartEditNodeId)
      ? snapshot.smartEditNodeId
      : null,
    lockedNodeIds: snapshot.lockedNodeIds.filter((nodeId) => nextNodeIdSet.has(nodeId)),
    ...(!snapshot.smartEditNodeId ? _getSmartEditSessionResetState() : {}),
  });

  if (!isCanvasHistoryRestoreContextCurrent(get, restoreContext)) {
    return 'stale';
  }

  if (!projectId) {
    return 'success';
  }

  if (!isCanvasHistoryRestoreContextCurrent(get, restoreContext)) {
    return 'stale';
  }
  syncGroupStorageFromNodes(projectId, nextNodes);

  const currentNodeIdSet = new Set(currentState.nodes.map((node) => node.id));
  const nodesToCreate = nextNodes.filter((node) => !currentNodeIdSet.has(node.id));
  const nodeIdsToDelete = currentState.nodes
    .map((node) => node.id)
    .filter((nodeId) => !nextNodeIdSet.has(nodeId));
  const nodeUpdates = nextNodes
    .filter((node) => {
      const currentNode = currentNodeById.get(node.id);
      return currentNode && !areCanvasHistoryValuesEqual(currentNode, node);
    })
    .map((node) => buildCanvasHistoryNodeUpdate(node));
  const durablyCreatedNodeIds = new Set<string>();

  try {
    if (!isCanvasHistoryRestoreContextCurrent(get, restoreContext)) {
      return 'stale';
    }
    const {
      batchCreateCanvasNodes,
      commandCanvasNodeBinding,
      batchDeleteCanvasNodes,
      batchUpdateCanvasNodes,
      deleteCanvasNode,
      createCanvasGroup,
      ungroupCanvasNodes,
    } = await import('@/lib/api/canvas-nodes');
    if (!isCanvasHistoryRestoreContextCurrent(get, restoreContext)) {
      return 'stale';
    }

    // History restore can represent an ungroup as "container removed, children
    // retained". It must use the same non-cascading operation as the toolbar.
    const nextNodeIdSetForGroupOps = new Set(nextNodes.map((node) => node.id));
    const groupsToUngroup = nodeIdsToDelete
      .map((nodeId) => currentNodeById.get(nodeId))
      .filter((node): node is CanvasNode => Boolean(
        node?.groupChildIds?.length
        && node.groupChildIds.every((childId) => nextNodeIdSetForGroupOps.has(childId)),
      ));
    const groupIdsToUngroup = new Set(groupsToUngroup.map((node) => node.id));
    for (const groupNode of groupsToUngroup) {
      if (!isCanvasHistoryRestoreContextCurrent(get, restoreContext)) {
        return 'stale';
      }
      await ungroupCanvasNodes(projectId, groupNode.id);
      if (!isCanvasHistoryRestoreContextCurrent(get, restoreContext)) {
        return 'stale';
      }
      _unmarkPersistedCanvasNodeIds([groupNode.id]);
    }

    const remainingNodeIdsToDelete = nodeIdsToDelete.filter((nodeId) => !groupIdsToUngroup.has(nodeId));
    if (remainingNodeIdsToDelete.length === 1) {
      if (!isCanvasHistoryRestoreContextCurrent(get, restoreContext)) {
        return 'stale';
      }
      await deleteCanvasNode(remainingNodeIdsToDelete[0], projectId);
      if (!isCanvasHistoryRestoreContextCurrent(get, restoreContext)) {
        return 'stale';
      }
      _unmarkPersistedCanvasNodeIds(remainingNodeIdsToDelete);
    } else if (remainingNodeIdsToDelete.length > 1) {
      if (!isCanvasHistoryRestoreContextCurrent(get, restoreContext)) {
        return 'stale';
      }
      await batchDeleteCanvasNodes(projectId, remainingNodeIdsToDelete);
      if (!isCanvasHistoryRestoreContextCurrent(get, restoreContext)) {
        return 'stale';
      }
      _unmarkPersistedCanvasNodeIds(remainingNodeIdsToDelete);
    }

    const restoredIds = new Set<string>();
    for (const node of nodesToCreate) {
      const revision = node.mediaBindingRevision ?? getCanvasBindingRevision(node.id);
      if (revision === undefined) continue;
      if (!isCanvasHistoryRestoreContextCurrent(get, restoreContext)) return 'stale';
      const restored = await commandCanvasNodeBinding(node.id, 'restore', revision, {}, projectId);
      if (!isCanvasHistoryRestoreContextCurrent(get, restoreContext)) return 'stale';
      restoredIds.add(node.id);
      _markPersistedCanvasNodeIds([node.id]);
      durablyCreatedNodeIds.add(node.id);
      _clearConfirmedCanvasDeleteTombstones(projectId, [node.id]);
      set(state => ({ nodes: state.nodes.map(current => current.id === node.id ? applyCanvasBindingRow(current, restored) : current) }));
    }

    // The reverse history direction recreates a group. Do not recreate its
    // container and child links as two independent writes.
    const nodeIdsBeingCreated = new Set(nodesToCreate.map((node) => node.id));
    const groupsToCreate = nodesToCreate.filter((node) => (
      node.groupChildIds?.length
      && node.groupChildIds.every((childId) => nextNodeIdSetForGroupOps.has(childId))
      && node.groupChildIds.every((childId) => !nodeIdsBeingCreated.has(childId))
    ));
    const groupIdsToCreate = new Set(groupsToCreate.map((node) => node.id));
    for (const groupNode of groupsToCreate) {
      if (!isCanvasHistoryRestoreContextCurrent(get, restoreContext)) {
        return 'stale';
      }
      await createCanvasGroup(projectId, {
        id: groupNode.id,
        child_node_ids: groupNode.groupChildIds || [],
        canvas_position: groupNode.position,
        group_label: groupNode.groupLabel,
      });
      if (!isCanvasHistoryRestoreContextCurrent(get, restoreContext)) {
        return 'stale';
      }
      _clearConfirmedCanvasDeleteTombstones(projectId, [groupNode.id]);
      _markPersistedCanvasNodeIds([groupNode.id]);
      durablyCreatedNodeIds.add(groupNode.id);
    }

    const remainingNodesToCreate = nodesToCreate.filter((node) => !groupIdsToCreate.has(node.id) && !restoredIds.has(node.id));
    if (remainingNodesToCreate.length > 0) {
      if (!isCanvasHistoryRestoreContextCurrent(get, restoreContext)) {
        return 'stale';
      }
      await batchCreateCanvasNodes(projectId, remainingNodesToCreate.map((node) => buildCanvasNodeCreatePayload(node)));
      if (!isCanvasHistoryRestoreContextCurrent(get, restoreContext)) {
        return 'stale';
      }
      _clearConfirmedCanvasDeleteTombstones(
        projectId,
        remainingNodesToCreate.map((node) => node.id),
      );
      _markPersistedCanvasNodeIds(remainingNodesToCreate.map((node) => node.id));
      remainingNodesToCreate.forEach((node) => durablyCreatedNodeIds.add(node.id));
    }

    if (nodeUpdates.length > 0) {
      if (!isCanvasHistoryRestoreContextCurrent(get, restoreContext)) {
        return 'stale';
      }
      await batchUpdateCanvasNodes(projectId, nodeUpdates);
      if (!isCanvasHistoryRestoreContextCurrent(get, restoreContext)) {
        return 'stale';
      }
    }

    if (!isCanvasHistoryRestoreContextCurrent(get, restoreContext)) {
      return 'stale';
    }
    await _runFullCanvasEdgeSyncForState(projectId, nextNodes, nextEdges, {
      extraPersistedNodeIds: nodesToCreate.map((node) => node.id),
      context: restoreContext,
    });
    if (!isCanvasHistoryRestoreContextCurrent(get, restoreContext)) {
      return 'stale';
    }
  } catch (error) {
    if (!isCanvasHistoryRestoreContextCurrent(get, restoreContext)) {
      return 'stale';
    }
    if (!isProjectQuotaError(error)) console.error('[VisualEditorStore] 画布撤销同步失败:', error);
    const unresolvedCreatedNodeIds = new Set(
      nodesToCreate
        .filter((node) => !durablyCreatedNodeIds.has(node.id))
        .map((node) => node.id),
    );
    if (unresolvedCreatedNodeIds.size > 0) {
      // Do not present an Undo-created node as durable when its recreate could
      // not be confirmed. Removing only unresolved creations keeps any
      // already-confirmed subset visible, while returning false leaves the
      // history entry available for a later retry.
      if (!isCanvasHistoryRestoreContextCurrent(get, restoreContext)) {
        return 'stale';
      }
      set((state) => {
        if (state.projectId !== restoreContext.projectId || _canvasHistoryEpoch !== restoreContext.epoch) {
          return {};
        }
        const filteredNodes = state.nodes.filter((node) => !unresolvedCreatedNodeIds.has(node.id));
        const normalized = normalizeCanvasGroupTopology(filteredNodes);
        const { edges } = filterCanvasEdgesWithExistingNodes(normalized.nodes, state.canvasEdges);
        const liveNodeIds = new Set(normalized.nodes.map((node) => node.id));
        return {
          nodes: normalized.nodes,
          canvasEdges: edges,
          currentShotId: state.currentShotId && liveNodeIds.has(state.currentShotId)
            ? state.currentShotId
            : normalized.nodes[0]?.id || null,
          selectedClipIdForAI: state.selectedClipIdForAI && liveNodeIds.has(state.selectedClipIdForAI)
            ? state.selectedClipIdForAI
            : null,
          templateInspectorNodeId: state.templateInspectorNodeId && liveNodeIds.has(state.templateInspectorNodeId)
            ? state.templateInspectorNodeId
            : null,
          smartEditNodeId: state.smartEditNodeId && liveNodeIds.has(state.smartEditNodeId)
            ? state.smartEditNodeId
            : null,
          lockedNodeIds: state.lockedNodeIds.filter((nodeId) => liveNodeIds.has(nodeId)),
        };
      });
      if (!isCanvasHistoryRestoreContextCurrent(get, restoreContext)) {
        return 'stale';
      }
    }
    toast.showError(error, 'canvas', { dedupeKey: 'canvas-history-restore-failed' });
    return 'failure';
  }

  return 'success';
}

function ensureNodeAssetId(node: CanvasNode, context: string): CanvasNode {
  if (node.isEmpty) return node;

  const normalizedAssetId = normalizeOptionalAssetId(
    (node as CanvasNode & { assetId?: unknown }).assetId,
    `VisualEditorStore.${context}`,
    { nodeId: node.id, mediaType: node.mediaType },
  );

  if (!normalizedAssetId) {
    console.debug('[VisualEditorStore] Node 无 assetId (将以 NULL 持久化):', node.id);
  }

  if (normalizedAssetId === node.assetId) {
    return node;
  }

  return {
    ...node,
    assetId: normalizedAssetId,
  };
}

function normalizeNodeMediaUrl(url?: string): string | undefined {
  if (typeof url !== 'string') {
    return undefined;
  }

  const normalized = toDisplayMediaUrl(url.trim());
  return normalized || undefined;
}

function getStoryboardBoardImageUrlForCache(node: Pick<CanvasNode, 'originalImageUrl' | 'thumbnail'>): string {
  return normalizeNodeMediaUrl(node.originalImageUrl)
    ?? normalizeNodeMediaUrl(node.thumbnail)
    ?? '';
}

function shouldInvalidateStoryboardPanelCacheForImageUpdate(
  currentNode: CanvasNode,
  nextNodePatch: Partial<CanvasNode>,
): boolean {
  if (!currentNode.storyboardData || Object.prototype.hasOwnProperty.call(nextNodePatch, 'storyboardData')) {
    return false;
  }

  if (!Object.prototype.hasOwnProperty.call(nextNodePatch, 'thumbnail')
    && !Object.prototype.hasOwnProperty.call(nextNodePatch, 'originalImageUrl')) {
    return false;
  }

  const previousBoardImageUrl = normalizeStoryboardMediaUrlForCacheComparison(
    getStoryboardBoardImageUrlForCache(currentNode),
  );
  const nextBoardImageUrl = getStoryboardBoardImageUrlForCache({ ...currentNode, ...nextNodePatch });
  const normalizedNextBoardImageUrl = normalizeStoryboardMediaUrlForCacheComparison(nextBoardImageUrl);

  return Boolean(normalizedNextBoardImageUrl && previousBoardImageUrl !== normalizedNextBoardImageUrl);
}

function ensureNodeDisplayMediaUrls(node: CanvasNode): CanvasNode {
  const normalizedRawThumbnail = normalizeNodeMediaUrl(node.thumbnail);
  const normalizedOriginalImageUrl = normalizeNodeMediaUrl(node.originalImageUrl);
  const normalizedVideoUrl = normalizeNodeMediaUrl(node.videoUrl);
  const normalizedModelUrl = normalizeNodeMediaUrl(node.modelUrl);
  const normalizedReplacedVideoUrl = normalizeNodeMediaUrl(node.replacedVideoUrl);
  const normalizedRawThumbnailUrl = normalizeNodeMediaUrl(node.thumbnailUrl);
  const thumbnailMediaType = node.mediaType === 'video' ? 'video' : 'image';
  const normalizedThumbnail = getDisplayThumbnailUrl(normalizedRawThumbnail, thumbnailMediaType);
  const normalizedThumbnailUrl = getDisplayThumbnailUrl(normalizedRawThumbnailUrl, thumbnailMediaType);

  if (
    normalizedThumbnail === node.thumbnail
    && normalizedOriginalImageUrl === node.originalImageUrl
    && normalizedVideoUrl === node.videoUrl
    && normalizedModelUrl === node.modelUrl
    && normalizedReplacedVideoUrl === node.replacedVideoUrl
    && normalizedThumbnailUrl === node.thumbnailUrl
  ) {
    return node;
  }

  return {
    ...node,
    thumbnail: normalizedThumbnail,
    originalImageUrl: normalizedOriginalImageUrl,
    videoUrl: normalizedVideoUrl,
    modelUrl: normalizedModelUrl,
    replacedVideoUrl: normalizedReplacedVideoUrl,
    thumbnailUrl: normalizedThumbnailUrl,
  };
}

function deriveGenerationGroupId(
  node: Pick<CanvasNode, 'generationGroupId' | 'generatedByTaskId' | 'generatingTaskId'>,
): string | undefined {
  return node.generationGroupId || node.generatedByTaskId || node.generatingTaskId || undefined;
}

function ensureNodePersistenceFields(node: CanvasNode, context: string): CanvasNode {
  const knownRevision = getCanvasBindingRevision(node.id);
  const nodeWithRevision = node.mediaBindingRevision === undefined && knownRevision !== undefined
    ? { ...node, mediaBindingRevision: knownRevision } : node;
  const nodeWithEntryTime = ensureEnteredCanvasAt(nodeWithRevision);
  const nodeWithActivityTime = ensureLastActivityAt(nodeWithEntryTime);
  const nodeWithAssetId = ensureNodeAssetId(nodeWithActivityTime, context);
  const nodeWithDisplayMediaUrls = ensureNodeDisplayMediaUrls(nodeWithAssetId);
  const normalizedGenerationGroupId = deriveGenerationGroupId(nodeWithDisplayMediaUrls);

  if (normalizedGenerationGroupId === nodeWithDisplayMediaUrls.generationGroupId) {
    return nodeWithDisplayMediaUrls;
  }

  return {
    ...nodeWithDisplayMediaUrls,
    generationGroupId: normalizedGenerationGroupId,
  };
}

/** Only explicit binding commands may replace the local media projection. */
function applyCanvasBindingRow(node: CanvasNode, row: CanvasNodeRow): CanvasNode {
  const metadata = row.metadata ?? {};
  return {
    ...node,
    mediaBindingRevision: row.media_binding_revision,
    mediaType: node.mediaType === 'prompt' || node.mediaType === 'sequence' ? node.mediaType : row.media_type,
    replacedVideoUrl: undefined,
    styleAnalysis: undefined,
    assetId: row.asset_id || undefined,
    videoUrl: row.video_url || undefined,
    thumbnail: row.thumbnail_url || undefined,
    thumbnailUrl: row.thumbnail_url || undefined,
    assetThumbnailUrl: row.asset_thumbnail_url || undefined,
    originalImageUrl: typeof metadata.original_image_url === 'string' ? metadata.original_image_url : undefined,
    modelUrl: typeof metadata.model_url === 'string' ? metadata.model_url : undefined,
    generatingTaskId: typeof metadata.generating_task_id === 'string' ? metadata.generating_task_id : undefined,
    generatedByTaskId: typeof metadata.generated_by_task_id === 'string' ? metadata.generated_by_task_id : undefined,
    generatingError: typeof metadata.generating_error === 'string' ? metadata.generating_error : undefined,
    entitlementFailure: readEntitlementFailure(metadata) ?? undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function hydratePersistedNodeMetadata(
  existingNodes: CanvasNode[],
  rows: Array<{ id: string; created_at: string; updated_at: string; metadata: Record<string, unknown> | null; media_binding_revision?: number; asset_id?: string | null; asset_thumbnail_url?: string | null }>,
): CanvasNode[] {
  if (rows.length === 0) return existingNodes;

  _markPersistedCanvasNodeIds(rows.map((row) => row.id));

  const rowById = new Map(rows.map((row) => [row.id, row]));
  let changed = false;
  const nextNodes = existingNodes.map((node) => {
    const row = rowById.get(node.id);
    if (!row) return node;

    const metadata = (row.metadata || {}) as Record<string, unknown>;
    // Background thumbnail completion may not change canvas metadata/timestamps.
    // Never attach a preview from a stale media binding to the current image.
    const assetThumbnailUrl = row.asset_id === node.assetId && row.asset_thumbnail_url !== undefined
      ? row.asset_thumbnail_url || undefined : node.assetThumbnailUrl;
    const enteredCanvasAt = typeof metadata.entered_canvas_at === 'string' && metadata.entered_canvas_at.length > 0
      ? metadata.entered_canvas_at
      : node.enteredCanvasAt;
    const lastActivityAt = typeof metadata.last_activity_at === 'string' && metadata.last_activity_at.length > 0
      ? metadata.last_activity_at
      : node.lastActivityAt;
    const workflowAreaGroupId = typeof metadata.workflow_area_group_id === 'string' && metadata.workflow_area_group_id.length > 0
      ? metadata.workflow_area_group_id
      : undefined;
    if (
      node.mediaBindingRevision === (row.media_binding_revision ?? node.mediaBindingRevision)
      && node.createdAt === row.created_at
      && node.updatedAt === row.updated_at
      && node.enteredCanvasAt === enteredCanvasAt
      && node.lastActivityAt === lastActivityAt
      && node.workflowAreaGroupId === workflowAreaGroupId
      && node.assetThumbnailUrl === assetThumbnailUrl
    ) {
      return node;
    }

    changed = true;
    return {
      ...node,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
      mediaBindingRevision: row.media_binding_revision ?? node.mediaBindingRevision,
      enteredCanvasAt,
      lastActivityAt,
      workflowAreaGroupId,
      assetThumbnailUrl,
    };
  });

  return changed ? nextNodes : existingNodes;
}

interface NormalizedCanvasGroupPersistence {
  groupId: string;
  childIds: string[];
  collapsed: boolean;
  label: string;
  position: { x: number; y: number };
  thumbnail?: string;
  thumbnails: string[];
}

interface CanvasGroupNormalizationResult {
  nodes: CanvasNode[];
  groupsToPersist: NormalizedCanvasGroupPersistence[];
  removedGroupIds: string[];
}

function areSameNodeIds(previous: string[] | undefined, next: string[]): boolean {
  if (!previous || previous.length !== next.length) {
    return false;
  }
  return next.every((id, index) => previous[index] === id);
}

function normalizeCanvasGroupTopology(nodes: CanvasNode[]): CanvasGroupNormalizationResult {
  if (nodes.length === 0) {
    return {
      nodes,
      groupsToPersist: [],
      removedGroupIds: [],
    };
  }

  const topology = buildCanvasGroupTopology(nodes);
  const nodeById = topology.nodeById as Map<string, CanvasNode>;
  const removedGroupIds: string[] = [];
  const groupStateById = new Map<string, {
    childIds: string[];
    thumbnail?: string;
    thumbnails: string[];
    groupNode: CanvasNode;
  }>();
  const groupsToPersist: NormalizedCanvasGroupPersistence[] = [];

  nodes.forEach((node) => {
    if (!node.groupChildIds?.length) {
      return;
    }

    const childIds = (topology.groupChildIdsByGroupId.get(node.id) || []) as string[];
    if (childIds.length === 0) {
      removedGroupIds.push(node.id);
      return;
    }

    const childNodes = childIds
      .map((childId) => nodeById.get(childId))
      .filter((childNode): childNode is CanvasNode => Boolean(childNode));
    const thumbnails = childNodes
      .map((childNode) => childNode.thumbnail)
      .filter((thumbnail): thumbnail is string => typeof thumbnail === 'string' && thumbnail.length > 0)
      .slice(0, 4);
    const thumbnail = thumbnails[0];

    groupStateById.set(node.id, {
      childIds,
      thumbnail,
      thumbnails,
      groupNode: node,
    });

    if (!areSameNodeIds(node.groupChildIds, childIds) || node.thumbnail !== thumbnail) {
      groupsToPersist.push({
        groupId: node.id,
        childIds,
        collapsed: node.groupCollapsed === true,
        label: node.groupLabel || `${childIds.length} items`,
        position: { ...node.position },
        thumbnail,
        thumbnails,
      });
    }
  });

  const keptGroupIdSet = new Set(groupStateById.keys());
  const childGroupIdMap = topology.groupIdByChildId as Map<string, string>;

  let changed = removedGroupIds.length > 0;
  const removedGroupIdSet = new Set(removedGroupIds);

  const nextNodes = nodes.reduce<CanvasNode[]>((result, node) => {
    if (removedGroupIdSet.has(node.id)) {
      return result;
    }

    if (node.groupChildIds?.length) {
      const groupState = groupStateById.get(node.id);
      if (!groupState) {
        return result;
      }

      const savedChildPositions = (node as CanvasNode & {
        savedChildPositions?: Record<string, { x: number; y: number }>;
      }).savedChildPositions;
      const nextSavedChildPositions = savedChildPositions
        ? Object.fromEntries(
            Object.entries(savedChildPositions).filter(([childId]) => groupState.childIds.includes(childId)),
          )
        : undefined;

      if (
        !areSameNodeIds(node.groupChildIds, groupState.childIds)
        || node.thumbnail !== groupState.thumbnail
        || (
          savedChildPositions
          && Object.keys(savedChildPositions).length !== Object.keys(nextSavedChildPositions || {}).length
        )
      ) {
        changed = true;
      }

      result.push(ensureNodePersistenceFields({
        ...node,
        groupChildIds: groupState.childIds,
        thumbnail: groupState.thumbnail,
        ...(nextSavedChildPositions ? { savedChildPositions: nextSavedChildPositions } : {}),
      } as CanvasNode, `normalizeCanvasGroupTopology(${node.id})`));
      return result;
    }

    const normalizedGroupId = childGroupIdMap.get(node.id);
    const nextGroupId = normalizedGroupId && keptGroupIdSet.has(normalizedGroupId)
      ? normalizedGroupId
      : undefined;

    if (node.groupId !== nextGroupId) {
      changed = true;
      result.push({
        ...node,
        groupId: nextGroupId,
      });
      return result;
    }

    result.push(node);
    return result;
  }, []);

  return {
    nodes: changed ? nextNodes : nodes,
    groupsToPersist,
    removedGroupIds,
  };
}

function hasTemplateSlotContent(value: CanvasTemplateSlotValue | undefined): boolean {
  return Boolean(
    value?.workflowBinding
    || value?.sourceNodeId
    || value?.assetId
    || value?.sourceUrl?.trim()
    || value?.previewUrl?.trim()
    || value?.text?.trim()
    || value?.selectValue?.trim(),
  );
}

function areCanvasEdgesEqual(left: CanvasEdge[], right: CanvasEdge[]): boolean {
  if (left === right) {
    return true;
  }

  if (left.length !== right.length) {
    return false;
  }

  return left.every((edge, index) => {
    const other = right[index];
    return edge.id === other.id
      && edge.source === other.source
      && edge.target === other.target
      && edge.sourceHandle === other.sourceHandle
      && edge.targetHandle === other.targetHandle
      && edge.relationType === other.relationType
      && edge.relationLabel === other.relationLabel
      && edge.createdAt === other.createdAt;
  });
}

function dedupeCanvasNodesById(nodes: CanvasNode[]): CanvasNode[] {
  const nodeById = new Map<string, CanvasNode>();
  for (const node of nodes) {
    nodeById.delete(node.id);
    nodeById.set(node.id, node);
  }

  return nodeById.size === nodes.length ? nodes : [...nodeById.values()];
}

function dedupeCanvasEdgesById(edges: CanvasEdge[]): CanvasEdge[] {
  const edgeById = new Map<string, CanvasEdge>();
  for (const edge of edges) {
    edgeById.delete(edge.id);
    edgeById.set(edge.id, edge);
  }

  return edgeById.size === edges.length ? edges : [...edgeById.values()];
}

function normalizeCanvasEdgeMergePart(value: string | null | undefined): string {
  return typeof value === 'string' ? value.trim() : '';
}

function buildNormalizedCanvasEdgeIdentity(
  nodeById: Map<string, CanvasNode>,
  edge: Pick<CanvasEdge, 'source' | 'target' | 'sourceHandle' | 'targetHandle' | 'relationType' | 'relationLabel'>,
): {
  source: string;
  target: string;
  sourceHandle: string | null;
  targetHandle: string | null;
  relationType: CanvasEdge['relationType'] | null;
  relationLabel: string | null;
} {
  const semanticHandles = resolveTemplateWorkflowSemanticHandles(nodeById, edge);

  return {
    source: normalizeCanvasEdgeMergePart(edge.source),
    target: normalizeCanvasEdgeMergePart(edge.target),
    sourceHandle: semanticHandles?.sourceHandle ?? (edge.sourceHandle || null),
    targetHandle: semanticHandles?.targetHandle ?? (edge.targetHandle || null),
    relationType: edge.relationType || null,
    relationLabel: edge.relationLabel || null,
  };
}

function buildCanvasEdgeMergeKey(
  nodeById: Map<string, CanvasNode>,
  edge: Pick<CanvasEdge, 'source' | 'target' | 'sourceHandle' | 'targetHandle' | 'relationType' | 'relationLabel'>,
): string {
  return buildCanvasEdgeSemanticKey(buildNormalizedCanvasEdgeIdentity(nodeById, edge));
}

function normalizeCanvasEdgeForNodes(nodeById: Map<string, CanvasNode>, edge: CanvasEdge): CanvasEdge {
  const edgeIdentity = buildNormalizedCanvasEdgeIdentity(nodeById, edge);
  const trimmedEdgeId = normalizeCanvasEdgeMergePart(edge.id);
  const edgeId = UUID_LIKE_RE.test(trimmedEdgeId)
    ? trimmedEdgeId
    : buildCanonicalCanvasEdgeId(edgeIdentity);

  return {
    ...edge,
    id: edgeId,
    source: edgeIdentity.source,
    target: edgeIdentity.target,
    sourceHandle: edgeIdentity.sourceHandle || undefined,
    targetHandle: edgeIdentity.targetHandle || undefined,
    relationType: edgeIdentity.relationType || undefined,
    relationLabel: edgeIdentity.relationLabel || undefined,
  };
}

function normalizeCanvasEdgesForNodes(nodes: CanvasNode[], edges: CanvasEdge[]): CanvasEdge[] {
  const nodeById = new Map(nodes.map((node) => [node.id, node] as const));
  const normalizedInputEdges = edges.map((edge) => normalizeCanvasEdgeForNodes(nodeById, edge));
  const dedupedEdges = dedupeCanvasEdgesById(normalizedInputEdges);
  if (dedupedEdges.length <= 1) {
    return dedupedEdges;
  }

  const edgeByMergeKey = new Map<string, CanvasEdge>();

  dedupedEdges.forEach((edge) => {
    const mergeKey = buildCanvasEdgeMergeKey(nodeById, edge);
    edgeByMergeKey.delete(mergeKey);
    edgeByMergeKey.set(mergeKey, edge);
  });

  return edgeByMergeKey.size === dedupedEdges.length ? dedupedEdges : [...edgeByMergeKey.values()];
}

function mergeCanvasNodesById(baseNodes: CanvasNode[], incomingNodes: CanvasNode[]): CanvasNode[] {
  const normalizedBaseNodes = dedupeCanvasNodesById(baseNodes);
  if (incomingNodes.length === 0) {
    return normalizedBaseNodes;
  }

  const existingNodeIds = new Set(normalizedBaseNodes.map((node) => node.id));
  const appendedNodes = dedupeCanvasNodesById(incomingNodes).filter((node) => !existingNodeIds.has(node.id));
  return appendedNodes.length === 0 ? normalizedBaseNodes : [...normalizedBaseNodes, ...appendedNodes];
}

function mergeCanvasEdgesForNodes(
  nodes: CanvasNode[],
  baseEdges: CanvasEdge[],
  incomingEdges: CanvasEdge[],
): CanvasEdge[] {
  const normalizedBaseEdges = normalizeCanvasEdgesForNodes(nodes, baseEdges);
  if (incomingEdges.length === 0) {
    return normalizedBaseEdges;
  }

  const normalizedIncomingEdges = normalizeCanvasEdgesForNodes(nodes, incomingEdges);
  if (normalizedIncomingEdges.length === 0) {
    return normalizedBaseEdges;
  }

  const nodeById = new Map(nodes.map((node) => [node.id, node] as const));
  const existingEdgeIds = new Set(normalizedBaseEdges.map((edge) => edge.id));
  const existingMergeKeys = new Set(normalizedBaseEdges.map((edge) => buildCanvasEdgeMergeKey(nodeById, edge)));

  const appendedEdges = normalizedIncomingEdges.filter((edge) => {
    if (existingEdgeIds.has(edge.id)) {
      return false;
    }

    const mergeKey = buildCanvasEdgeMergeKey(nodeById, edge);
    if (existingMergeKeys.has(mergeKey)) {
      return false;
    }

    existingEdgeIds.add(edge.id);
    existingMergeKeys.add(mergeKey);
    return true;
  });

  return appendedEdges.length === 0 ? normalizedBaseEdges : [...normalizedBaseEdges, ...appendedEdges];
}

function reconcileTemplateWorkflowBindings(
  nodes: CanvasNode[],
  _canvasEdges: CanvasEdge[],
): { nodes: CanvasNode[]; changedNodeIds: string[]; removedBindingCount: number } {
  void _canvasEdges;

  if (nodes.length === 0) {
    return { nodes, changedNodeIds: [], removedBindingCount: 0 };
  }

  const nodeById = new Map(nodes.map((node) => [node.id, node] as const));

  const changedNodeIds: string[] = [];
  let removedBindingCount = 0;

  const nextNodes = nodes.map((node) => {
    const instance = node.templateInstance;
    if (!instance) {
      return node;
    }

    const slotEntries = Object.entries(instance.values || {});
    if (slotEntries.length === 0) {
      return node;
    }

    let nextValues = instance.values;
    let nodeChanged = false;
    const slotByKey = new Map(getTemplateInstanceSlots(instance).map((slot) => [slot.key, slot] as const));

    slotEntries.forEach(([slotKey, value]) => {
      if (!value) {
        return;
      }

      const sourceNodeId = value.sourceNodeId?.trim();
      const sourceNode = sourceNodeId ? nodeById.get(sourceNodeId) : null;
      const shouldClearDirectSource = Boolean(
        sourceNodeId
        && (
          !sourceNode
          || sourceNode.id === node.id
          || Boolean(sourceNode.templateInstance)
        )
      );

      const binding = value?.workflowBinding;
      const sourceTemplateNodeId = binding?.sourceTemplateNodeId?.trim();
      const sourceTemplateNode = sourceTemplateNodeId ? nodeById.get(sourceTemplateNodeId) : null;
      const shouldClearWorkflowBinding = Boolean(
        binding
        && (
          !sourceTemplateNodeId
          || sourceTemplateNodeId === node.id
          || !sourceTemplateNode
          || !sourceTemplateNode.templateInstance
        )
      );

      if (!shouldClearDirectSource && !shouldClearWorkflowBinding) {
        return;
      }

      if (!nodeChanged) {
        nextValues = { ...instance.values };
        nodeChanged = true;
      }

      const nextValue: CanvasTemplateSlotValue = { ...value };
      if (shouldClearDirectSource) {
        delete nextValue.sourceNodeId;
        delete nextValue.sourceUrl;
        delete nextValue.previewUrl;
        delete nextValue.assetId;
        if (slotByKey.get(slotKey)?.type === 'text') {
          delete nextValue.text;
        }
      }

      if (shouldClearWorkflowBinding) {
        delete nextValue.workflowBinding;
      }

      if (hasTemplateSlotContent(nextValue)) {
        nextValues[slotKey] = nextValue;
      } else {
        delete nextValues[slotKey];
      }
      removedBindingCount += 1;
    });

    if (!nodeChanged) {
      return node;
    }

    changedNodeIds.push(node.id);

    return {
      ...node,
      templateInstance: {
        ...instance,
        values: nextValues,
      },
    };
  });

  return changedNodeIds.length > 0
    ? { nodes: nextNodes, changedNodeIds, removedBindingCount }
    : { nodes, changedNodeIds, removedBindingCount: 0 };
}

function resolveTemplateWorkflowSlotKeyForEdge(
  instance: CanvasTemplateInstance,
  targetHandle: string | null | undefined,
  sourceNodeId: string,
  outputIndex: number,
): string | null {
  const mediaSlots = getTemplateInstanceSlots(instance).filter((slot) => slot.type === 'image' || slot.type === 'video');
  if (mediaSlots.length === 0) {
    return null;
  }

  const explicitSlotKey = parseTemplateWorkflowInputHandleId(targetHandle);
  if (explicitSlotKey && mediaSlots.some((slot) => slot.key === explicitSlotKey)) {
    return explicitSlotKey;
  }

  const directSlot = mediaSlots.find((slot) => instance.values[slot.key]?.sourceNodeId === sourceNodeId);
  if (directSlot) {
    return directSlot.key;
  }

  const boundSlot = mediaSlots.find((slot) => {
    const binding = instance.values[slot.key]?.workflowBinding;
    return binding?.sourceTemplateNodeId === sourceNodeId
      && Math.max(0, Math.round(binding.outputIndex || 0)) === outputIndex;
  });

  return boundSlot?.key ?? null;
}

function resolveTemplateWorkflowSemanticHandles(
  nodeById: Map<string, CanvasNode>,
  edge: Pick<CanvasEdge, 'source' | 'target' | 'sourceHandle' | 'targetHandle'>,
): { sourceHandle: string; targetHandle: string } | null {
  const sourceNode = nodeById.get(edge.source);
  const targetNode = nodeById.get(edge.target);
  if (!sourceNode?.templateInstance || !targetNode?.templateInstance) {
    return null;
  }

  const outputIndex = parseTemplateWorkflowOutputIndex(edge.sourceHandle);
  const targetSlotKey = resolveTemplateWorkflowSlotKeyForEdge(
    targetNode.templateInstance,
    edge.targetHandle,
    edge.source,
    outputIndex,
  );
  if (!targetSlotKey) {
    return null;
  }

  return {
    sourceHandle: buildTemplateWorkflowOutputHandleId(outputIndex),
    targetHandle: buildTemplateWorkflowInputHandleId(targetSlotKey),
  };
}

function persistTemplateWorkflowBindingCleanup(
  projectId: string | null | undefined,
  nodes: CanvasNode[],
  changedNodeIds: string[],
) {
  if (!projectId || changedNodeIds.length === 0) {
    return;
  }

  const changedIdSet = new Set(changedNodeIds);
  nodes.forEach((node) => {
    if (!changedIdSet.has(node.id)) {
      return;
    }

    _enqueueBatchUpdate(projectId, {
      id: node.id,
      metadata: {
        template_instance: node.templateInstance || null,
      },
    });
  });
}

let _smartEditSubmitHandler: (() => void | Promise<void>) | null = null;
let _smartEditCloseHandler: (() => boolean | Promise<boolean>) | null = null;

async function _shouldCloseSmartEditSession(): Promise<boolean> {
  return _smartEditCloseHandler ? _smartEditCloseHandler() : true;
}

function _getSmartEditComposerResetState() {
  return {
    smartEditPrompt: '',
    smartEditGlobalInstruction: '',
    smartEditPendingCount: 0,
    smartEditHasLocalChanges: false,
    smartEditLocalEditsDone: false,
    smartEditSubmitting: false,
  };
}

function _getSmartEditSessionResetState() {
  return {
    smartEditNodeId: null,
    smartEditTool: null,
    smartEditBrushSize: DEFAULT_SMART_EDIT_BRUSH_SIZE,
    smartEditBrushColor: DEFAULT_SMART_EDIT_BRUSH_COLOR,
    smartEditMaskDataUrl: null,
    ..._getSmartEditComposerResetState(),
  };
}


// ==========================================
// ★ 节点位置防抖保存（自由节点 + 序列节点共用）
// ==========================================

function _debouncedPositionSave(projectId: string, nodeId: string, position: { x: number; y: number }) {
  // ★ 治本：位置更新复用统一 batch-update 队列，避免同一节点的位置/metadata 拆成两次请求
  _enqueueBatchUpdate(projectId, {
    id: nodeId,
    canvas_position: position,
  });
}

// ==========================================
// ★ 画布连线持久化（增量 patch）
// ==========================================

// ★ 增量 edge 操作队列：收集 300ms 内的 add/remove，合并为一次 PATCH
type PendingCanvasEdgeAdd = CanvasEdgeSyncPayloadItem & { mutationSeq: number };
type PendingCanvasEdgeRemove = { edgeId: string; mutationSeq: number };

const _pendingEdgeAdds: PendingCanvasEdgeAdd[] = [];
const _pendingEdgeRemoves: PendingCanvasEdgeRemove[] = [];
const _deferredEdgeAdds = new Map<string, PendingCanvasEdgeAdd>();
let _canvasEdgeMutationSeq = 0;
let _edgePatchTimer: ReturnType<typeof setTimeout> | null = null;
let _deferredFullEdgeSyncProjectId: string | null = null;
let _deferredFullEdgeSyncSignature: string | null = null;
let _deferredFullEdgeSyncPayload: CanvasEdgeSyncPayloadItem[] | null = null;
let _deferredFullEdgeSyncPersistablePayload: CanvasEdgeSyncPayloadItem[] | null = null;
let _deferredFullEdgeSyncMutationCutoff: number | null = null;
let _deferredFullEdgeSyncIntentSeq: number | null = null;
let _scheduledFullEdgeSyncContext: CanvasHistoryRestoreContext | null = null;
let _scheduledFullEdgeSyncSignature: string | null = null;
let _scheduledFullEdgeSyncPayload: CanvasEdgeSyncPayloadItem[] | null = null;
let _scheduledFullEdgeSyncMutationCutoff: number | null = null;
let _scheduledFullEdgeSyncIntentId: number | null = null;
let _canvasEdgeFullSyncIntentSeq = 0;

interface SuspendedCanvasEdgeQueue {
  pendingAdds: PendingCanvasEdgeAdd[];
  pendingRemoves: PendingCanvasEdgeRemove[];
  deferredAdds: Array<[string, PendingCanvasEdgeAdd]>;
  deferredFullSyncSignature: string | null;
  deferredFullSyncPayload: CanvasEdgeSyncPayloadItem[] | null;
  deferredFullSyncPersistablePayload: CanvasEdgeSyncPayloadItem[] | null;
  deferredFullSyncMutationCutoff: number | null;
  deferredFullSyncIntentSeq: number | null;
  scheduledFullSyncSignature: string | null;
  scheduledFullSyncPayload: CanvasEdgeSyncPayloadItem[] | null;
  scheduledFullSyncMutationCutoff: number | null;
  scheduledFullSyncIntentSeq: number | null;
  fullSyncRetryPayload: CanvasEdgeSyncPayloadItem[] | null;
  fullSyncMutationCutoff: number | null;
  fullSyncIntentSeq: number | null;
  fullSyncDesiredEdgeIds: string[] | null;
  hadPatchRetry: boolean;
}

const MAX_SUSPENDED_EDGE_PROJECTS = 8;
const MAX_SUSPENDED_EDGE_MUTATIONS = 2_000;
const _suspendedCanvasEdgeQueues = new Map<string, SuspendedCanvasEdgeQueue>();

function _storeSuspendedCanvasEdgeQueue(projectId: string, queue: SuspendedCanvasEdgeQueue) {
  const pendingAdds = queue.pendingAdds.slice(-MAX_SUSPENDED_EDGE_MUTATIONS);
  const pendingRemoves = queue.pendingRemoves.slice(-MAX_SUSPENDED_EDGE_MUTATIONS);
  const deferredAdds = queue.deferredAdds.slice(-MAX_SUSPENDED_EDGE_MUTATIONS);
  if (
    pendingAdds.length === 0
    && pendingRemoves.length === 0
    && deferredAdds.length === 0
    && !queue.deferredFullSyncSignature
    && !queue.deferredFullSyncPayload
    && !queue.scheduledFullSyncSignature
    && !queue.scheduledFullSyncPayload
    && !queue.fullSyncRetryPayload
    && !queue.hadPatchRetry
  ) {
    _suspendedCanvasEdgeQueues.delete(projectId);
    return;
  }
  _suspendedCanvasEdgeQueues.delete(projectId);
  _suspendedCanvasEdgeQueues.set(projectId, {
    ...queue,
    pendingAdds,
    pendingRemoves,
    deferredAdds,
  });
  while (_suspendedCanvasEdgeQueues.size > MAX_SUSPENDED_EDGE_PROJECTS) {
    const oldestProjectId = _suspendedCanvasEdgeQueues.keys().next().value as string | undefined;
    if (!oldestProjectId) break;
    _suspendedCanvasEdgeQueues.delete(oldestProjectId);
  }
}

function _suspendCanvasEdgeMutations(
  context: CanvasHistoryRestoreContext,
  adds: PendingCanvasEdgeAdd[],
  removes: PendingCanvasEdgeRemove[],
) {
  if (!context.projectId || (adds.length === 0 && removes.length === 0)) return;
  const activeContext = _canvasEdgeQueueContext;
  if (
    activeContext?.projectId === context.projectId
    && useVisualEditorStore.getState().projectId === context.projectId
    && _isCanvasEdgeHydrationActive(activeContext)
  ) {
    _pendingEdgeAdds.push(...adds);
    _pendingEdgeRemoves.push(...removes);
    _scheduleEdgePatch(context.projectId, activeContext);
    return;
  }
  const existing = _suspendedCanvasEdgeQueues.get(context.projectId);
  _storeSuspendedCanvasEdgeQueue(context.projectId, {
    pendingAdds: [...(existing?.pendingAdds || []), ...adds],
    pendingRemoves: [...(existing?.pendingRemoves || []), ...removes],
    deferredAdds: existing?.deferredAdds || [],
    deferredFullSyncSignature: existing?.deferredFullSyncSignature || null,
    deferredFullSyncPayload: existing?.deferredFullSyncPayload || null,
    deferredFullSyncPersistablePayload: existing?.deferredFullSyncPersistablePayload || null,
    deferredFullSyncMutationCutoff: existing?.deferredFullSyncMutationCutoff ?? null,
    deferredFullSyncIntentSeq: existing?.deferredFullSyncIntentSeq ?? null,
    scheduledFullSyncSignature: existing?.scheduledFullSyncSignature || null,
    scheduledFullSyncPayload: existing?.scheduledFullSyncPayload || null,
    scheduledFullSyncMutationCutoff: existing?.scheduledFullSyncMutationCutoff ?? null,
    scheduledFullSyncIntentSeq: existing?.scheduledFullSyncIntentSeq ?? null,
    fullSyncRetryPayload: existing?.fullSyncRetryPayload || null,
    fullSyncMutationCutoff: existing?.fullSyncMutationCutoff ?? null,
    fullSyncIntentSeq: existing?.fullSyncIntentSeq ?? null,
    fullSyncDesiredEdgeIds: existing?.fullSyncDesiredEdgeIds || null,
    hadPatchRetry: true,
  });
}

function _suspendCanvasFullSyncRetry(
  context: CanvasHistoryRestoreContext,
  edges: CanvasEdgeSyncPayloadItem[],
  mutationCutoff: number,
  desiredEdgeIds: string[],
  fullIntentSeq: number,
) {
  if (!context.projectId) return;
  const activeContext = _canvasEdgeQueueContext;
  if (
    activeContext?.projectId === context.projectId
    && useVisualEditorStore.getState().projectId === context.projectId
    && _isCanvasEdgeHydrationActive(activeContext)
  ) {
    _scheduleFullEdgeSyncRetry(context.projectId, edges, activeContext, mutationCutoff, desiredEdgeIds, fullIntentSeq);
    return;
  }
  const existing = _suspendedCanvasEdgeQueues.get(context.projectId);
  const keepExistingRetry = (existing?.fullSyncIntentSeq ?? -1) > fullIntentSeq;
  _storeSuspendedCanvasEdgeQueue(context.projectId, {
    pendingAdds: existing?.pendingAdds || [],
    pendingRemoves: existing?.pendingRemoves || [],
    deferredAdds: existing?.deferredAdds || [],
    deferredFullSyncSignature: existing?.deferredFullSyncSignature || null,
    deferredFullSyncPayload: existing?.deferredFullSyncPayload || null,
    deferredFullSyncPersistablePayload: existing?.deferredFullSyncPersistablePayload || null,
    deferredFullSyncMutationCutoff: existing?.deferredFullSyncMutationCutoff ?? null,
    deferredFullSyncIntentSeq: existing?.deferredFullSyncIntentSeq ?? null,
    scheduledFullSyncSignature: existing?.scheduledFullSyncSignature || null,
    scheduledFullSyncPayload: existing?.scheduledFullSyncPayload || null,
    scheduledFullSyncMutationCutoff: existing?.scheduledFullSyncMutationCutoff ?? null,
    scheduledFullSyncIntentSeq: existing?.scheduledFullSyncIntentSeq ?? null,
    fullSyncRetryPayload: keepExistingRetry ? existing?.fullSyncRetryPayload || null : edges,
    fullSyncMutationCutoff: keepExistingRetry ? existing?.fullSyncMutationCutoff ?? null : mutationCutoff,
    fullSyncIntentSeq: keepExistingRetry ? existing?.fullSyncIntentSeq ?? null : fullIntentSeq,
    fullSyncDesiredEdgeIds: keepExistingRetry ? existing?.fullSyncDesiredEdgeIds || null : desiredEdgeIds,
    hadPatchRetry: existing?.hadPatchRetry || false,
  });
}

function _freezeCanvasEdgeQueueOwner(flushSuspendedOwner = false) {
  const previousContext = _canvasEdgeQueueContext;
  if (previousContext?.projectId) {
    const existing = _suspendedCanvasEdgeQueues.get(previousContext.projectId);
    const previousState = useVisualEditorStore.getState();
    const inFlightFullSyncPayload = (
      _edgeSyncInFlight
      && areCanvasProjectEpochContextsEqual(_edgeSyncContext, previousContext)
    )
      ? previousState.canvasEdges.map(buildCanvasEdgeSyncPayloadItem)
      : null;
    const newestDeferredFullSync = [
      _deferredFullEdgeSyncPayload && _deferredFullEdgeSyncIntentSeq !== null
        ? {
          payload: _deferredFullEdgeSyncPayload,
          persistablePayload: _deferredFullEdgeSyncPersistablePayload || [],
          mutationCutoff: _deferredFullEdgeSyncMutationCutoff,
          intentSeq: _deferredFullEdgeSyncIntentSeq,
          signature: _deferredFullEdgeSyncSignature,
        }
        : null,
      existing?.deferredFullSyncPayload && existing.deferredFullSyncIntentSeq !== null
        ? {
          payload: existing.deferredFullSyncPayload,
          persistablePayload: existing.deferredFullSyncPersistablePayload || [],
          mutationCutoff: existing.deferredFullSyncMutationCutoff,
          intentSeq: existing.deferredFullSyncIntentSeq,
          signature: existing.deferredFullSyncSignature,
        }
        : null,
    ].filter((candidate): candidate is NonNullable<typeof candidate> => Boolean(candidate))
      .sort((left, right) => right.intentSeq - left.intentSeq)[0] ?? null;
    const newestScheduledFullSync = [
      _scheduledFullEdgeSyncPayload && _scheduledFullEdgeSyncIntentId !== null
        ? {
          payload: _scheduledFullEdgeSyncPayload,
          mutationCutoff: _scheduledFullEdgeSyncMutationCutoff,
          intentSeq: _scheduledFullEdgeSyncIntentId,
          signature: _scheduledFullEdgeSyncSignature,
        }
        : null,
      inFlightFullSyncPayload && _edgeSyncIntentSeq !== null
        ? {
          payload: inFlightFullSyncPayload,
          mutationCutoff: _edgeSyncMutationCutoff,
          intentSeq: _edgeSyncIntentSeq,
          signature: buildCanvasEdgesSignature(previousState.canvasEdges),
        }
        : null,
      existing?.scheduledFullSyncPayload && existing.scheduledFullSyncIntentSeq !== null
        ? {
          payload: existing.scheduledFullSyncPayload,
          mutationCutoff: existing.scheduledFullSyncMutationCutoff,
          intentSeq: existing.scheduledFullSyncIntentSeq,
          signature: existing.scheduledFullSyncSignature,
        }
        : null,
    ].filter((candidate): candidate is NonNullable<typeof candidate> => Boolean(candidate))
      .sort((left, right) => right.intentSeq - left.intentSeq)[0] ?? null;
    const newestRetryFullSync = [
      _pendingFullEdgeSyncPayload && _pendingFullEdgeSyncIntentSeq !== null
        ? {
          payload: _pendingFullEdgeSyncPayload,
          mutationCutoff: _pendingFullEdgeSyncMutationCutoff,
          intentSeq: _pendingFullEdgeSyncIntentSeq,
          desiredEdgeIds: _pendingFullEdgeSyncDesiredEdgeIds,
        }
        : null,
      existing?.fullSyncRetryPayload && existing.fullSyncIntentSeq !== null
        ? {
          payload: existing.fullSyncRetryPayload,
          mutationCutoff: existing.fullSyncMutationCutoff,
          intentSeq: existing.fullSyncIntentSeq,
          desiredEdgeIds: existing.fullSyncDesiredEdgeIds,
        }
        : null,
    ].filter((candidate): candidate is NonNullable<typeof candidate> => Boolean(candidate))
      .sort((left, right) => right.intentSeq - left.intentSeq)[0] ?? null;
    _storeSuspendedCanvasEdgeQueue(previousContext.projectId, {
      pendingAdds: [...(existing?.pendingAdds || []), ..._pendingEdgeAdds],
      pendingRemoves: [...(existing?.pendingRemoves || []), ..._pendingEdgeRemoves],
      deferredAdds: [...(existing?.deferredAdds || []), ..._deferredEdgeAdds.entries()],
      deferredFullSyncSignature: newestDeferredFullSync?.signature || null,
      deferredFullSyncPayload: newestDeferredFullSync ? [...newestDeferredFullSync.payload] : null,
      deferredFullSyncPersistablePayload: newestDeferredFullSync ? [...newestDeferredFullSync.persistablePayload] : null,
      deferredFullSyncMutationCutoff: newestDeferredFullSync?.mutationCutoff ?? null,
      deferredFullSyncIntentSeq: newestDeferredFullSync?.intentSeq ?? null,
      scheduledFullSyncSignature: newestScheduledFullSync?.signature || null,
      scheduledFullSyncPayload: newestScheduledFullSync
        ? [...newestScheduledFullSync.payload]
        : null,
      scheduledFullSyncMutationCutoff: newestScheduledFullSync?.mutationCutoff ?? null,
      scheduledFullSyncIntentSeq: newestScheduledFullSync?.intentSeq ?? null,
      fullSyncRetryPayload: newestRetryFullSync ? [...newestRetryFullSync.payload] : null,
      fullSyncMutationCutoff: newestRetryFullSync?.mutationCutoff ?? null,
      fullSyncIntentSeq: newestRetryFullSync?.intentSeq ?? null,
      fullSyncDesiredEdgeIds: newestRetryFullSync?.desiredEdgeIds ?? null,
      hadPatchRetry: Boolean(_edgePatchRetryTimer) || existing?.hadPatchRetry || false,
    });
    if (flushSuspendedOwner) {
      void _flushSuspendedCanvasEdgeProject(previousContext.projectId).catch((error) => {
        console.error('[VisualEditorStore] 后台保存已挂起的画布连线失败:', error);
      });
    }
  }
  if (_edgePatchTimer) {
    clearTimeout(_edgePatchTimer);
    _edgePatchTimer = null;
  }
  if (_edgeSaveTimer) {
    clearTimeout(_edgeSaveTimer);
    _edgeSaveTimer = null;
  }
  _clearScheduledEdgePatchRetry();
  _clearPendingFullEdgeSyncRetry();
  _pendingEdgeAdds.length = 0;
  _pendingEdgeRemoves.length = 0;
  _deferredEdgeAdds.clear();
  _deferredFullEdgeSyncProjectId = null;
  _deferredFullEdgeSyncSignature = null;
  _deferredFullEdgeSyncPayload = null;
  _deferredFullEdgeSyncPersistablePayload = null;
  _deferredFullEdgeSyncMutationCutoff = null;
  _deferredFullEdgeSyncIntentSeq = null;
  _scheduledFullEdgeSyncContext = null;
  _scheduledFullEdgeSyncSignature = null;
  _scheduledFullEdgeSyncPayload = null;
  _scheduledFullEdgeSyncMutationCutoff = null;
  _scheduledFullEdgeSyncIntentId = null;
  _canvasEdgeQueueContext = null;
  _canvasEdgeHydrationLease = null;
}

function _claimCanvasEdgeQueueOwner(context: CanvasHistoryRestoreContext): string | null {
  _canvasEdgeQueueContext = context;
  if (!context.projectId) {
    _canvasEdgeHydrationLease = null;
    return null;
  }
  const token = generateUuidLike();
  _canvasEdgeHydrationLease = { ...context, token, active: false };
  return token;
}

function _claimCanvasEdgeQueue(context: CanvasHistoryRestoreContext): boolean {
  const getStore = () => useVisualEditorStore.getState();
  if (!isCanvasHistoryRestoreContextCurrent(getStore, context)) {
    return false;
  }
  if (!isCanvasEdgeQueueOwnedBy(context)) {
    _freezeCanvasEdgeQueueOwner(true);
    _claimCanvasEdgeQueueOwner(context);
  }
  return true;
}

function _pendingCanvasEdgeAddToCanvasEdge(edge: CanvasEdgeSyncPayloadItem): CanvasEdge {
  return {
    id: edge.id,
    source: edge.source_node_id,
    target: edge.target_node_id,
    sourceHandle: edge.source_handle || undefined,
    targetHandle: edge.target_handle || undefined,
    relationType: (edge.relation_type as CanvasEdge['relationType'] | null) || undefined,
    relationLabel: edge.relation_label || undefined,
  };
}

function _pendingCanvasEdgeAddToPayload(edge: PendingCanvasEdgeAdd): CanvasEdgeSyncPayloadItem {
  return {
    id: edge.id,
    source_node_id: edge.source_node_id,
    target_node_id: edge.target_node_id,
    source_handle: edge.source_handle,
    target_handle: edge.target_handle,
    relation_type: edge.relation_type,
    relation_label: edge.relation_label,
  };
}

function _activateCanvasEdgeHydration(
  projectId: string,
  hydrationToken: string,
): boolean {
  const lease = _canvasEdgeHydrationLease;
  const context = _canvasEdgeQueueContext;
  const currentState = useVisualEditorStore.getState();
  if (
    !lease
    || !context
    || lease.active
    || lease.token !== hydrationToken
    || lease.projectId !== projectId
    || currentState.projectId !== projectId
    || !areCanvasProjectEpochContextsEqual(lease, context)
    || !isCanvasHistoryRestoreContextCurrent(() => useVisualEditorStore.getState(), context)
  ) {
    return false;
  }

  const suspended = _suspendedCanvasEdgeQueues.get(projectId);
  const newestSuspendedFullSync = [
    suspended?.deferredFullSyncPayload && suspended.deferredFullSyncIntentSeq !== null
      ? {
        payload: suspended.deferredFullSyncPayload,
        mutationCutoff: suspended.deferredFullSyncMutationCutoff ?? -1,
        intentSeq: suspended.deferredFullSyncIntentSeq,
      }
      : null,
    suspended?.scheduledFullSyncPayload && suspended.scheduledFullSyncIntentSeq !== null
      ? {
        payload: suspended.scheduledFullSyncPayload,
        mutationCutoff: suspended.scheduledFullSyncMutationCutoff ?? -1,
        intentSeq: suspended.scheduledFullSyncIntentSeq,
      }
      : null,
    suspended?.fullSyncRetryPayload && suspended.fullSyncIntentSeq !== null
      ? {
        payload: suspended.fullSyncRetryPayload,
        mutationCutoff: suspended.fullSyncMutationCutoff ?? -1,
        intentSeq: suspended.fullSyncIntentSeq,
      }
      : null,
  ].filter((candidate): candidate is NonNullable<typeof candidate> => Boolean(candidate))
    .sort((left, right) => right.intentSeq - left.intentSeq)[0] ?? null;
  const suspendedFullSyncPayload = newestSuspendedFullSync?.payload || null;
  const suspendedFullSyncCutoff = newestSuspendedFullSync?.mutationCutoff ?? -1;
  const pendingAdds = [
    ...(suspended?.pendingAdds || []),
    ...(suspended?.deferredAdds.map(([, edge]) => edge) || []),
    ..._pendingEdgeAdds,
    ..._deferredEdgeAdds.values(),
  ].filter((edge) => edge.mutationSeq > suspendedFullSyncCutoff);
  const pendingRemoveItems = [
    ...(suspended?.pendingRemoves || []),
    ..._pendingEdgeRemoves,
  ].filter((remove) => remove.mutationSeq > suspendedFullSyncCutoff);
  const latestAddById = new Map<string, PendingCanvasEdgeAdd>();
  pendingAdds.forEach((edge) => {
    const existing = latestAddById.get(edge.id);
    if (!existing || edge.mutationSeq > existing.mutationSeq) latestAddById.set(edge.id, edge);
  });
  const latestRemoveById = new Map<string, PendingCanvasEdgeRemove>();
  pendingRemoveItems.forEach((remove) => {
    const existing = latestRemoveById.get(remove.edgeId);
    if (!existing || remove.mutationSeq > existing.mutationSeq) latestRemoveById.set(remove.edgeId, remove);
  });
  const normalizedPendingAdds = [...latestAddById.values()].filter((edge) => (
    edge.mutationSeq > (latestRemoveById.get(edge.id)?.mutationSeq ?? -1)
  ));
  const normalizedPendingRemoves = [...latestRemoveById.values()].filter((remove) => (
    remove.mutationSeq > (latestAddById.get(remove.edgeId)?.mutationSeq ?? -1)
  ));
  const mergedEdgeById = new Map(
    (suspendedFullSyncPayload
      ? suspendedFullSyncPayload.map(_pendingCanvasEdgeAddToCanvasEdge)
      : currentState.canvasEdges
    ).map((edge) => [edge.id, edge] as const),
  );
  normalizedPendingAdds.forEach((edge) => {
    mergedEdgeById.set(edge.id, _pendingCanvasEdgeAddToCanvasEdge(edge));
  });
  normalizedPendingRemoves.forEach((remove) => mergedEdgeById.delete(remove.edgeId));
  const { edges: mergedEdges } = filterCanvasEdgesWithExistingNodes(
    currentState.nodes,
    Array.from(mergedEdgeById.values()),
  );

  _pendingEdgeAdds.length = 0;
  _pendingEdgeAdds.push(...normalizedPendingAdds);
  _pendingEdgeRemoves.length = 0;
  _pendingEdgeRemoves.push(...normalizedPendingRemoves);
  _deferredEdgeAdds.clear();
  _deferredFullEdgeSyncProjectId = suspended?.deferredFullSyncSignature ? projectId : null;
  _deferredFullEdgeSyncSignature = suspended?.deferredFullSyncSignature || null;
  _deferredFullEdgeSyncPayload = suspended?.deferredFullSyncPayload || null;
  _deferredFullEdgeSyncPersistablePayload = suspended?.deferredFullSyncPersistablePayload || null;
  _deferredFullEdgeSyncMutationCutoff = suspended?.deferredFullSyncMutationCutoff ?? null;
  _deferredFullEdgeSyncIntentSeq = suspended?.deferredFullSyncIntentSeq ?? null;
  _suspendedCanvasEdgeQueues.delete(projectId);
  _canvasEdgeHydrationLease = { ...lease, active: true };
  useVisualEditorStore.setState({ canvasEdges: mergedEdges });

  const shouldRetryFullSync = Boolean(
    suspended?.fullSyncRetryPayload
    || suspended?.scheduledFullSyncPayload
    || suspended?.deferredFullSyncPayload
    || suspended?.scheduledFullSyncSignature
    || suspended?.deferredFullSyncSignature
    || _scheduledFullEdgeSyncSignature,
  );
  if (shouldRetryFullSync) {
    _saveCanvasEdges(projectId, context);
  }
  if (
    suspended?.hadPatchRetry
    || _pendingEdgeAdds.length > 0
    || _pendingEdgeRemoves.length > 0
  ) {
    _scheduleEdgePatch(projectId, context);
  }
  return true;
}

function _rememberDeferredFullCanvasEdgeSync(
  projectId: string,
  desiredEdges: CanvasEdge[],
  persistablePayload: CanvasEdgeSyncPayloadItem[],
  mutationCutoff: number,
  fullIntentSeq: number,
) {
  _deferredFullEdgeSyncProjectId = projectId;
  _deferredFullEdgeSyncSignature = buildCanvasEdgesSignature(desiredEdges);
  _deferredFullEdgeSyncPayload = desiredEdges.map(buildCanvasEdgeSyncPayloadItem);
  _deferredFullEdgeSyncPersistablePayload = [...persistablePayload];
  _deferredFullEdgeSyncMutationCutoff = mutationCutoff;
  _deferredFullEdgeSyncIntentSeq = fullIntentSeq;
}

function _clearDeferredFullCanvasEdgeSync() {
  _deferredFullEdgeSyncProjectId = null;
  _deferredFullEdgeSyncSignature = null;
  _deferredFullEdgeSyncPayload = null;
  _deferredFullEdgeSyncPersistablePayload = null;
  _deferredFullEdgeSyncMutationCutoff = null;
  _deferredFullEdgeSyncIntentSeq = null;
}

function _flushDeferredFullCanvasEdgeSyncIfReady(
  projectId: string,
  context: CanvasHistoryRestoreContext,
) {
  if (
    !isCanvasEdgeQueueOwnedBy(context)
    || _deferredFullEdgeSyncProjectId !== projectId
    || _deferredEdgeAdds.size > 0
  ) {
    return;
  }

  const latestSignature = buildCanvasEdgesSignature(useVisualEditorStore.getState().canvasEdges);
  if (_deferredFullEdgeSyncSignature && _deferredFullEdgeSyncSignature !== latestSignature) {
    _clearDeferredFullCanvasEdgeSync();
    _saveCanvasEdges(projectId, context);
    return;
  }

  _clearDeferredFullCanvasEdgeSync();
  _saveCanvasEdges(projectId, context);
}

function _buildDeferredFullSyncError(plan: CanvasEdgeFullSyncPlan): Error {
  const preview = plan.deferredEdgeIds.slice(0, 5).join(', ');
  return new Error(
    `[VisualEditorStore] Canvas edge full sync deferred: ${plan.deferredEdgeIds.length} edge(s) reference nodes that are not persisted yet${preview ? ` (${preview})` : ''}`,
  );
}

function _deferFullSyncEdgesUntilPersisted(
  projectId: string,
  plan: CanvasEdgeFullSyncPlan,
  context: CanvasHistoryRestoreContext,
  desiredEdges: CanvasEdge[],
  mutationCutoff: number,
  fullIntentSeq: number,
) {
  if (!isCanvasEdgeQueueOwnedBy(context)) return;
  plan.deferredAdds.forEach((edge) => {
    const pendingRemove = _pendingEdgeRemoves.find((remove) => remove.edgeId === edge.id);
    if (!pendingRemove) {
      _deferredEdgeAdds.set(edge.id, { ...edge, mutationSeq: ++_canvasEdgeMutationSeq });
    }
  });
  _rememberDeferredFullCanvasEdgeSync(
    projectId,
    desiredEdges,
    plan.edges,
    mutationCutoff,
    fullIntentSeq,
  );
  _scheduleEdgePatch(projectId, context);
}

async function _recoverFullCanvasEdgeSyncMissingNodes(
  projectId: string,
  edges: CanvasEdgeSyncPayloadItem[],
  missingNodeIds: string[],
  context: CanvasHistoryRestoreContext,
  mutationCutoff: number,
  desiredEdgeIds: string[],
  fullIntentSeq: number,
  options?: { keepalive?: boolean },
) {
  const getStore = () => useVisualEditorStore.getState();
  if (
    !isCanvasHistoryRestoreContextCurrent(getStore, context)
    || !isCanvasEdgeQueueOwnedBy(context)
  ) {
    return;
  }
  const recoveryPlan = buildMissingCanvasEdgeNodeRecoveryPlan(edges, missingNodeIds) as CanvasEdgeMissingNodeRecoveryPlan;
  _unmarkPersistedCanvasNodeIds(recoveryPlan.missingNodeIds);

  recoveryPlan.deferredAdds.forEach((edge) => {
    const pendingRemove = _pendingEdgeRemoves.find((remove) => remove.edgeId === edge.id);
    if (!pendingRemove) {
      _deferredEdgeAdds.set(edge.id, { ...edge, mutationSeq: ++_canvasEdgeMutationSeq });
    }
  });

  if (recoveryPlan.deferredAdds.length > 0) {
    const deferredIdSet = new Set(recoveryPlan.deferredEdgeIds);
    _rememberDeferredFullCanvasEdgeSync(
      projectId,
      edges.map(_pendingCanvasEdgeAddToCanvasEdge),
      edges.filter((edge) => !deferredIdSet.has(edge.id)),
      mutationCutoff,
      fullIntentSeq,
    );
  }

  try {
    await _persistDeferredEdgeEndpointNodes(projectId, options);
    if (
      !isCanvasHistoryRestoreContextCurrent(getStore, context)
      || !isCanvasEdgeQueueOwnedBy(context)
    ) {
      return;
    }
  } catch (recoveryError) {
    if (
      !isCanvasHistoryRestoreContextCurrent(getStore, context)
      || !isCanvasEdgeQueueOwnedBy(context)
    ) {
      return;
    }
    _scheduleEdgePatchRetry(projectId, context);
    console.error('[VisualEditorStore] 缺失连线端点节点恢复失败:', recoveryError);
  }

  if (recoveryPlan.deferredAdds.length === 0) {
    _scheduleFullEdgeSyncRetry(projectId, edges, context, mutationCutoff, desiredEdgeIds, fullIntentSeq);
  }
  if (_deferredEdgeAdds.size > 0) {
    _scheduleEdgePatch(projectId, context);
  }
}

async function _runFullCanvasEdgeSyncForState(
  projectId: string,
  nodes: CanvasNode[],
  edges: CanvasEdge[],
  options?: {
    keepalive?: boolean;
    extraPersistedNodeIds?: Iterable<string>;
    requireComplete?: boolean;
    context?: CanvasHistoryRestoreContext;
    mutationCutoff?: number;
    desiredEdgeIds?: string[];
    fullIntentSeq?: number;
  },
) {
  const getStore = () => useVisualEditorStore.getState();
  const syncContext = options?.context ?? {
    projectId,
    epoch: _canvasHistoryEpoch,
  };
  if (
    !isCanvasHistoryRestoreContextCurrent(getStore, syncContext)
    || !_claimCanvasEdgeQueue(syncContext)
  ) {
    return;
  }
  const mutationCutoff = options?.mutationCutoff ?? _canvasEdgeMutationSeq;
  const fullIntentSeq = options?.fullIntentSeq ?? ++_canvasEdgeFullSyncIntentSeq;
  const plan = buildCanvasEdgeFullSyncPlan(nodes, edges, options?.extraPersistedNodeIds);
  if (plan.deferredAdds.length > 0) {
    if (
      !isCanvasHistoryRestoreContextCurrent(getStore, syncContext)
      || !isCanvasEdgeQueueOwnedBy(syncContext)
    ) {
      return;
    }
    _deferFullSyncEdgesUntilPersisted(
      projectId,
      plan,
      syncContext,
      edges,
      mutationCutoff,
      fullIntentSeq,
    );
    // Commit the persistable subset immediately so a replace-to-deferred graph
    // still removes backend edges absent from the desired state. Deferred adds
    // remain queued until their endpoint nodes are acknowledged.
    await _runFullCanvasEdgeSync(
      projectId,
      plan.edges,
      syncContext,
      mutationCutoff,
      edges.map((edge) => edge.id),
      fullIntentSeq,
      options?.keepalive ? { keepalive: true } : undefined,
    );
    const deferredError = _buildDeferredFullSyncError(plan);
    if (options?.requireComplete) {
      throw deferredError;
    }
    console.warn(deferredError.message);
    return;
  }

  await _runFullCanvasEdgeSync(
    projectId,
    plan.edges,
    syncContext,
    mutationCutoff,
    options?.desiredEdgeIds ?? edges.map((edge) => edge.id),
    fullIntentSeq,
    options?.keepalive ? { keepalive: true } : undefined,
  );
}

function _consumeQueuedEdgeMutationsCoveredByFullSync(
  projectId: string,
  context: CanvasHistoryRestoreContext,
  mutationCutoff: number,
  fullIntentSeq?: number,
) {
  if (isCanvasEdgeQueueOwnedBy(context)) {
    for (let index = _pendingEdgeAdds.length - 1; index >= 0; index -= 1) {
      if (_pendingEdgeAdds[index].mutationSeq <= mutationCutoff) _pendingEdgeAdds.splice(index, 1);
    }
    for (let index = _pendingEdgeRemoves.length - 1; index >= 0; index -= 1) {
      if (_pendingEdgeRemoves[index].mutationSeq <= mutationCutoff) _pendingEdgeRemoves.splice(index, 1);
    }
    _deferredEdgeAdds.forEach((edge, edgeId) => {
      if (edge.mutationSeq <= mutationCutoff) _deferredEdgeAdds.delete(edgeId);
    });
  }
  const suspended = _suspendedCanvasEdgeQueues.get(projectId);
  if (!suspended) return;
  _storeSuspendedCanvasEdgeQueue(projectId, {
    ...suspended,
    pendingAdds: suspended.pendingAdds.filter((edge) => edge.mutationSeq > mutationCutoff),
    pendingRemoves: suspended.pendingRemoves.filter((remove) => remove.mutationSeq > mutationCutoff),
    deferredAdds: suspended.deferredAdds.filter(([, edge]) => edge.mutationSeq > mutationCutoff),
    ...(suspended.scheduledFullSyncMutationCutoff !== null
      && suspended.scheduledFullSyncMutationCutoff <= mutationCutoff
      && fullIntentSeq !== undefined
      && (suspended.scheduledFullSyncIntentSeq ?? -1) <= fullIntentSeq
      ? {
        scheduledFullSyncSignature: null,
        scheduledFullSyncPayload: null,
        scheduledFullSyncMutationCutoff: null,
        scheduledFullSyncIntentSeq: null,
      }
      : {}),
    ...(suspended.fullSyncMutationCutoff !== null
      && suspended.fullSyncMutationCutoff <= mutationCutoff
      && fullIntentSeq !== undefined
      && (suspended.fullSyncIntentSeq ?? -1) <= fullIntentSeq
      ? {
        fullSyncRetryPayload: null,
        fullSyncMutationCutoff: null,
        fullSyncIntentSeq: null,
        fullSyncDesiredEdgeIds: null,
      }
      : {}),
  });
}

function isPersistedCanvasNode(node: CanvasNode | undefined): boolean {
  return Boolean(node && _persistedCanvasNodeIds.has(node.id));
}

async function _persistDeferredEdgeEndpointNodes(projectId: string, options?: { keepalive?: boolean }) {
  if (_deferredEdgeAdds.size === 0) {
    return;
  }

  const initialState = useVisualEditorStore.getState();
  const endpointContext: CanvasHistoryRestoreContext = {
    projectId: initialState.projectId,
    epoch: _canvasHistoryEpoch,
  };
  const getStore = () => useVisualEditorStore.getState();
  if (
    initialState.projectId !== projectId
    || !isCanvasHistoryRestoreContextCurrent(getStore, endpointContext)
    || !isCanvasEdgeQueueOwnedBy(endpointContext)
  ) {
    return;
  }
  const latestNodes = initialState.nodes;
  const nodeById = new Map(latestNodes.map((node) => [node.id, node] as const));
  const endpointNodeIds = new Set<string>();

  _deferredEdgeAdds.forEach((edge) => {
    endpointNodeIds.add(edge.source_node_id);
    endpointNodeIds.add(edge.target_node_id);
  });

  const nodesToPersist = [...endpointNodeIds]
    .map((nodeId) => nodeById.get(nodeId))
    .filter((node): node is CanvasNode => Boolean(node && !isPersistedCanvasNode(node)));

  if (nodesToPersist.length === 0) {
    return;
  }

  const { batchCreateCanvasNodes } = await import('@/lib/api/canvas-nodes');
  if (
    !isCanvasHistoryRestoreContextCurrent(getStore, endpointContext)
    || !isCanvasEdgeQueueOwnedBy(endpointContext)
  ) {
    return;
  }
  const response = await batchCreateCanvasNodes(
    projectId,
    nodesToPersist.map((node) => buildCanvasNodeCreatePayload(node)),
    {
      keepalive: options?.keepalive,
      // Endpoint recovery is not an Undo operation. Respect an explicit
      // upload-delete fence so a late edge flush cannot revive a deleted node.
      respectDeleteIntents: true,
    },
  );
  if (
    !isCanvasHistoryRestoreContextCurrent(getStore, endpointContext)
    || !isCanvasEdgeQueueOwnedBy(endpointContext)
  ) {
    return;
  }
  useVisualEditorStore.setState((state) => ({
    ...(state.projectId === projectId && _canvasHistoryEpoch === endpointContext.epoch
      ? { nodes: hydratePersistedNodeMetadata(state.nodes, response.nodes) }
      : {}),
  }));
  if (
    !isCanvasHistoryRestoreContextCurrent(getStore, endpointContext)
    || !isCanvasEdgeQueueOwnedBy(endpointContext)
  ) {
    return;
  }
  flushDeferredEdgeAdds(projectId);
}

export function filterCanvasEdgesWithExistingNodes(
  nodes: CanvasNode[],
  edges: CanvasEdge[],
): { edges: CanvasEdge[]; removedEdgeIds: string[] } {
  const nodeIdSet = new Set(nodes.map((node) => node.id));
  const normalizedEdges = normalizeCanvasEdgesForNodes(nodes, edges);
  const filteredEdges: CanvasEdge[] = [];
  const removedEdgeIds: string[] = [];

  normalizedEdges.forEach((edge) => {
    if (nodeIdSet.has(edge.source) && nodeIdSet.has(edge.target)) {
      filteredEdges.push(edge);
      return;
    }

    removedEdgeIds.push(edge.id);
  });

  return {
    edges: filteredEdges,
    removedEdgeIds,
  };
}

type CanvasEdgeSyncPayloadItem = {
  id: string;
  source_node_id: string;
  target_node_id: string;
  source_handle: string | null;
  target_handle: string | null;
  relation_type: string | null;
  relation_label: string | null;
};

type CanvasEdgeFullSyncPlan = {
  edges: CanvasEdgeSyncPayloadItem[];
  deferredAdds: CanvasEdgeSyncPayloadItem[];
  deferredEdgeIds: string[];
};

type CanvasEdgeMissingNodeRecoveryPlan = {
  missingNodeIds: string[];
  deferredAdds: CanvasEdgeSyncPayloadItem[];
  deferredEdgeIds: string[];
};

function buildCanvasEdgeSyncPayloadItem(edge: CanvasEdge): CanvasEdgeSyncPayloadItem {
  return {
    id: edge.id,
    source_node_id: edge.source,
    target_node_id: edge.target,
    source_handle: edge.sourceHandle || null,
    target_handle: edge.targetHandle || null,
    relation_type: edge.relationType || null,
    relation_label: edge.relationLabel || null,
  };
}

function buildCanvasEdgeFullSyncPlan(
  nodes: CanvasNode[],
  edges: CanvasEdge[],
  extraPersistedNodeIds?: Iterable<string>,
): CanvasEdgeFullSyncPlan {
  const { edges: existingEdges } = filterCanvasEdgesWithExistingNodes(nodes, edges);
  const nodeById = new Map(nodes.map((node) => [node.id, node] as const));
  const extraPersistedIdSet = new Set(extraPersistedNodeIds || []);

  const persistedEdges: CanvasEdgeSyncPayloadItem[] = [];
  const deferredAdds: CanvasEdgeSyncPayloadItem[] = [];
  const deferredEdgeIds: string[] = [];

  existingEdges.forEach((edge) => {
    const sourceNode = nodeById.get(edge.source);
    const targetNode = nodeById.get(edge.target);

    if (!sourceNode || !targetNode) {
      return;
    }

    const payloadItem = buildCanvasEdgeSyncPayloadItem(edge);
    const sourcePersisted = extraPersistedIdSet.has(edge.source) || isPersistedCanvasNode(sourceNode);
    const targetPersisted = extraPersistedIdSet.has(edge.target) || isPersistedCanvasNode(targetNode);
    if (!sourcePersisted || !targetPersisted) {
      deferredAdds.push(payloadItem);
      deferredEdgeIds.push(edge.id);
      return;
    }

    persistedEdges.push(payloadItem);
  });

  return {
    edges: persistedEdges,
    deferredAdds,
    deferredEdgeIds,
  };
}

export function buildPersistableCanvasEdgeSyncPayload(
  nodes: CanvasNode[],
  edges: CanvasEdge[],
  extraPersistedNodeIds?: Iterable<string>,
): CanvasEdgeSyncPayloadItem[] {
  return buildCanvasEdgeFullSyncPlan(nodes, edges, extraPersistedNodeIds).edges;
}

function flushDeferredEdgeAdds(projectId: string) {
  const context: CanvasHistoryRestoreContext = { projectId, epoch: _canvasHistoryEpoch };
  if (!_claimCanvasEdgeQueue(context)) return;
  if (_deferredEdgeAdds.size === 0) {
    _flushDeferredFullCanvasEdgeSyncIfReady(projectId, context);
    return;
  }
  _scheduleEdgePatch(projectId, context);
  _flushDeferredFullCanvasEdgeSyncIfReady(projectId, context);
}

function _addEdge(projectId: string, edge: CanvasEdgeSyncPayloadItem) {
  const context: CanvasHistoryRestoreContext = { projectId, epoch: _canvasHistoryEpoch };
  if (!_claimCanvasEdgeQueue(context)) return;
  for (let index = _pendingEdgeAdds.length - 1; index >= 0; index -= 1) {
    if (_pendingEdgeAdds[index].id === edge.id) _pendingEdgeAdds.splice(index, 1);
  }
  for (let index = _pendingEdgeRemoves.length - 1; index >= 0; index -= 1) {
    if (_pendingEdgeRemoves[index].edgeId === edge.id) _pendingEdgeRemoves.splice(index, 1);
  }
  _deferredEdgeAdds.delete(edge.id);
  _pendingEdgeAdds.push({ ...edge, mutationSeq: ++_canvasEdgeMutationSeq });
  _scheduleEdgePatch(projectId, context);
}

function _removeEdgeById(projectId: string, edgeId: string) {
  const context: CanvasHistoryRestoreContext = { projectId, epoch: _canvasHistoryEpoch };
  if (!_claimCanvasEdgeQueue(context)) return;
  for (let index = _pendingEdgeAdds.length - 1; index >= 0; index -= 1) {
    if (_pendingEdgeAdds[index].id === edgeId) _pendingEdgeAdds.splice(index, 1);
  }
  _deferredEdgeAdds.delete(edgeId);
  _pendingEdgeRemoves.push({ edgeId, mutationSeq: ++_canvasEdgeMutationSeq });
  _scheduleEdgePatch(projectId, context);
}

function _flushQueuedEdgePatch(
  projectId: string,
  context: CanvasHistoryRestoreContext,
  options?: { keepalive?: boolean },
): Promise<boolean> {
  return _sequenceCanvasEdgeWrite(projectId, () => (
    _flushQueuedEdgePatchNow(projectId, context, options)
  ));
}

async function _flushQueuedEdgePatchNow(
  projectId: string,
  context: CanvasHistoryRestoreContext,
  options?: { keepalive?: boolean },
): Promise<boolean> {
  const getStore = () => useVisualEditorStore.getState();
  if (
    !isCanvasHistoryRestoreContextCurrent(getStore, context)
    || !isCanvasEdgeQueueOwnedBy(context)
    || !_isCanvasEdgeHydrationActive(context)
  ) {
    return true;
  }

  const adds = _pendingEdgeAdds.splice(0);
  const removes = _pendingEdgeRemoves.splice(0);

  const pendingAddMap = new Map<string, PendingCanvasEdgeAdd>();
  _deferredEdgeAdds.forEach((edge, edgeId) => {
    pendingAddMap.set(edgeId, edge);
  });
  adds.forEach((edge) => {
    if (edge?.id) {
      const existing = pendingAddMap.get(edge.id);
      if (!existing || edge.mutationSeq > existing.mutationSeq) {
        pendingAddMap.set(edge.id, edge);
      }
    }
  });
  const pendingRemoveMap = new Map<string, PendingCanvasEdgeRemove>();
  removes.forEach((remove) => {
    const existing = pendingRemoveMap.get(remove.edgeId);
    if (!existing || remove.mutationSeq > existing.mutationSeq) {
      pendingRemoveMap.set(remove.edgeId, remove);
    }
  });
  pendingRemoveMap.forEach((remove, edgeId) => {
    const pendingAdd = pendingAddMap.get(edgeId);
    if (pendingAdd && pendingAdd.mutationSeq > remove.mutationSeq) {
      pendingRemoveMap.delete(edgeId);
      return;
    }
    pendingAddMap.delete(edgeId);
    _deferredEdgeAdds.delete(edgeId);
  });
  const effectiveRemoves = [...pendingRemoveMap.values()];

  const latestNodes = useVisualEditorStore.getState().nodes;
  const nodeById = new Map(latestNodes.map((node) => [node.id, node] as const));
  const persistableAdds: PendingCanvasEdgeAdd[] = [];
  const nextDeferredAdds = new Map<string, PendingCanvasEdgeAdd>();

  pendingAddMap.forEach((edge, edgeId) => {
    const sourceNode = nodeById.get(edge.source_node_id);
    const targetNode = nodeById.get(edge.target_node_id);

    if (!sourceNode || !targetNode) {
      return;
    }

    if (isPersistedCanvasNode(sourceNode) && isPersistedCanvasNode(targetNode)) {
      persistableAdds.push(edge);
      return;
    }

    nextDeferredAdds.set(edgeId, edge);
  });

  _deferredEdgeAdds.clear();
  nextDeferredAdds.forEach((edge, edgeId) => {
    _deferredEdgeAdds.set(edgeId, edge);
  });

  if (persistableAdds.length === 0 && effectiveRemoves.length === 0) {
    if (
      !isCanvasHistoryRestoreContextCurrent(getStore, context)
      || !isCanvasEdgeQueueOwnedBy(context)
    ) {
      return true;
    }
    _flushDeferredFullCanvasEdgeSyncIfReady(projectId, context);
    return true;
  }

  try {
    const { patchCanvasEdges } = await import('@/lib/api/canvas-nodes');
    if (
      !isCanvasHistoryRestoreContextCurrent(getStore, context)
      || !isCanvasEdgeQueueOwnedBy(context)
    ) {
      _suspendCanvasEdgeMutations(context, persistableAdds, effectiveRemoves);
      return true;
    }
    await patchCanvasEdges(projectId, {
      add: persistableAdds.length > 0
        ? persistableAdds.map(_pendingCanvasEdgeAddToPayload)
        : undefined,
      remove: effectiveRemoves.length > 0 ? effectiveRemoves.map((remove) => remove.edgeId) : undefined,
    }, options?.keepalive ? { keepalive: true } : undefined);
    if (
      !isCanvasHistoryRestoreContextCurrent(getStore, context)
      || !isCanvasEdgeQueueOwnedBy(context)
    ) {
      return true;
    }
    _clearScheduledEdgePatchRetry();
    _flushDeferredFullCanvasEdgeSyncIfReady(projectId, context);
  } catch (error) {
    if (
      !isCanvasHistoryRestoreContextCurrent(getStore, context)
      || !isCanvasEdgeQueueOwnedBy(context)
    ) {
      _suspendCanvasEdgeMutations(context, persistableAdds, effectiveRemoves);
      return true;
    }
    persistableAdds.forEach((edge) => {
      _deferredEdgeAdds.set(edge.id, edge);
    });
    effectiveRemoves.forEach((remove) => {
      _pendingEdgeRemoves.push(remove);
    });
    _scheduleEdgePatchRetry(projectId, context);
    console.error('[VisualEditorStore] 增量连线保存失败:', error);
  }

  return true;
}

function _scheduleEdgePatch(projectId: string, context?: CanvasHistoryRestoreContext) {
  const scheduledContext = context ?? {
    projectId,
    epoch: _canvasHistoryEpoch,
  };
  if (!_claimCanvasEdgeQueue(scheduledContext)) {
    return;
  }
  if (!_isCanvasEdgeHydrationActive(scheduledContext)) {
    return;
  }
  if (_edgePatchTimer) clearTimeout(_edgePatchTimer);
  _edgePatchTimer = setTimeout(async () => {
    _edgePatchTimer = null;
    const getStore = () => useVisualEditorStore.getState();
    if (
      !isCanvasHistoryRestoreContextCurrent(getStore, scheduledContext)
      || !isCanvasEdgeQueueOwnedBy(scheduledContext)
    ) {
      return;
    }
    const flushed = await _flushQueuedEdgePatch(projectId, scheduledContext);
    if (
      !isCanvasHistoryRestoreContextCurrent(getStore, scheduledContext)
      || !isCanvasEdgeQueueOwnedBy(scheduledContext)
    ) {
      return;
    }
    if (!flushed) {
      _scheduleEdgePatch(projectId, scheduledContext);
    }
  }, 300);
}

// ★ 全量同步（仅用于 removeNode 级联删除后的兜底）
let _edgeSaveTimer: ReturnType<typeof setTimeout> | null = null;

function _clearScheduledFullCanvasEdgeSyncThrough(
  context: CanvasHistoryRestoreContext,
  mutationCutoff: number,
  fullIntentSeq: number,
) {
  if (
    !areCanvasProjectEpochContextsEqual(_scheduledFullEdgeSyncContext, context)
    || _scheduledFullEdgeSyncMutationCutoff === null
    || _scheduledFullEdgeSyncMutationCutoff > mutationCutoff
    || _scheduledFullEdgeSyncIntentId === null
    || _scheduledFullEdgeSyncIntentId > fullIntentSeq
  ) {
    return;
  }
  if (_edgeSaveTimer) clearTimeout(_edgeSaveTimer);
  _edgeSaveTimer = null;
  _scheduledFullEdgeSyncContext = null;
  _scheduledFullEdgeSyncSignature = null;
  _scheduledFullEdgeSyncPayload = null;
  _scheduledFullEdgeSyncMutationCutoff = null;
  _scheduledFullEdgeSyncIntentId = null;
}

function _takeScheduledFullCanvasEdgeSync(intentId: number): {
  context: CanvasHistoryRestoreContext;
  payload: CanvasEdgeSyncPayloadItem[];
  mutationCutoff: number;
  fullIntentSeq: number;
} | null {
  if (
    _scheduledFullEdgeSyncIntentId !== intentId
    || !_scheduledFullEdgeSyncContext
    || !_scheduledFullEdgeSyncPayload
    || _scheduledFullEdgeSyncMutationCutoff === null
  ) {
    return null;
  }
  const intent = {
    context: _scheduledFullEdgeSyncContext,
    payload: _scheduledFullEdgeSyncPayload,
    mutationCutoff: _scheduledFullEdgeSyncMutationCutoff,
    fullIntentSeq: intentId,
  };
  _edgeSaveTimer = null;
  _scheduledFullEdgeSyncContext = null;
  _scheduledFullEdgeSyncSignature = null;
  _scheduledFullEdgeSyncPayload = null;
  _scheduledFullEdgeSyncMutationCutoff = null;
  _scheduledFullEdgeSyncIntentId = null;
  return intent;
}

async function _runScheduledFullCanvasEdgeSyncIntent(
  projectId: string,
  intent: {
    context: CanvasHistoryRestoreContext;
    payload: CanvasEdgeSyncPayloadItem[];
    mutationCutoff: number;
    fullIntentSeq: number;
  },
  options?: { keepalive?: boolean },
) {
  const latestState = useVisualEditorStore.getState();
  const desiredEdges = intent.payload.map(_pendingCanvasEdgeAddToCanvasEdge);
  await _runFullCanvasEdgeSyncForState(projectId, latestState.nodes, desiredEdges, {
    ...options,
    context: intent.context,
    mutationCutoff: intent.mutationCutoff,
    desiredEdgeIds: desiredEdges.map((edge) => edge.id),
    fullIntentSeq: intent.fullIntentSeq,
  });
}

function _saveCanvasEdges(projectId: string, context?: CanvasHistoryRestoreContext) {
  const scheduledContext = context ?? {
    projectId,
    epoch: _canvasHistoryEpoch,
  };
  if (!_claimCanvasEdgeQueue(scheduledContext)) {
    return;
  }
  _scheduledFullEdgeSyncContext = scheduledContext;
  const scheduledState = useVisualEditorStore.getState();
  _scheduledFullEdgeSyncSignature = buildCanvasEdgesSignature(scheduledState.canvasEdges);
  _scheduledFullEdgeSyncPayload = scheduledState.canvasEdges.map(buildCanvasEdgeSyncPayloadItem);
  _scheduledFullEdgeSyncMutationCutoff = _canvasEdgeMutationSeq;
  const intentId = ++_canvasEdgeFullSyncIntentSeq;
  _scheduledFullEdgeSyncIntentId = intentId;
  if (!_isCanvasEdgeHydrationActive(scheduledContext)) {
    return;
  }
  if (_edgeSaveTimer) clearTimeout(_edgeSaveTimer);
  _edgeSaveTimer = setTimeout(async () => {
    const intent = _takeScheduledFullCanvasEdgeSync(intentId);
    if (!intent) return;
    const getStore = () => useVisualEditorStore.getState();
    if (!isCanvasHistoryRestoreContextCurrent(getStore, intent.context)) {
      return;
    }
    try {
      await _runScheduledFullCanvasEdgeSyncIntent(projectId, intent);
      if (!isCanvasHistoryRestoreContextCurrent(getStore, intent.context)) {
        return;
      }
    } catch (error) {
      if (!isCanvasHistoryRestoreContextCurrent(getStore, intent.context)) {
        return;
      }
      console.error('[VisualEditorStore] 连线全量同步失败:', error);
    }
  }, 300);
}

async function _flushScheduledFullCanvasEdgeSync(projectId: string, options?: { keepalive?: boolean }) {
  const intentId = _scheduledFullEdgeSyncIntentId;
  if (intentId === null) {
    return;
  }

  if (_edgeSaveTimer) clearTimeout(_edgeSaveTimer);
  const intent = _takeScheduledFullCanvasEdgeSync(intentId);
  if (!intent) return;
  await _runScheduledFullCanvasEdgeSyncIntent(projectId, intent, options);
}

async function _flushPendingFullCanvasEdgeSyncRetry(
  projectId: string,
  options?: { keepalive?: boolean },
) {
  const context = _pendingFullEdgeSyncContext;
  const payload = _pendingFullEdgeSyncPayload;
  const mutationCutoff = _pendingFullEdgeSyncMutationCutoff;
  const desiredEdgeIds = _pendingFullEdgeSyncDesiredEdgeIds;
  const fullIntentSeq = _pendingFullEdgeSyncIntentSeq;
  if (
    _pendingFullEdgeSyncProjectId !== projectId
    || !context
    || !payload
    || mutationCutoff === null
    || !desiredEdgeIds
    || fullIntentSeq === null
  ) {
    return;
  }
  _clearPendingFullEdgeSyncRetryThrough(context, mutationCutoff, fullIntentSeq);
  await _runFullCanvasEdgeSync(
    projectId,
    payload,
    context,
    mutationCutoff,
    desiredEdgeIds,
    fullIntentSeq,
    options,
  );
}

function _normalizeSuspendedCanvasEdgeMutations(
  queue: SuspendedCanvasEdgeQueue,
  afterMutationSeq: number,
  includeDeferredAdds = true,
) {
  const addById = new Map<string, PendingCanvasEdgeAdd>();
  [
    ...queue.pendingAdds,
    ...(includeDeferredAdds ? queue.deferredAdds.map(([, edge]) => edge) : []),
  ].forEach((edge) => {
    if (edge.mutationSeq <= afterMutationSeq) return;
    const existing = addById.get(edge.id);
    if (!existing || existing.mutationSeq < edge.mutationSeq) addById.set(edge.id, edge);
  });
  const removeById = new Map<string, PendingCanvasEdgeRemove>();
  queue.pendingRemoves.forEach((remove) => {
    if (remove.mutationSeq <= afterMutationSeq) return;
    const existing = removeById.get(remove.edgeId);
    if (!existing || existing.mutationSeq < remove.mutationSeq) removeById.set(remove.edgeId, remove);
  });
  const adds = [...addById.values()].filter((edge) => (
    edge.mutationSeq > (removeById.get(edge.id)?.mutationSeq ?? -1)
  ));
  const removes = [...removeById.values()].filter((remove) => (
    remove.mutationSeq > (addById.get(remove.edgeId)?.mutationSeq ?? -1)
  ));
  return { adds, removes };
}

async function _flushSuspendedCanvasEdgeProject(
  projectId: string,
  options?: { keepalive?: boolean },
  retainQueueAfterSuccess = false,
) {
  await _sequenceCanvasEdgeWrite(projectId, async () => {
    const queue = _suspendedCanvasEdgeQueues.get(projectId);
    if (!queue) return;

    const newestFullSync = [
      queue.deferredFullSyncPayload && queue.deferredFullSyncIntentSeq !== null
        ? {
          payload: queue.deferredFullSyncPersistablePayload || [],
          mutationCutoff: queue.deferredFullSyncMutationCutoff ?? -1,
          intentSeq: queue.deferredFullSyncIntentSeq,
          deferred: true,
        }
        : null,
      queue.scheduledFullSyncPayload && queue.scheduledFullSyncIntentSeq !== null
        ? {
          payload: queue.scheduledFullSyncPayload,
          mutationCutoff: queue.scheduledFullSyncMutationCutoff ?? -1,
          intentSeq: queue.scheduledFullSyncIntentSeq,
          deferred: false,
        }
        : null,
      queue.fullSyncRetryPayload && queue.fullSyncIntentSeq !== null
        ? {
          payload: queue.fullSyncRetryPayload,
          mutationCutoff: queue.fullSyncMutationCutoff ?? -1,
          intentSeq: queue.fullSyncIntentSeq,
          deferred: false,
        }
        : null,
    ].filter((candidate): candidate is NonNullable<typeof candidate> => Boolean(candidate))
      .sort((left, right) => right.intentSeq - left.intentSeq)[0] ?? null;
    const fullSyncPayload = newestFullSync?.payload || null;
    const fullSyncIntentSeq = newestFullSync?.intentSeq ?? -1;
    const fullSyncCutoff = newestFullSync?.mutationCutoff ?? -1;
    const { adds, removes } = _normalizeSuspendedCanvasEdgeMutations(
      queue,
      fullSyncPayload ? fullSyncCutoff : -1,
      !newestFullSync?.deferred,
    );
    const mutationCutoff = Math.max(
      fullSyncCutoff,
      ...adds.map((edge) => edge.mutationSeq),
      ...removes.map((remove) => remove.mutationSeq),
    );
    const { syncCanvasEdges, patchCanvasEdges } = await import('@/lib/api/canvas-nodes');

    if (fullSyncPayload) {
      const desiredById = new Map(fullSyncPayload.map((edge) => [edge.id, edge] as const));
      adds.forEach((edge) => desiredById.set(edge.id, _pendingCanvasEdgeAddToPayload(edge)));
      removes.forEach((remove) => desiredById.delete(remove.edgeId));
      await syncCanvasEdges(
        projectId,
        [...desiredById.values()],
        options?.keepalive ? { keepalive: true } : undefined,
      );
      if (!retainQueueAfterSuccess) {
        _consumeQueuedEdgeMutationsCoveredByFullSync(
          projectId,
          { projectId, epoch: Number.MIN_SAFE_INTEGER },
          mutationCutoff,
          fullSyncIntentSeq,
        );
      }
      return;
    }

    if (adds.length === 0 && removes.length === 0) return;
    await patchCanvasEdges(projectId, {
      add: adds.length > 0 ? adds.map(_pendingCanvasEdgeAddToPayload) : undefined,
      remove: removes.length > 0 ? removes.map((remove) => remove.edgeId) : undefined,
    }, options?.keepalive ? { keepalive: true } : undefined);
    if (!retainQueueAfterSuccess) {
      _consumeQueuedEdgeMutationsCoveredByFullSync(
        projectId,
        { projectId, epoch: Number.MIN_SAFE_INTEGER },
        mutationCutoff,
      );
    }
  });
}

function _snapshotInactiveCanvasEdgeQueueForKeepalive(context: CanvasHistoryRestoreContext) {
  if (!context.projectId || !isCanvasEdgeQueueOwnedBy(context)) return;
  const existing = _suspendedCanvasEdgeQueues.get(context.projectId);
  const currentState = useVisualEditorStore.getState();
  const newestScheduled = [
    _scheduledFullEdgeSyncPayload
      && _scheduledFullEdgeSyncIntentId !== null
      && areCanvasProjectEpochContextsEqual(_scheduledFullEdgeSyncContext, context)
      ? {
        payload: _scheduledFullEdgeSyncPayload,
        mutationCutoff: _scheduledFullEdgeSyncMutationCutoff ?? -1,
        intentSeq: _scheduledFullEdgeSyncIntentId,
      }
      : null,
    existing?.scheduledFullSyncPayload && existing.scheduledFullSyncIntentSeq !== null
      ? {
        payload: existing.scheduledFullSyncPayload,
        mutationCutoff: existing.scheduledFullSyncMutationCutoff ?? -1,
        intentSeq: existing.scheduledFullSyncIntentSeq,
      }
      : null,
  ].filter((candidate): candidate is NonNullable<typeof candidate> => Boolean(candidate))
    .sort((left, right) => right.intentSeq - left.intentSeq)[0] ?? null;
  const scheduledDesiredEdges = newestScheduled?.payload.map(_pendingCanvasEdgeAddToCanvasEdge) || [];
  const scheduledPlan = newestScheduled
    ? buildCanvasEdgeFullSyncPlan(currentState.nodes, scheduledDesiredEdges)
    : null;
  const newestDeferred = [
    _deferredFullEdgeSyncPayload
      && _deferredFullEdgeSyncIntentSeq !== null
      && _deferredFullEdgeSyncProjectId === context.projectId
      ? {
        signature: _deferredFullEdgeSyncSignature,
        payload: _deferredFullEdgeSyncPayload,
        persistablePayload: _deferredFullEdgeSyncPersistablePayload || [],
        mutationCutoff: _deferredFullEdgeSyncMutationCutoff ?? -1,
        intentSeq: _deferredFullEdgeSyncIntentSeq,
        deferredAdds: [] as CanvasEdgeSyncPayloadItem[],
      }
      : null,
    existing?.deferredFullSyncPayload && existing.deferredFullSyncIntentSeq !== null
      ? {
        signature: existing.deferredFullSyncSignature,
        payload: existing.deferredFullSyncPayload,
        persistablePayload: existing.deferredFullSyncPersistablePayload || [],
        mutationCutoff: existing.deferredFullSyncMutationCutoff ?? -1,
        intentSeq: existing.deferredFullSyncIntentSeq,
        deferredAdds: [] as CanvasEdgeSyncPayloadItem[],
      }
      : null,
    newestScheduled && scheduledPlan
      ? {
        signature: _buildEdgePayloadSignature(newestScheduled.payload),
        payload: newestScheduled.payload,
        persistablePayload: scheduledPlan.edges,
        mutationCutoff: newestScheduled.mutationCutoff,
        intentSeq: newestScheduled.intentSeq,
        deferredAdds: scheduledPlan.deferredAdds,
      }
      : null,
  ].filter((candidate): candidate is NonNullable<typeof candidate> => Boolean(candidate))
    .sort((left, right) => right.intentSeq - left.intentSeq)[0] ?? null;
  const snapshotDeferredAdds = (newestDeferred?.deferredAdds || []).map((edge) => (
    [edge.id, { ...edge, mutationSeq: ++_canvasEdgeMutationSeq }] as [string, PendingCanvasEdgeAdd]
  ));
  const newestRetry = [
    _pendingFullEdgeSyncPayload
      && _pendingFullEdgeSyncIntentSeq !== null
      && _pendingFullEdgeSyncProjectId === context.projectId
      ? {
        payload: _pendingFullEdgeSyncPayload,
        mutationCutoff: _pendingFullEdgeSyncMutationCutoff,
        intentSeq: _pendingFullEdgeSyncIntentSeq,
        desiredEdgeIds: _pendingFullEdgeSyncDesiredEdgeIds,
      }
      : null,
    existing?.fullSyncRetryPayload && existing.fullSyncIntentSeq !== null
      ? {
        payload: existing.fullSyncRetryPayload,
        mutationCutoff: existing.fullSyncMutationCutoff,
        intentSeq: existing.fullSyncIntentSeq,
        desiredEdgeIds: existing.fullSyncDesiredEdgeIds,
      }
      : null,
  ].filter((candidate): candidate is NonNullable<typeof candidate> => Boolean(candidate))
    .sort((left, right) => right.intentSeq - left.intentSeq)[0] ?? null;
  _storeSuspendedCanvasEdgeQueue(context.projectId, {
    pendingAdds: [...(existing?.pendingAdds || []), ..._pendingEdgeAdds],
    pendingRemoves: [...(existing?.pendingRemoves || []), ..._pendingEdgeRemoves],
    deferredAdds: [...(existing?.deferredAdds || []), ..._deferredEdgeAdds.entries(), ...snapshotDeferredAdds],
    deferredFullSyncSignature: newestDeferred?.signature || null,
    deferredFullSyncPayload: newestDeferred?.payload || null,
    deferredFullSyncPersistablePayload: newestDeferred?.persistablePayload || null,
    deferredFullSyncMutationCutoff: newestDeferred?.mutationCutoff ?? null,
    deferredFullSyncIntentSeq: newestDeferred?.intentSeq ?? null,
    scheduledFullSyncSignature: null,
    scheduledFullSyncPayload: null,
    scheduledFullSyncMutationCutoff: null,
    scheduledFullSyncIntentSeq: null,
    fullSyncRetryPayload: newestRetry?.payload || null,
    fullSyncMutationCutoff: newestRetry?.mutationCutoff ?? null,
    fullSyncIntentSeq: newestRetry?.intentSeq ?? null,
    fullSyncDesiredEdgeIds: newestRetry?.desiredEdgeIds || null,
    hadPatchRetry: true,
  });
}

async function _flushCurrentCanvasEdgeProject(
  projectId: string,
  options?: { keepalive?: boolean },
) {
  const flushContext: CanvasHistoryRestoreContext = {
    projectId,
    epoch: _canvasHistoryEpoch,
  };

  if (!_isCanvasEdgeHydrationActive(flushContext)) {
    _snapshotInactiveCanvasEdgeQueueForKeepalive(flushContext);
    await _flushSuspendedCanvasEdgeProject(projectId, options, true);
    return;
  }

  if (
    areCanvasProjectEpochContextsEqual(_scheduledFullEdgeSyncContext, flushContext)
    || areCanvasProjectEpochContextsEqual(_pendingFullEdgeSyncContext, flushContext)
  ) {
    // Unload is a new desired-state intent. It must be queued after earlier full
    // writes and reflect the current graph, never replay an older retry at the tail.
    _saveCanvasEdges(projectId, flushContext);
  }

  await _flushScheduledFullCanvasEdgeSync(projectId, options);
  if (isCanvasHistoryRestoreContextCurrent(() => useVisualEditorStore.getState(), flushContext)) {
    await _flushPendingFullCanvasEdgeSyncRetry(projectId, options);
  }
  if (
    isCanvasHistoryRestoreContextCurrent(() => useVisualEditorStore.getState(), flushContext)
    && _deferredEdgeAdds.size > 0
  ) {
    await _persistDeferredEdgeEndpointNodes(projectId, options);
  }
  if (!isCanvasHistoryRestoreContextCurrent(() => useVisualEditorStore.getState(), flushContext)) {
    return;
  }
  if (_edgePatchTimer) {
    clearTimeout(_edgePatchTimer);
    _edgePatchTimer = null;
  }
  const flushed = await _flushQueuedEdgePatch(projectId, flushContext, options);
  if (!isCanvasHistoryRestoreContextCurrent(() => useVisualEditorStore.getState(), flushContext)) {
    return;
  }
  if (!flushed && !options?.keepalive) _scheduleEdgePatch(projectId, flushContext);
  if (_deferredEdgeAdds.size > 0) {
    throw new Error(`[VisualEditorStore] Canvas edge flush incomplete: ${_deferredEdgeAdds.size} edge(s) are waiting for node persistence`);
  }
}

function applyCanvasEdgesState(
  get: () => VisualEditorStore,
  set: VisualEditorSetState,
  edges: CanvasEdge[],
): {
  changed: boolean;
  normalizedEdges: CanvasEdge[];
  nextNodes: CanvasNode[];
  projectId: string | null | undefined;
} {
  const { nodes, projectId, canvasEdges } = get();
  const { edges: normalizedEdges, removedEdgeIds } = filterCanvasEdgesWithExistingNodes(nodes, edges);
  const reconciliation = reconcileTemplateWorkflowBindings(nodes, normalizedEdges);

  if (areCanvasEdgesEqual(canvasEdges, normalizedEdges) && reconciliation.changedNodeIds.length === 0) {
    return {
      changed: false,
      normalizedEdges,
      nextNodes: nodes,
      projectId,
    };
  }

  set({
    canvasEdges: normalizedEdges,
    ...(reconciliation.changedNodeIds.length > 0 ? { nodes: reconciliation.nodes } : {}),
  });

  if (reconciliation.changedNodeIds.length > 0) {
    console.warn('[VisualEditorStore] 清理孤立模板工作流绑定', {
      changedNodeIds: reconciliation.changedNodeIds,
      removedBindingCount: reconciliation.removedBindingCount,
    });
    persistTemplateWorkflowBindingCleanup(projectId, reconciliation.nodes, reconciliation.changedNodeIds);
  }

  if (removedEdgeIds.length > 0) {
    console.warn('[VisualEditorStore] 丢弃引用不存在节点的画布连线', {
      removedEdgeIds,
    });
  }

  return {
    changed: true,
    normalizedEdges,
    nextNodes: reconciliation.nodes,
    projectId,
  };
}

function _rememberDeletedNodeSnapshot(snapshot: DeletedNodeSnapshot) {
  _lastDeletedNodeSnapshot = snapshot;
}

function _clearDeletedNodeSnapshot() {
  _lastDeletedNodeSnapshot = null;
}

const _canvasDeleteIntentAttempts = new Map<string, Promise<void>>();

function _isDefiniteCanvasDeleteIntentRejection(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const candidate = error as {
    retryable?: unknown;
    details?: { httpStatus?: unknown };
  };
  const status = candidate.details?.httpStatus;
  return candidate.retryable === false
    && typeof status === 'number'
    && status >= 400
    && status < 500
    && ![408, 409, 425, 429].includes(status);
}

function _waitForCanvasDeleteIntentRetry(attempt: number): Promise<void> {
  const delayMs = attempt <= 1 ? 0 : Math.min(5_000, 100 * (2 ** Math.min(attempt - 2, 6)));
  if (delayMs === 0) return Promise.resolve();
  return new Promise((resolve) => setTimeout(resolve, delayMs));
}

async function _confirmCanvasNodeDeleteIntent(
  projectId: string,
  nodeIds: string[],
  certainty: { everAmbiguousOrCommitted: boolean },
) {
  const normalizedIds = [...new Set(nodeIds)].sort();
  const flightKey = `${projectId}:${normalizedIds.join(',')}`;
  let attempt = 0;
  while (true) {
    attempt += 1;
    let request = _canvasDeleteIntentAttempts.get(flightKey);
    if (!request) {
      request = (async () => {
        const { recordCanvasNodeDeleteIntents } = await import('@/lib/api/canvas-nodes');
        await recordCanvasNodeDeleteIntents(projectId, normalizedIds);
      })();
      _canvasDeleteIntentAttempts.set(flightKey, request);
    }
    try {
      await request;
      certainty.everAmbiguousOrCommitted = true;
      return;
    } catch (error) {
      if (
        _isDefiniteCanvasDeleteIntentRejection(error)
        && !certainty.everAmbiguousOrCommitted
      ) {
        throw error;
      }
      // A network/timeout/5xx/parse failure may have committed server-side. Once
      // any round is ambiguous or confirmed, later 4xx responses cannot disprove
      // that tombstone and must not roll the local delete marker back.
      certainty.everAmbiguousOrCommitted = true;
      await _waitForCanvasDeleteIntentRetry(attempt);
    } finally {
      if (_canvasDeleteIntentAttempts.get(flightKey) === request) {
        _canvasDeleteIntentAttempts.delete(flightKey);
      }
    }
  }
}

async function _deleteNodesInternal(
  get: () => VisualEditorStore,
  set: VisualEditorSetState,
  nodeIds: string[],
  options?: { rememberForUndo?: boolean; recordDeleteIntent?: boolean; historyLabel?: string },
) {
  if (nodeIds.length === 0) return false;

  const initialState = get();
  let deleteContext: CanvasHistoryRestoreContext = {
    projectId: initialState.projectId,
    epoch: _canvasHistoryEpoch,
  };
  const projectId = initialState.projectId;
  const dedupedNodeIds = [...new Set(nodeIds)].filter(Boolean);
  const resolveDeletePlan = (state: VisualEditorStore, forcedDeleteIds: ReadonlySet<string>) => {
    const { nodes, canvasEdges } = state;
    const deleteSet = new Set([
      ...expandCanvasGroupDeletionNodeIds(nodes, dedupedNodeIds),
      ...forcedDeleteIds,
    ]);
    nodes.forEach((node) => {
      const sourceNodeId = node.gridPanelEditData?.sourceNodeId;
      if (typeof sourceNodeId === 'string' && deleteSet.has(sourceNodeId)) {
        deleteSet.add(node.id);
      }
    });
    const nodesToDelete = nodes.filter((node) => deleteSet.has(node.id));
    const normalizedDeleteSet = new Set(nodesToDelete.map((node) => node.id));
    const filteredNodes = nodes.filter((node) => !normalizedDeleteSet.has(node.id));
    const groupNormalization = normalizeCanvasGroupTopology(filteredNodes);
    const finalDeleteSet = new Set([...normalizedDeleteSet, ...groupNormalization.removedGroupIds]);
    const filteredEdges = canvasEdges.filter((edge) => !finalDeleteSet.has(edge.source) && !finalDeleteSet.has(edge.target));
    return {
      state,
      nodes,
      canvasEdges,
      nodesToDelete,
      normalizedDeleteSet,
      groupNormalization,
      finalDeleteSet,
      existingNodeById: new Map(nodes.map((node) => [node.id, node] as const)),
      deletedGroupIds: nodesToDelete
        .filter((node) => node.groupChildIds?.length)
        .map((node) => node.id),
      filteredEdges,
      reconciliation: reconcileTemplateWorkflowBindings(groupNormalization.nodes, filteredEdges),
    };
  };

  const fencedDeleteIds = new Set<string>();
  let deletePlan = resolveDeletePlan(initialState, fencedDeleteIds);
  if (deletePlan.nodesToDelete.length === 0) return false;
  let deleteIntentCommitted = false;
  if (options?.recordDeleteIntent) {
    if (!projectId || !isCanvasHistoryRestoreContextCurrent(get, deleteContext)) {
      return false;
    }
    const intentCertainty = { everAmbiguousOrCommitted: false };
    while (true) {
      const beforeIntentState = get();
      if (beforeIntentState.projectId !== projectId) {
        return intentCertainty.everAmbiguousOrCommitted;
      }
      if (!isCanvasHistoryRestoreContextCurrent(get, deleteContext)) {
        deleteContext = { projectId, epoch: _canvasHistoryEpoch };
        deletePlan = resolveDeletePlan(beforeIntentState, fencedDeleteIds);
      }
      const intentRoundNodeIds = Array.from(deletePlan.finalDeleteSet);
      await _confirmCanvasNodeDeleteIntent(
        projectId,
        intentRoundNodeIds,
        intentCertainty,
      );
      _recordConfirmedCanvasDeleteTombstones(projectId, intentRoundNodeIds);
      deletePlan.finalDeleteSet.forEach((nodeId) => fencedDeleteIds.add(nodeId));
      const afterIntentState = get();
      if (afterIntentState.projectId !== projectId) {
        // The tombstone is durable for the captured project. Never reconcile it
        // into whichever project is now mounted.
        return true;
      }
      deleteContext = { projectId, epoch: _canvasHistoryEpoch };
      // The intent request is the first async boundary. Recompute against the
      // latest same-project state so unrelated node/edge changes are preserved.
      // IDs already fenced remain forced into the local delete even if topology
      // changed while awaiting, because the backend tombstone is irreversible.
      deletePlan = resolveDeletePlan(afterIntentState, fencedDeleteIds);
      const hasUnfencedCascade = Array.from(deletePlan.finalDeleteSet)
        .some((nodeId) => !fencedDeleteIds.has(nodeId));
      if (!hasUnfencedCascade) {
        break;
      }
    }
    if (deletePlan.nodesToDelete.length === 0) {
      // Another continuation sharing the same idempotent fence may already have
      // reconciled these nodes. The delete is durable, but there is no second
      // local transaction/history/toast to create.
      _unmarkPersistedCanvasNodeIds(fencedDeleteIds);
      return true;
    }
    // The intent RPC owns the exact durable delete transaction, including rows
    // that arrive concurrently. Unmark the same fully-expanded set atomically so
    // this call cannot issue a second batch-delete for only part of the cascade.
    _unmarkPersistedCanvasNodeIds(fencedDeleteIds);
    deleteIntentCommitted = true;
  }

  const {
    state,
    nodes,
    canvasEdges,
    nodesToDelete,
    normalizedDeleteSet,
    groupNormalization,
    finalDeleteSet,
    existingNodeById,
    deletedGroupIds,
    filteredEdges,
    reconciliation,
  } = deletePlan;
  const generatingTaskIds = nodesToDelete
    .map((node) => node.generatingTaskId)
    .filter((taskId): taskId is string => Boolean(taskId));
  const statePatch: Partial<VisualEditorState> = {};
  if (state.selectedClipIdForAI && normalizedDeleteSet.has(state.selectedClipIdForAI)) {
    statePatch.selectedClipIdForAI = null;
  }
  if (state.templateInspectorNodeId && normalizedDeleteSet.has(state.templateInspectorNodeId)) {
    statePatch.activeSidebar = state.activeSidebar === 'templateInspector' ? null : state.activeSidebar;
    statePatch.templateInspectorNodeId = null;
  }
  if (state.smartEditNodeId && normalizedDeleteSet.has(state.smartEditNodeId)) {
    statePatch.activeSidebar = null;
    Object.assign(statePatch, _getSmartEditSessionResetState());
  }
  const staleLockedIds = state.lockedNodeIds.filter((id: string) => normalizedDeleteSet.has(id));
  if (staleLockedIds.length > 0) {
    statePatch.lockedNodeIds = state.lockedNodeIds.filter((id: string) => !normalizedDeleteSet.has(id));
  }

  if (options?.rememberForUndo) {
    _rememberDeletedNodeSnapshot({
      nodeIds: Array.from(normalizedDeleteSet),
      nodes: nodesToDelete.map((node) => ({ ...node })),
      selectedClipIdForAI: get().selectedClipIdForAI,
      templateInspectorNodeId: get().templateInspectorNodeId,
      smartEditNodeId: get().smartEditNodeId,
      lockedNodeIds: get().lockedNodeIds.filter((id) => normalizedDeleteSet.has(id)),
    });
  }

  const persistedDeleteIds = Array.from(finalDeleteSet).filter((nodeId) => isPersistedCanvasNode(existingNodeById.get(nodeId)));
  const pendingUpdatesForDeletedNodes = new Map<string, CanvasNodeBatchUpdate>();
  // Capture history only after the intent fence has settled and the delete plan
  // has been rebased onto the latest same-project state. `afterSnapshot` is taken
  // immediately after the local mutation so Undo can preserve later unrelated edits.
  const deleteHistoryBeforeSnapshot = options?.historyLabel
    ? captureCanvasHistorySnapshot(state)
    : null;

  finalDeleteSet.forEach((nodeId) => {
    _cancelPromptNodeSaveTimer(nodeId);
    const pendingUpdate = _pendingNodeUpdates.get(nodeId);
    if (pendingUpdate) {
      pendingUpdatesForDeletedNodes.set(nodeId, pendingUpdate);
      _pendingNodeUpdates.delete(nodeId);
    }
  });

  set({
    ...statePatch,
    nodes: reconciliation.nodes,
    canvasEdges: filteredEdges,
  });
  const deleteStateAfterLocalSet = get();
  const deleteHistoryAfterSnapshot = deleteHistoryBeforeSnapshot
    ? captureCanvasHistorySnapshot(get())
    : null;
  const deleteHistoryEntryId = options?.historyLabel && deleteHistoryBeforeSnapshot && deleteHistoryAfterSnapshot
    ? generateUuidLike()
    : null;
  if (deleteHistoryEntryId && options?.historyLabel && deleteHistoryBeforeSnapshot && deleteHistoryAfterSnapshot) {
    // Reserve temporal order immediately after the local transaction. Later
    // edits are pushed above this entry while the durable delete is pending.
    rememberCanvasHistoryEntry({
      id: deleteHistoryEntryId,
      label: options.historyLabel,
      snapshot: cloneCanvasHistoryValue(deleteHistoryBeforeSnapshot),
      afterSnapshot: cloneCanvasHistoryValue(deleteHistoryAfterSnapshot),
      pending: true,
    });
  }
  let deleteHistoryCommitted = false;
  const commitDeleteHistory = () => {
    if (
      deleteHistoryCommitted
      || !deleteHistoryEntryId
      || !isCanvasHistoryRestoreContextCurrent(get, deleteContext)
    ) {
      return;
    }
    const provisionalEntry = _canvasHistoryPast.find((entry) => entry.id === deleteHistoryEntryId);
    if (!provisionalEntry) return;
    deleteHistoryCommitted = true;
    provisionalEntry.pending = false;
    _clearDeletedNodeSnapshot();
  };
  const rollbackDeleteHistory = () => {
    if (!deleteHistoryEntryId) return;
    _canvasHistoryPast = _canvasHistoryPast.filter((entry) => entry.id !== deleteHistoryEntryId);
  };

  // The visible delete is complete once this local transaction is applied.
  // Network durability and derived-state cleanup continue in the background;
  // callers must not hold React Flow/toolbars in a delete-wide busy state.
  const completeDelete = async () => {
  if (projectId) {
    try {
      if (!deleteIntentCommitted && persistedDeleteIds.length > 0) {
        if (!isCanvasHistoryRestoreContextCurrent(get, deleteContext)) {
          return false;
        }
        const { batchDeleteCanvasNodes } = await import('@/lib/api/canvas-nodes');
        if (!isCanvasHistoryRestoreContextCurrent(get, deleteContext)) {
          return false;
        }
        await batchDeleteCanvasNodes(projectId, persistedDeleteIds);
        if (!isCanvasHistoryRestoreContextCurrent(get, deleteContext)) {
          // The backend deletion completed for the initiating project. A later
          // project must not receive its ledger or UI side effects.
          return true;
        }
        _unmarkPersistedCanvasNodeIds(persistedDeleteIds);
        console.log('[VisualEditorStore] 批量删除成功');
      }
    } catch (error) {
      if (!isCanvasHistoryRestoreContextCurrent(get, deleteContext)) {
        return false;
      }
      rollbackDeleteHistory();
      pendingUpdatesForDeletedNodes.forEach((pendingUpdate) => {
        _enqueueBatchUpdate(projectId, pendingUpdate);
      });
      const persistedDeleteIdSet = new Set(persistedDeleteIds);
      const locallyDeletedUnpersistedIds = new Set(
        Array.from(finalDeleteSet).filter((nodeId) => !persistedDeleteIdSet.has(nodeId)),
      );
      set((currentState) => {
        if (currentState.projectId !== deleteContext.projectId || _canvasHistoryEpoch !== deleteContext.epoch) {
          return {};
        }
        // Apply the inverse of this delete only. A whole-snapshot rollback would
        // overwrite unrelated edits made while DELETE was pending.
        const beforeNodeById = new Map(nodes.map((node) => [node.id, node] as const));
        const afterNodeById = new Map(reconciliation.nodes.map((node) => [node.id, node] as const));
        const persistedDeleteIdSet = new Set(persistedDeleteIds);
        const topologyFields = [
          'groupId',
          'groupChildIds',
          'groupCollapsed',
          'groupLabel',
          'thumbnail',
          'savedChildPositions',
        ] as const;
        const restoreChangedTopology = (currentNode: CanvasNode): CanvasNode => {
          const beforeNode = beforeNodeById.get(currentNode.id);
          const afterNode = afterNodeById.get(currentNode.id);
          if (!beforeNode || !afterNode) return currentNode;
          const nextNode = { ...currentNode } as CanvasNode & Record<string, unknown>;
          const mutableNextNode = nextNode as Record<string, unknown>;
          topologyFields.forEach((field) => {
            const beforeValue = (beforeNode as CanvasNode & Record<string, unknown>)[field];
            const afterValue = (afterNode as CanvasNode & Record<string, unknown>)[field];
            const currentValue = (currentNode as CanvasNode & Record<string, unknown>)[field];
            if (
              areCanvasHistoryValuesEqual(beforeValue, afterValue)
              || !areCanvasHistoryValuesEqual(currentValue, afterValue)
            ) {
              return;
            }
            if (beforeValue === undefined) {
              delete mutableNextNode[field];
            } else {
              mutableNextNode[field] = cloneCanvasHistoryValue(beforeValue);
            }
          });
          return nextNode;
        };

        const mergedRollbackNodes = currentState.nodes.map(restoreChangedTopology);
        const currentNodeIdSet = new Set(mergedRollbackNodes.map((node) => node.id));
        nodes.forEach((beforeNode, beforeIndex) => {
          if (
            !persistedDeleteIdSet.has(beforeNode.id)
            || currentNodeIdSet.has(beforeNode.id)
          ) {
            return;
          }
          // Unpersisted upload placeholders intentionally stay deleted: their
          // retry action/blob URL was disposed by the optimistic removal.
          const insertIndex = Math.min(beforeIndex, mergedRollbackNodes.length);
          mergedRollbackNodes.splice(insertIndex, 0, cloneCanvasHistoryValue(beforeNode));
          currentNodeIdSet.add(beforeNode.id);
        });
        const normalizedRollback = normalizeCanvasGroupTopology(mergedRollbackNodes);
        const mergedRollbackEdges = mergeCanvasHistoryEdgesForUndo(
          currentState.canvasEdges,
          canvasEdges,
          filteredEdges,
        ).filter((edge) => (
          !locallyDeletedUnpersistedIds.has(edge.source)
          && !locallyDeletedUnpersistedIds.has(edge.target)
        ));
        const { edges: validRollbackEdges } = filterCanvasEdgesWithExistingNodes(
          normalizedRollback.nodes,
          mergedRollbackEdges,
        );
        const rollbackReconciliation = reconcileTemplateWorkflowBindings(
          normalizedRollback.nodes,
          validRollbackEdges,
        );
        const rollbackUiPatch: Partial<VisualEditorState> = {};
        const mutableRollbackUiPatch = rollbackUiPatch as Record<string, unknown>;
        (Object.keys(statePatch) as Array<keyof VisualEditorState>).forEach((key) => {
          const currentValue = currentState[key];
          const afterValue = deleteStateAfterLocalSet[key];
          if (!areCanvasHistoryValuesEqual(currentValue, afterValue)) return;
          mutableRollbackUiPatch[key] = cloneCanvasHistoryValue(state[key]);
        });
        return {
          ...rollbackUiPatch,
          nodes: rollbackReconciliation.nodes,
          canvasEdges: validRollbackEdges,
        };
      });
      if (!isCanvasHistoryRestoreContextCurrent(get, deleteContext)) {
        return false;
      }
      console.error('[VisualEditorStore] 批量删除后端失败:', error);
      // All single-node entry points delegate here. Do not let optimistic
      // rollback look like a successful deletion with no explanation.
      toast.showError(error, 'canvas', { dedupeKey: 'canvas-node-delete-failed' });
      return false;
    }
  }

  if (generatingTaskIds.length > 0) {
    if (!isCanvasHistoryRestoreContextCurrent(get, deleteContext)) {
      return true;
    }
    try {
      const { useChatStore } = await import('@/features/chat/store/chatStore');
      if (!isCanvasHistoryRestoreContextCurrent(get, deleteContext)) {
        return true;
      }
      const chatStore = useChatStore.getState();
      for (const taskId of generatingTaskIds) {
        chatStore.updateTaskResult(taskId, { status: 'failed', error: 'Node has been deleted' });
      }
    } catch (error) {
      if (!isCanvasHistoryRestoreContextCurrent(get, deleteContext)) {
        return true;
      }
      console.warn('[VisualEditorStore] 删除后生成任务清理失败:', error);
    }
  }

  {
    if (!isCanvasHistoryRestoreContextCurrent(get, deleteContext)) {
      return true;
    }
    try {
      const { useChatStore } = await import('@/features/chat/store/chatStore');
      if (!isCanvasHistoryRestoreContextCurrent(get, deleteContext)) {
        return true;
      }
      const chatState = useChatStore.getState();
      for (const nodeId of normalizedDeleteSet) {
        if (chatState.attachedNodes.some((n: { nodeId: string }) => n.nodeId === nodeId)) {
          chatState.detachNode(nodeId);
        }
      }
    } catch (error) {
      if (!isCanvasHistoryRestoreContextCurrent(get, deleteContext)) {
        return true;
      }
      console.warn('[VisualEditorStore] 删除后 Chat 附件清理失败:', error);
    }
  }

  if (!isCanvasHistoryRestoreContextCurrent(get, deleteContext)) {
    return true;
  }
  if (projectId) {
    [...new Set([...deletedGroupIds, ...groupNormalization.removedGroupIds])].forEach((groupId) => {
      removeGroup(projectId, groupId);
    });
    groupNormalization.groupsToPersist.forEach((groupState) => {
      saveGroup(projectId, groupState.groupId, {
        childIds: groupState.childIds,
        collapsed: groupState.collapsed,
        label: groupState.label,
        position: groupState.position,
        thumbnail: groupState.thumbnail,
      });
    });
  }

  if (!projectId) {
    return true;
  }

  // The backend delete already tombstones incident edges. Keep the existing
  // edge queue reconciliation as a background safety pass, never on the
  // caller-facing delete promise.
  if (isCanvasHistoryRestoreContextCurrent(get, deleteContext)) {
    _saveCanvasEdges(projectId, deleteContext);
  }

  if (!isCanvasHistoryRestoreContextCurrent(get, deleteContext)) {
    return true;
  }
  if (reconciliation.changedNodeIds.length > 0) {
    persistTemplateWorkflowBindingCleanup(projectId, reconciliation.nodes, reconciliation.changedNodeIds);
  }

  const reconciledNodeById = new Map(reconciliation.nodes.map((node) => [node.id, node] as const));
  const persistedGroupsToPersist = groupNormalization.groupsToPersist.filter((groupState) => (
    isPersistedCanvasNode(reconciledNodeById.get(groupState.groupId))
  ));

  if (persistedGroupsToPersist.length > 0) {
    try {
      if (!isCanvasHistoryRestoreContextCurrent(get, deleteContext)) {
        return true;
      }
      const { batchUpdateCanvasNodes } = await import('@/lib/api/canvas-nodes');
      if (!isCanvasHistoryRestoreContextCurrent(get, deleteContext)) {
        return true;
      }
      await batchUpdateCanvasNodes(projectId, persistedGroupsToPersist.map((groupState) => ({
        id: groupState.groupId,
        thumbnail_url: getPersistableThumbnailUrl(groupState.thumbnail || null, 'image') ?? null,
        metadata: {
          group_child_ids: groupState.childIds,
          group_collapsed: groupState.collapsed,
          group_label: groupState.label,
          group_thumbnails: groupState.thumbnails,
        },
      })));
      if (!isCanvasHistoryRestoreContextCurrent(get, deleteContext)) {
        return true;
      }
    } catch (error) {
      if (!isCanvasHistoryRestoreContextCurrent(get, deleteContext)) {
        return true;
      }
      console.error('[VisualEditorStore] 删除后 group 拓扑持久化失败:', error);
    }
  }

  return true;
  };

  // History is an optimistic local transaction. A failed background write
  // removes this entry during the delta rollback below, while a successful
  // write leaves Undo available immediately after the click.
  commitDeleteHistory();
  void completeDelete();
  return true;
}

// ==========================================
// ★ 批量节点更新队列（位置 + 属性 + metadata 统一合并，500ms 一次落库）
// ==========================================

type CanvasNodeBatchUpdate = Parameters<typeof import('@/lib/api/canvas-nodes').batchUpdateCanvasNodes>[1][number];

const _pendingNodeUpdates = new Map<string, CanvasNodeBatchUpdate>();
const DEFAULT_BATCH_UPDATE_FLUSH_DELAY_MS = 500;
const MAX_BATCH_UPDATE_RETRY_ATTEMPTS = 5;
let _batchUpdateTimer: ReturnType<typeof setTimeout> | null = null;
let _batchUpdateTimerDueAt: number | null = null;
let _pendingNodeUpdatesProjectId: string | null = null;
let _batchUpdateInFlight = false;
let _batchUpdateNeedsFlush = false;
let _batchUpdateNeedsKeepalive = false;
let _batchUpdateLastError: Error | null = null;
let _batchUpdateRetryProjectId: string | null = null;
let _batchUpdateRetryCount = 0;
const _batchUpdateIdleResolvers = new Set<() => void>();

function _normalizeBatchUpdateError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }
  return new Error(typeof error === 'string' ? error : 'Unknown canvas node batch update error');
}

function _retainCanvasNodeBatchUpdatesWithoutScheduling(
  projectId: string,
  updates: CanvasNodeBatchUpdate[],
): boolean {
  const currentProjectId = useVisualEditorStore.getState().projectId;
  if (currentProjectId !== projectId) {
    return false;
  }
  if (_pendingNodeUpdatesProjectId && _pendingNodeUpdatesProjectId !== projectId) {
    return false;
  }

  _pendingNodeUpdatesProjectId = projectId;
  updates.forEach((update) => {
    const existing = _pendingNodeUpdates.get(update.id);
    _pendingNodeUpdates.set(
      update.id,
      existing ? mergeQueuedCanvasNodeUpdate(update, existing) : update,
    );
  });
  return true;
}

function _clearCanvasNodeBatchUpdateFailure(projectId?: string): void {
  if (projectId && _batchUpdateRetryProjectId !== projectId) {
    return;
  }
  _batchUpdateLastError = null;
  _batchUpdateRetryProjectId = null;
  _batchUpdateRetryCount = 0;
}

function _isPendingCanvasNodeBatchUpdateBlocked(): boolean {
  return Boolean(
    _batchUpdateLastError
    && _batchUpdateRetryProjectId
    && _pendingNodeUpdatesProjectId === _batchUpdateRetryProjectId,
  );
}

function _showCanvasNodeBatchUpdateFailure(
  projectId: string,
  error: Error,
  retry: () => void,
): void {
  const errorShape = error as Error & {
    code?: string;
    requestId?: string;
    severity?: 'info' | 'warning' | 'error';
    userMessageKey?: string;
  };
  let retryStarted = false;
  toast.showError({
    code: errorShape.code || 'UNKNOWN_ERROR',
    message: error.message,
    requestId: errorShape.requestId,
    retryable: true,
    action: 'retry',
    severity: errorShape.severity,
    userMessageKey: errorShape.userMessageKey,
  }, 'canvas', {
    dedupeKey: _getCanvasNodeBatchUpdateFailureDedupeKey(projectId),
    onAction: () => {
      if (retryStarted) return;
      retryStarted = true;
      retry();
    },
  });
}

function _getCanvasNodeBatchUpdateFailureDedupeKey(projectId: string): string {
  return `canvas-node-autosave-failed:${projectId}`;
}

function _dismissCanvasNodeBatchUpdateFailure(projectId: string): void {
  const toastStore = useToastStore.getState();
  const dedupeKey = _getCanvasNodeBatchUpdateFailureDedupeKey(projectId);
  toastStore.toasts
    .filter((item) => item.dedupeKey === dedupeKey)
    .forEach((item) => toastStore.removeToast(item.id));
}

function _retryRetainedOrDetachedCanvasNodeBatchUpdates(
  projectId: string,
  updates: CanvasNodeBatchUpdate[],
): void {
  if (
    useVisualEditorStore.getState().projectId === projectId
    && _pendingNodeUpdatesProjectId === projectId
    && _pendingNodeUpdates.size > 0
  ) {
    _clearCanvasNodeBatchUpdateFailure(projectId);
    _scheduleBatchNodeUpdatesFlush(0);
    return;
  }

  _clearCanvasNodeBatchUpdateFailure(projectId);
  _persistDetachedCanvasNodeBatchUpdates(projectId, updates, 1, { keepalive: true });
}

function _resolveBatchUpdateIdleResolvers(force = false) {
  if (!force && (_batchUpdateInFlight || _batchUpdateTimer || _pendingNodeUpdates.size > 0)) {
    return;
  }

  const resolvers = Array.from(_batchUpdateIdleResolvers);
  _batchUpdateIdleResolvers.clear();
  resolvers.forEach((resolve) => resolve());
}

function _clearScheduledBatchUpdateFlush() {
  if (_batchUpdateTimer) {
    clearTimeout(_batchUpdateTimer);
    _batchUpdateTimer = null;
  }
  _batchUpdateTimerDueAt = null;
}

function _scheduleBatchNodeUpdatesFlush(delayMs: number = DEFAULT_BATCH_UPDATE_FLUSH_DELAY_MS) {
  const normalizedDelayMs = Math.max(0, Math.round(delayMs));
  const dueAt = Date.now() + normalizedDelayMs;

  if (_batchUpdateTimer && _batchUpdateTimerDueAt !== null && _batchUpdateTimerDueAt <= dueAt) {
    return;
  }

  _clearScheduledBatchUpdateFlush();
  _batchUpdateTimerDueAt = dueAt;
  _batchUpdateTimer = setTimeout(() => {
    _batchUpdateTimer = null;
    _batchUpdateTimerDueAt = null;
    void _flushBatchNodeUpdates();
  }, normalizedDelayMs);
}

async function _flushBatchNodeUpdates(options?: { keepalive?: boolean }) {
  const keepalive = options?.keepalive === true;
  let failedRequestRequiresPendingFlush = false;

  if (_batchUpdateInFlight) {
    _batchUpdateNeedsFlush = true;
    _batchUpdateNeedsKeepalive = _batchUpdateNeedsKeepalive || keepalive;
    return;
  }

  if (_pendingNodeUpdates.size === 0 || !_pendingNodeUpdatesProjectId) {
    _resolveBatchUpdateIdleResolvers();
    return;
  }

  const projectId = _pendingNodeUpdatesProjectId;
  const updates = Array.from(_pendingNodeUpdates.values());
  _pendingNodeUpdates.clear();
  _pendingNodeUpdatesProjectId = null;
  _batchUpdateInFlight = true;
  _batchUpdateNeedsKeepalive = false;
  if (_batchUpdateRetryProjectId === projectId) {
    // This request is the scheduled/manual retry for this project. Keep the
    // attempt count, but stop the previous failure from blocking its result.
    _batchUpdateLastError = null;
    _dismissCanvasNodeBatchUpdateFailure(projectId);
  }

  try {
    await _persistCanvasNodeBatchUpdates(projectId, updates, keepalive ? { keepalive: true } : undefined);
    _clearCanvasNodeBatchUpdateFailure(projectId);
  } catch (error) {
    const normalizedError = _normalizeBatchUpdateError(error);
    const retryable = isCanvasNodeBatchUpdateRetryable(error);
    const transition = transitionCanvasNodeBatchQueueAfterFailure({
      failedProjectId: projectId,
      currentProjectId: useVisualEditorStore.getState().projectId,
      pendingProjectId: _pendingNodeUpdatesProjectId,
      retryProjectId: _batchUpdateRetryProjectId,
      retryCount: _batchUpdateRetryCount,
      needsFlush: _batchUpdateNeedsFlush,
      retryable,
      maxAttempts: MAX_BATCH_UPDATE_RETRY_ATTEMPTS,
    });
    failedRequestRequiresPendingFlush = transition.shouldFlushPendingProject;
    const retained = transition.canRetainInActiveQueue
      && _retainCanvasNodeBatchUpdatesWithoutScheduling(projectId, updates);

    if (retained) {
      _batchUpdateLastError = normalizedError;
      _batchUpdateRetryProjectId = projectId;
      _batchUpdateRetryCount = transition.failedAttempt;
    } else if (_batchUpdateRetryProjectId === projectId) {
      _clearCanvasNodeBatchUpdateFailure(projectId);
    }

    if (transition.shouldRetryAutomatically) {
      const retryDelayMs = getBatchNodeUpdateRetryDelayMs(transition.failedAttempt);
      if (retained) {
        _scheduleBatchNodeUpdatesFlush(retryDelayMs);
      } else {
        setTimeout(() => {
          _persistDetachedCanvasNodeBatchUpdates(
            projectId,
            updates,
            transition.failedAttempt + 1,
          );
        }, retryDelayMs);
      }
      console.error('[VisualEditorStore] 批量属性更新失败，将重试:', normalizedError, {
        retryDelayMs,
        retained,
        attempts: transition.failedAttempt,
      });
    } else {
      _showCanvasNodeBatchUpdateFailure(projectId, normalizedError, () => {
        _retryRetainedOrDetachedCanvasNodeBatchUpdates(projectId, updates);
      });
      console.error('[VisualEditorStore] 批量属性更新已停止自动重试:', normalizedError, {
        retryable,
        attempts: transition.failedAttempt,
        retained,
      });
    }
  } finally {
    _batchUpdateInFlight = false;
    if (_isPendingCanvasNodeBatchUpdateBlocked()) {
      _batchUpdateNeedsFlush = false;
      _batchUpdateNeedsKeepalive = false;
      _resolveBatchUpdateIdleResolvers(true);
      return;
    }

    if ((_batchUpdateNeedsFlush || failedRequestRequiresPendingFlush) && _pendingNodeUpdates.size > 0) {
      const nextKeepalive = _batchUpdateNeedsKeepalive;
      _batchUpdateNeedsFlush = false;
      _batchUpdateNeedsKeepalive = false;
      void _flushBatchNodeUpdates(nextKeepalive ? { keepalive: true } : undefined);
    } else {
      _batchUpdateNeedsFlush = false;
      _batchUpdateNeedsKeepalive = false;
      _resolveBatchUpdateIdleResolvers();
    }
  }
}

async function _persistCanvasNodeBatchUpdates(
  projectId: string,
  updates: CanvasNodeBatchUpdate[],
  options?: { keepalive?: boolean },
) {
  const { batchUpdateCanvasNodes } = await import('@/lib/api/canvas-nodes');
  await batchUpdateCanvasNodes(projectId, updates, options?.keepalive ? { keepalive: true } : undefined);
}

function _persistDetachedCanvasNodeBatchUpdates(
  projectId: string,
  updates: CanvasNodeBatchUpdate[],
  attempt = 1,
  options?: { keepalive?: boolean },
) {
  void _persistCanvasNodeBatchUpdates(projectId, updates, options).catch((error) => {
    const normalizedError = _normalizeBatchUpdateError(error);
    if (!isCanvasNodeBatchUpdateRetryable(error) || attempt >= MAX_BATCH_UPDATE_RETRY_ATTEMPTS) {
      _showCanvasNodeBatchUpdateFailure(projectId, normalizedError, () => {
        _persistDetachedCanvasNodeBatchUpdates(projectId, updates, 1, options);
      });
      console.error('[VisualEditorStore] 跨项目待批量节点更新最终失败:', normalizedError, {
        attempts: attempt,
      });
      return;
    }

    const retryDelayMs = getBatchNodeUpdateRetryDelayMs(attempt);
    setTimeout(() => {
      _persistDetachedCanvasNodeBatchUpdates(projectId, updates, attempt + 1, options);
    }, retryDelayMs);
  });
}

async function _flushBatchNodeUpdatesAndWait(options?: { keepalive?: boolean }) {
  const hadScheduledRetry = Boolean(
    _batchUpdateTimer
    && _batchUpdateLastError
    && _batchUpdateRetryProjectId === _pendingNodeUpdatesProjectId,
  );
  _clearScheduledBatchUpdateFlush();

  if (_pendingNodeUpdates.size > 0 && (!_isPendingCanvasNodeBatchUpdateBlocked() || hadScheduledRetry)) {
    await _flushBatchNodeUpdates(options);
  }

  if (
    !_isPendingCanvasNodeBatchUpdateBlocked()
    && (_batchUpdateInFlight || _pendingNodeUpdates.size > 0 || _batchUpdateTimer)
  ) {
    await new Promise<void>((resolve) => {
      _batchUpdateIdleResolvers.add(resolve);
    });
  }

  if (_batchUpdateLastError) {
    throw _batchUpdateLastError;
  }
}

function _enqueueBatchUpdate(
  projectId: string,
  updateData: CanvasNodeBatchUpdate,
  options?: { flushDelayMs?: number },
) {
  if (_pendingNodeUpdatesProjectId && _pendingNodeUpdatesProjectId !== projectId) {
    const previousProjectId = _pendingNodeUpdatesProjectId;
    const previousUpdates = Array.from(_pendingNodeUpdates.values());
    const previousFailure = _batchUpdateRetryProjectId === previousProjectId
      ? _batchUpdateLastError
      : null;
    const previousAutomaticRetryScheduled = Boolean(previousFailure && _batchUpdateTimer);
    const previousNextAttempt = previousAutomaticRetryScheduled
      ? _batchUpdateRetryCount + 1
      : 1;
    console.warn('[VisualEditorStore] 项目已切换，立即持久化旧项目的待批量节点更新');
    _clearScheduledBatchUpdateFlush();
    _pendingNodeUpdates.clear();
    _pendingNodeUpdatesProjectId = null;
    if (previousUpdates.length > 0) {
      if (previousFailure && !previousAutomaticRetryScheduled) {
        _showCanvasNodeBatchUpdateFailure(previousProjectId, previousFailure, () => {
          _persistDetachedCanvasNodeBatchUpdates(
            previousProjectId,
            previousUpdates,
            1,
            { keepalive: true },
          );
        });
      } else if (previousNextAttempt <= MAX_BATCH_UPDATE_RETRY_ATTEMPTS) {
        _persistDetachedCanvasNodeBatchUpdates(
          previousProjectId,
          previousUpdates,
          previousNextAttempt,
          { keepalive: true },
        );
      }
    }
    _clearCanvasNodeBatchUpdateFailure(previousProjectId);
  }
  _pendingNodeUpdatesProjectId = projectId;

  const nodeId = updateData.id;
  const existing = _pendingNodeUpdates.get(nodeId);
  if (existing) {
    _pendingNodeUpdates.set(nodeId, mergeQueuedCanvasNodeUpdate(existing, updateData));
  } else {
    _pendingNodeUpdates.set(nodeId, updateData);
  }

  _scheduleBatchNodeUpdatesFlush(options?.flushDelayMs ?? DEFAULT_BATCH_UPDATE_FLUSH_DELAY_MS);
}

// ==========================================
// ★ Prompt 节点持久化（防抖）
// ==========================================

const _promptSaveTimers = new Map<string, ReturnType<typeof setTimeout>>();
const PROMPT_NODE_SAVE_DELAY_MS = 500;
const PROMPT_NODE_SAVE_PERSISTENCE_RETRY_DELAY_MS = 300;
const PROMPT_NODE_SAVE_MAX_PERSISTENCE_RETRIES = 20;
const PROMPT_NODE_SAVE_MAX_UPDATE_RETRIES = 5;

function _cancelPromptNodeSaveTimer(nodeId: string) {
  const existing = _promptSaveTimers.get(nodeId);
  if (!existing) {
    return;
  }
  clearTimeout(existing);
  _promptSaveTimers.delete(nodeId);
}

function _debouncedUpdatePromptNode(
  projectId: string | null,
  nodeId: string,
  getNode: () => CanvasNode | undefined,
  persistenceRetryCount = 0,
  updateRetryCount = 0,
) {
  _cancelPromptNodeSaveTimer(nodeId);
  _promptSaveTimers.set(nodeId, setTimeout(async () => {
    _promptSaveTimers.delete(nodeId);
    const node = getNode();
    if (!node) return;
    if (!isPersistedCanvasNode(node)) {
      if (persistenceRetryCount < PROMPT_NODE_SAVE_MAX_PERSISTENCE_RETRIES) {
        _debouncedUpdatePromptNode(projectId, nodeId, getNode, persistenceRetryCount + 1, updateRetryCount);
      } else {
        console.warn('[VisualEditorStore] Prompt 节点尚未入库，跳过本次更新:', nodeId);
      }
      return;
    }
    try {
      const { updateCanvasNode } = await import('@/lib/api/canvas-nodes');
      await updateCanvasNode(nodeId, {
        metadata: buildCanvasNodePersistenceMetadata(node),
      }, projectId || undefined);
    } catch (err) {
      if (updateRetryCount < PROMPT_NODE_SAVE_MAX_UPDATE_RETRIES) {
        _debouncedUpdatePromptNode(projectId, nodeId, getNode, 0, updateRetryCount + 1);
        return;
      }
      console.warn('[VisualEditorStore] Prompt 节点更新失败:', err);
    }
  }, (persistenceRetryCount > 0 || updateRetryCount > 0) ? PROMPT_NODE_SAVE_PERSISTENCE_RETRY_DELAY_MS : PROMPT_NODE_SAVE_DELAY_MS));
}

// ==========================================
// Store 实现
// ==========================================

export const useVisualEditorStore = create<VisualEditorStore>()((set, get) => ({
  ...DEFAULT_VISUAL_EDITOR_STATE,
  
  // ==========================================
  // 初始化
  // ==========================================
  
  initialize: (projectId, options) => {
    const currentState = get();
    const matchingPendingVideoEntry = currentState.pendingVideoEntry?.targetProjectId === projectId
      ? currentState.pendingVideoEntry
      : null;
    const initialChatSidebarOpen = options?.chatSidebarOpen
      ?? (currentState.projectId === projectId
        ? currentState.chatSidebarOpen
        : DEFAULT_VISUAL_EDITOR_STATE.chatSidebarOpen);
    // Freeze old timers and queued intents before changing project/epoch ownership.
    // The new owner stays inactive until its authoritative GET has hydrated state.
    _freezeCanvasEdgeQueueOwner(true);
    _clearDeletedNodeSnapshot();
    _resetPersistedCanvasNodeIds();
    clearCanvasHistory();
    // ★ 切换项目时必须清空所有旧数据，防止缓存污染
    set({
      ...DEFAULT_VISUAL_EDITOR_STATE,  // 重置为初始状态
      projectId,
      // React development mode may initialize the same route twice. Preserve the
      // first prepared result instead of reopening the sidebar on the second pass.
      chatSidebarOpen: initialChatSidebarOpen,
      isLoading: false,  // 初始化时不设置 loading，由 loadProjectData 控制
      error: null,
      pendingVideoEntry: matchingPendingVideoEntry,
    });
    _claimCanvasEdgeQueueOwner({ projectId, epoch: _canvasHistoryEpoch });
  },
  
  reset: () => {
    _freezeCanvasEdgeQueueOwner();
    _clearDeletedNodeSnapshot();
    clearCanvasHistory();
    set(DEFAULT_VISUAL_EDITOR_STATE);
    _claimCanvasEdgeQueueOwner({ projectId: null, epoch: _canvasHistoryEpoch });
  },

  setProjectName: (projectName) => {
    set({ projectName });
  },

  setProjectRole: (projectRole) => {
    set({ projectRole });
  },

  setPendingVideoEntry: (entry) => {
    set({
      pendingVideoEntry: entry,
    });
  },

  registerFocusNewNodesCallback: (fn) => {
    _focusNewNodesCallback = fn;
  },

  acknowledgePersistedCanvasNodes: (acknowledgedProjectId, rows) => {
    if (rows.length === 0 || get().projectId !== acknowledgedProjectId) {
      return false;
    }
    let acknowledgedAny = false;
    set((state) => {
      if (state.projectId !== acknowledgedProjectId) {
        return {};
      }
      const liveNodeIds = new Set(state.nodes.map((node) => node.id));
      const matchingRows = rows.filter((row) => (
        row.project_id === acknowledgedProjectId && liveNodeIds.has(row.id)
      ));
      if (matchingRows.length === 0) {
        return {};
      }
      acknowledgedAny = true;
      return {
        nodes: hydratePersistedNodeMetadata(state.nodes, matchingRows),
      };
    });
    if (acknowledgedAny) {
      flushDeferredEdgeAdds(acknowledgedProjectId);
    }
    return acknowledgedAny;
  },

  captureCanvasHistorySnapshot: () => captureCanvasHistorySnapshot(get()),

  pushCanvasHistorySnapshot: (label, snapshot) => {
    rememberCanvasHistoryEntry({
      id: generateUuidLike(),
      label,
      snapshot: cloneCanvasHistoryValue(snapshot),
    });
  },

  commitCanvasHistorySnapshot: (label, snapshot) => rememberCanvasHistorySnapshotIfChanged(label, snapshot, get()),

  canUndoCanvasHistory: () => {
    const topEntry = _canvasHistoryPast[_canvasHistoryPast.length - 1];
    return Boolean((topEntry && !topEntry.pending) || (!topEntry && _lastDeletedNodeSnapshot !== null));
  },

  canRedoCanvasHistory: () => _canvasHistoryFuture.length > 0,

  undoCanvasHistory: async () => {
    const restoreContext: CanvasHistoryRestoreContext = {
      projectId: get().projectId,
      epoch: _canvasHistoryEpoch,
    };
    const topEntry = _canvasHistoryPast[_canvasHistoryPast.length - 1];
    if (topEntry?.pending) {
      return false;
    }
    const entry = _canvasHistoryPast.pop();
    if (!entry) {
      return get().undoLastNodeDeletion();
    }
    const afterSnapshot = entry.afterSnapshot ?? captureCanvasHistorySnapshot(get());
    const restoreResult = await restoreCanvasHistorySnapshot(
      get,
      set,
      entry.snapshot,
      afterSnapshot,
      restoreContext,
    );
    if (restoreResult === 'stale' || !isCanvasHistoryRestoreContextCurrent(get, restoreContext)) {
      return false;
    }
    if (restoreResult === 'failure') {
      _canvasHistoryPast = [..._canvasHistoryPast, entry].slice(-MAX_CANVAS_HISTORY_ENTRIES);
      return false;
    }
    _canvasHistoryFuture = [
      ..._canvasHistoryFuture,
      {
        ...entry,
        snapshot: captureCanvasHistorySnapshot(get()),
        afterSnapshot: cloneCanvasHistoryValue(afterSnapshot),
      },
    ].slice(-MAX_CANVAS_HISTORY_ENTRIES);
    return true;
  },

  redoCanvasHistory: async () => {
    const restoreContext: CanvasHistoryRestoreContext = {
      projectId: get().projectId,
      epoch: _canvasHistoryEpoch,
    };
    const entry = _canvasHistoryFuture.pop();
    if (!entry?.afterSnapshot) {
      return false;
    }
    const restoreResult = await restoreCanvasHistorySnapshot(
      get,
      set,
      entry.afterSnapshot,
      entry.snapshot,
      restoreContext,
    );
    if (restoreResult === 'stale' || !isCanvasHistoryRestoreContextCurrent(get, restoreContext)) {
      return false;
    }
    if (restoreResult === 'failure') {
      _canvasHistoryFuture = [..._canvasHistoryFuture, entry].slice(-MAX_CANVAS_HISTORY_ENTRIES);
      return false;
    }
    _canvasHistoryPast = [..._canvasHistoryPast, entry].slice(-MAX_CANVAS_HISTORY_ENTRIES);
    return true;
  },
  
  // ==========================================
  // ★ 统一节点操作
  // ==========================================
  
  setNodes: (nodes) => {
    const { projectId } = get();
    const liveNodes = _filterConfirmedCanvasDeleteTombstones(projectId, nodes);
    const normalizedNodes = liveNodes.map((node, index) => ensureNodePersistenceFields(node, `setNodes[${index}]`));
    const groupNormalization = normalizeCanvasGroupTopology(normalizedNodes);
    const nextNodes = groupNormalization.nodes;

    _markPersistedCanvasNodeIds(nextNodes
      .filter((node) => typeof node.createdAt === 'string' && node.createdAt.length > 0)
      .map((node) => node.id));

    if (projectId) {
      groupNormalization.removedGroupIds.forEach((groupId) => {
        removeGroup(projectId, groupId);
      });
      groupNormalization.groupsToPersist.forEach((groupState) => {
        saveGroup(projectId, groupState.groupId, {
          childIds: groupState.childIds,
          collapsed: groupState.collapsed,
          label: groupState.label,
          position: groupState.position,
          thumbnail: groupState.thumbnail,
        });
      });
    }

    set({
      nodes: nextNodes,
      currentShotId: nextNodes.length > 0 ? nextNodes[0].id : null,
      isLoading: false,
    });
  },
  
  setCurrentShot: (shotId) => {
    set({ currentShotId: shotId });
  },
  
  updateShotBackground: (shotId, backgroundUpdates) => {
    const { nodes } = get();
    set({
      nodes: nodes.map(n => 
        n.id === shotId 
          ? { ...n, background: { ...(n.background || {}), ...backgroundUpdates } as ShotBackground }
          : n
      ),
    });
  },
  
  // ★ 通用节点字段更新
  updateNode: (nodeId, updates) => {
    const { nodes, projectId } = get();
    const idx = nodes.findIndex((node) => node.id === nodeId);
    // ★ 如果节点已不在 store 中（已被删除），跳过更新和持久化
    if (idx === -1) {
      console.warn('[VisualEditorStore] updateNode 跳过：节点已删除', nodeId);
      return;
    }

    const currentNode = nodes[idx];

    let normalizedUpdates: Partial<CanvasNode> = 'assetId' in updates
      ? {
          ...updates,
          assetId: normalizeOptionalAssetId(
            (updates as Partial<CanvasNode> & { assetId?: unknown }).assetId,
            'VisualEditorStore.updateNode',
            { nodeId },
          ),
        }
      : updates;

    if (!('assetThumbnailUrl' in normalizedUpdates) && (
      ('assetId' in normalizedUpdates && normalizedUpdates.assetId !== currentNode.assetId)
      || ('originalImageUrl' in normalizedUpdates && normalizedUpdates.originalImageUrl !== currentNode.originalImageUrl)
      || ('thumbnail' in normalizedUpdates && normalizedUpdates.thumbnail !== currentNode.thumbnail)
    )) {
      normalizedUpdates = { ...normalizedUpdates, assetThumbnailUrl: undefined };
    }

    if ('storyboardData' in normalizedUpdates) {
      normalizedUpdates = {
        ...normalizedUpdates,
        storyboardData: mergeCanonicalStoryboardData(
          currentNode.storyboardData,
          normalizedUpdates.storyboardData,
        ) as CanvasNode['storyboardData'],
      };
    }

    if (shouldInvalidateStoryboardPanelCacheForImageUpdate(currentNode, normalizedUpdates)) {
      const nextBoardImageUrl = getStoryboardBoardImageUrlForCache({ ...currentNode, ...normalizedUpdates });
      normalizedUpdates = {
        ...normalizedUpdates,
        storyboardData: invalidateStoryboardPanelImageCacheForBoardImage(
          currentNode.storyboardData,
          nextBoardImageUrl,
        ) as Record<string, unknown>,
      };
    }

    if ('generatingError' in normalizedUpdates && !normalizedUpdates.generatingError) normalizedUpdates.entitlementFailure = undefined;
    const nextNode = ensureNodePersistenceFields(
      { ...currentNode, ...normalizedUpdates },
      `updateNode(${nodeId})`,
    );
    const generationGroupIdChanged = !Object.is(currentNode.generationGroupId, nextNode.generationGroupId);
    const enteredCanvasAtChanged = !Object.is(currentNode.enteredCanvasAt, nextNode.enteredCanvasAt);
    const lastActivityAtChanged = !Object.is(currentNode.lastActivityAt, nextNode.lastActivityAt);

    let hasChanges = false;
    for (const key of Object.keys(normalizedUpdates) as Array<keyof CanvasNode>) {
      if (!Object.is(currentNode[key], nextNode[key])) {
        hasChanges = true;
        break;
      }
    }

    if (!hasChanges && (generationGroupIdChanged || enteredCanvasAtChanged || lastActivityAtChanged)) {
      hasChanges = true;
    }

    if (!hasChanges) {
      return;
    }

    const nextNodes = nodes.slice();
    nextNodes[idx] = nextNode;

    set({ nodes: nextNodes });
    // 持久化关键属性变化（用 'in' 检查，因为值可能被设为 undefined 来清除）
    if (projectId && ('position' in normalizedUpdates || 'videoUrl' in normalizedUpdates || 'thumbnail' in normalizedUpdates || 'thumbnailUrl' in normalizedUpdates || 'originalImageUrl' in normalizedUpdates || 'modelUrl' in normalizedUpdates || 'generatingTaskId' in normalizedUpdates || 'generatedByTaskId' in normalizedUpdates || 'duration' in normalizedUpdates || 'sourceDuration' in normalizedUpdates || 'startTime' in normalizedUpdates || 'endTime' in normalizedUpdates || 'sourceStart' in normalizedUpdates || 'sourceEnd' in normalizedUpdates || 'aspectRatio' in normalizedUpdates || 'whRatio' in normalizedUpdates || 'assetId' in normalizedUpdates || 'isEmpty' in normalizedUpdates || 'promptText' in normalizedUpdates || 'promptVariant' in normalizedUpdates || 'promptMetadata' in normalizedUpdates || 'storyboardData' in normalizedUpdates || 'gridPanelEditData' in normalizedUpdates || 'mediaType' in normalizedUpdates || 'decompositionParentId' in normalizedUpdates || '_elementIndex' in normalizedUpdates || 'outputIndex' in normalizedUpdates || 'generatingCapability' in normalizedUpdates || 'generatingError' in normalizedUpdates || 'entitlementFailure' in normalizedUpdates || 'sequenceData' in normalizedUpdates || 'rotation' in normalizedUpdates || 'flipH' in normalizedUpdates || 'flipV' in normalizedUpdates || 'colorAdj' in normalizedUpdates || 'textOverlays' in normalizedUpdates || 'stickerOverlays' in normalizedUpdates || 'generationPrompt' in normalizedUpdates || 'smartEditHistory' in normalizedUpdates || 'sourceLabel' in normalizedUpdates || 'styleAnalysis' in normalizedUpdates || 'templateInstance' in normalizedUpdates || 'directorBoard' in normalizedUpdates || 'lineageSourceNodeIds' in normalizedUpdates || 'lineageRelationType' in normalizedUpdates || 'lineageRelationLabel' in normalizedUpdates || 'artboard' in normalizedUpdates || 'viewportTransform' in normalizedUpdates || 'foregroundMaskUrl' in normalizedUpdates || 'workflowAreaGroupId' in normalizedUpdates || 'enteredCanvasAt' in normalizedUpdates || 'lastActivityAt' in normalizedUpdates || '_preparingCreatedAt' in normalizedUpdates || '_isChainPending' in normalizedUpdates || generationGroupIdChanged || enteredCanvasAtChanged || lastActivityAtChanged)) {
      const node = nextNode;
      if (isPromptNode(node)) {
        // Position has its own debounced write authority. Never couple it to a
        // delayed prompt metadata snapshot, which can arrive after a drag and
        // restore a stale canvas_position on the server.
        if ('position' in normalizedUpdates) {
          _debouncedPositionSave(projectId, nodeId, nextNode.position);
        }
        _debouncedUpdatePromptNode(projectId ?? null, nodeId, () => get().nodes.find(n => n.id === nodeId));
      } else if (isSequenceNode(node) && 'sequenceData' in normalizedUpdates) {
        // Sequence areas store their type-specific data in metadata. Do not
        // discard sibling updates in the same transaction: a storyboard board
        // updates both sequence_data and storyboardData when its panel URLs
        // become ready.
        const updateData: CanvasNodeBatchUpdate = { id: nodeId };
        if ('position' in normalizedUpdates) updateData.canvas_position = normalizedUpdates.position;
        if ('duration' in normalizedUpdates) updateData.duration = normalizedUpdates.duration;
        if ('startTime' in normalizedUpdates) updateData.start_time = normalizedUpdates.startTime;
        if ('endTime' in normalizedUpdates) updateData.end_time = normalizedUpdates.endTime;
        const metaUpdates: Record<string, unknown> = {
          sequence_data: normalizedUpdates.sequenceData || null,
        };
        if ('storyboardData' in normalizedUpdates) metaUpdates.storyboard = normalizedUpdates.storyboardData || null;
        if ('generatedByTaskId' in normalizedUpdates) metaUpdates.generated_by_task_id = normalizedUpdates.generatedByTaskId || null;
        if ('generationCapability' in normalizedUpdates) metaUpdates.generation_capability = normalizedUpdates.generationCapability || null;
        if (generationGroupIdChanged) metaUpdates.generation_group_id = nextNode.generationGroupId || null;
        if ('sourceLabel' in normalizedUpdates) metaUpdates.source_label = normalizedUpdates.sourceLabel || null;
        if ('lineageSourceNodeIds' in normalizedUpdates) metaUpdates.lineage_source_node_ids = normalizedUpdates.lineageSourceNodeIds || null;
        if ('lineageRelationType' in normalizedUpdates) metaUpdates.lineage_relation_type = normalizedUpdates.lineageRelationType || null;
        if ('lineageRelationLabel' in normalizedUpdates) metaUpdates.lineage_relation_label = normalizedUpdates.lineageRelationLabel || null;
        updateData.metadata = metaUpdates;
        _enqueueBatchUpdate(projectId, updateData);
      } else {
        // ★ 构建更新数据，加入批量队列
        const updateData: CanvasNodeBatchUpdate = { id: nodeId };
        if ('position' in normalizedUpdates) updateData.canvas_position = normalizedUpdates.position;
        if ('videoUrl' in normalizedUpdates) updateData.video_url = normalizedUpdates.videoUrl;
        if ('thumbnail' in normalizedUpdates || 'thumbnailUrl' in normalizedUpdates || 'mediaType' in normalizedUpdates) {
          updateData.thumbnail_url = getPersistableThumbnailUrl(
            normalizedUpdates.thumbnailUrl ?? normalizedUpdates.thumbnail ?? nextNode.thumbnailUrl ?? nextNode.thumbnail ?? null,
            nextNode.mediaType,
          ) ?? null;
        }
        if ('duration' in normalizedUpdates) updateData.duration = normalizedUpdates.duration;
        if ('startTime' in normalizedUpdates) updateData.start_time = normalizedUpdates.startTime;
        if ('endTime' in normalizedUpdates) updateData.end_time = normalizedUpdates.endTime;
        if ('assetId' in normalizedUpdates) updateData.asset_id = normalizedUpdates.assetId || null;
        if ('mediaType' in normalizedUpdates) updateData.media_type = normalizedUpdates.mediaType;
        const metaUpdates: Record<string, unknown> = {};
        if ('generatingTaskId' in normalizedUpdates) metaUpdates.generating_task_id = normalizedUpdates.generatingTaskId || null;
        if ('generatingCapability' in normalizedUpdates) metaUpdates.generating_capability = normalizedUpdates.generatingCapability || null;
        if ('generatedByTaskId' in normalizedUpdates) metaUpdates.generated_by_task_id = normalizedUpdates.generatedByTaskId || null;
        if ('generationPrompt' in normalizedUpdates) metaUpdates.generation_prompt = normalizedUpdates.generationPrompt || null;
        if ('generationCapability' in normalizedUpdates) metaUpdates.generation_capability = normalizedUpdates.generationCapability || null;
        if (generationGroupIdChanged) metaUpdates.generation_group_id = nextNode.generationGroupId || null;
        if ('aspectRatio' in normalizedUpdates) metaUpdates.aspect_ratio = normalizedUpdates.aspectRatio;
        if ('whRatio' in normalizedUpdates) metaUpdates.wh_ratio = normalizedUpdates.whRatio;
        if ('originalImageUrl' in normalizedUpdates) metaUpdates.original_image_url = normalizedUpdates.originalImageUrl || null;
        if ('isEmpty' in normalizedUpdates) metaUpdates.is_empty = normalizedUpdates.isEmpty;
        if ('modelUrl' in normalizedUpdates) metaUpdates.model_url = normalizedUpdates.modelUrl || null;
        if ('generatingError' in normalizedUpdates) metaUpdates.generating_error = normalizedUpdates.generatingError || null;
        if ('entitlementFailure' in normalizedUpdates) metaUpdates.entitlement_failure = normalizedUpdates.entitlementFailure || null;
        if ('decompositionParentId' in normalizedUpdates) metaUpdates.decomposition_parent_id = normalizedUpdates.decompositionParentId || null;
        if ('_elementIndex' in normalizedUpdates) metaUpdates.element_index = normalizedUpdates._elementIndex;
        if ('outputIndex' in normalizedUpdates) metaUpdates.output_index = normalizedUpdates.outputIndex;
        if ('_preparingCreatedAt' in normalizedUpdates) metaUpdates.preparing_created_at = normalizedUpdates._preparingCreatedAt || null;
        if ('_isChainPending' in normalizedUpdates) metaUpdates.chain_pending = normalizedUpdates._isChainPending === true;
        if ('rotation' in normalizedUpdates) metaUpdates.rotation = normalizedUpdates.rotation ?? 0;
        if ('flipH' in normalizedUpdates) metaUpdates.flip_h = !!normalizedUpdates.flipH;
        if ('flipV' in normalizedUpdates) metaUpdates.flip_v = !!normalizedUpdates.flipV;
        if ('colorAdj' in normalizedUpdates) metaUpdates.color_adj = normalizedUpdates.colorAdj || null;
        if ('stickerOverlays' in normalizedUpdates) metaUpdates.sticker_overlays = normalizedUpdates.stickerOverlays || [];
        if ('textOverlays' in normalizedUpdates) metaUpdates.text_overlays = normalizedUpdates.textOverlays || null;
        if ('smartEditHistory' in normalizedUpdates) metaUpdates.smart_edit_history = normalizedUpdates.smartEditHistory || null;
        if ('artboard' in normalizedUpdates) metaUpdates.artboard = normalizedUpdates.artboard || null;
        if ('viewportTransform' in normalizedUpdates) metaUpdates.viewport_transform = normalizedUpdates.viewportTransform || null;
        if ('foregroundMaskUrl' in normalizedUpdates) metaUpdates.foreground_mask_url = normalizedUpdates.foregroundMaskUrl || null;
        if ('sourceDuration' in normalizedUpdates) metaUpdates.source_duration = normalizedUpdates.sourceDuration ?? null;
        if ('sourceStart' in normalizedUpdates) metaUpdates.source_start = normalizedUpdates.sourceStart ?? null;
        if ('sourceEnd' in normalizedUpdates) metaUpdates.source_end = normalizedUpdates.sourceEnd ?? null;
        if ('sourceLabel' in normalizedUpdates) metaUpdates.source_label = normalizedUpdates.sourceLabel || null;
        if ('skipStyleAnalysis' in normalizedUpdates) metaUpdates.skip_style_analysis = normalizedUpdates.skipStyleAnalysis || null;
        if ('templateInstance' in normalizedUpdates) metaUpdates.template_instance = normalizedUpdates.templateInstance || null;
        if ('directorBoard' in normalizedUpdates) metaUpdates.director_board = normalizedUpdates.directorBoard || null;
        if ('lineageSourceNodeIds' in normalizedUpdates) metaUpdates.lineage_source_node_ids = normalizedUpdates.lineageSourceNodeIds || null;
        if ('lineageRelationType' in normalizedUpdates) metaUpdates.lineage_relation_type = normalizedUpdates.lineageRelationType || null;
        if ('lineageRelationLabel' in normalizedUpdates) metaUpdates.lineage_relation_label = normalizedUpdates.lineageRelationLabel || null;
        if ('styleAnalysis' in normalizedUpdates) {
          metaUpdates.style_analysis = normalizedUpdates.styleAnalysis
            ? {
                status: normalizedUpdates.styleAnalysis.status,
                analyzed_at: normalizedUpdates.styleAnalysis.analyzedAt ?? null,
                styles: normalizedUpdates.styleAnalysis.styles ?? [],
                moods: normalizedUpdates.styleAnalysis.moods ?? [],
                colors: normalizedUpdates.styleAnalysis.colors ?? [],
                scene: normalizedUpdates.styleAnalysis.scene ?? null,
                lighting: normalizedUpdates.styleAnalysis.lighting ?? null,
                body_framing: normalizedUpdates.styleAnalysis.bodyFraming ?? null,
                quality_score: normalizedUpdates.styleAnalysis.qualityScore ?? null,
                description: normalizedUpdates.styleAnalysis.description ?? null,
                content_type: normalizedUpdates.styleAnalysis.contentType ?? null,
                face_visibility: normalizedUpdates.styleAnalysis.faceVisibility ?? null,
                is_isolated_object: normalizedUpdates.styleAnalysis.isIsolatedObject ?? null,
                use_tags: normalizedUpdates.styleAnalysis.useTags ?? [],
                advise_tags: normalizedUpdates.styleAnalysis.adviseTags ?? [],
                error: normalizedUpdates.styleAnalysis.error ?? null,
              }
            : null;
        }
          if ('promptMetadata' in normalizedUpdates) metaUpdates.prompt_metadata = normalizedUpdates.promptMetadata || null;
          if ('storyboardData' in normalizedUpdates) metaUpdates.storyboard = normalizedUpdates.storyboardData || null;
          if ('gridPanelEditData' in normalizedUpdates) metaUpdates.grid_panel_edit_data = normalizedUpdates.gridPanelEditData || null;
        if ('workflowAreaGroupId' in normalizedUpdates) metaUpdates.workflow_area_group_id = normalizedUpdates.workflowAreaGroupId || null;
        if (enteredCanvasAtChanged) metaUpdates.entered_canvas_at = nextNode.enteredCanvasAt || null;
        if (lastActivityAtChanged) metaUpdates.last_activity_at = nextNode.lastActivityAt || null;
        if (Object.keys(metaUpdates).length > 0) updateData.metadata = metaUpdates;
        _enqueueBatchUpdate(projectId, updateData);
      }
    }
  },

  flushPendingNodeUpdates: async (options) => {
    await _flushBatchNodeUpdatesAndWait(options);
  },

  flushPendingCanvasEdges: async (options) => {
    const { projectId } = get();
    const inactiveProjectIds = [..._suspendedCanvasEdgeQueues.keys()]
      .filter((suspendedProjectId) => suspendedProjectId !== projectId);
    const results = await Promise.allSettled([
      ...(projectId ? [_flushCurrentCanvasEdgeProject(projectId, options)] : []),
      ...inactiveProjectIds.map((suspendedProjectId) => (
        _flushSuspendedCanvasEdgeProject(suspendedProjectId, options)
      )),
    ]);
    const failed = results.find((result): result is PromiseRejectedResult => result.status === 'rejected');
    if (failed) throw failed.reason;
  },

  touchNodesActivity: (nodeIds, activityAt = createCanvasActivityTimestamp()) => {
    if (nodeIds.length === 0) {
      return;
    }

    const { nodes, projectId } = get();
    const uniqueNodeIds = [...new Set(nodeIds.filter(Boolean))];
    if (uniqueNodeIds.length === 0) {
      return;
    }

    const nextNodes = nodes.slice();
    let changed = false;
    const touchedNodeIds = new Set<string>();
    for (let index = 0; index < nextNodes.length; index += 1) {
      const node = nextNodes[index];
      if (!uniqueNodeIds.includes(node.id) || node.lastActivityAt === activityAt) {
        continue;
      }
      nextNodes[index] = ensureNodePersistenceFields({
        ...node,
        lastActivityAt: activityAt,
      }, `touchNodesActivity(${node.id})`);
      touchedNodeIds.add(node.id);
      changed = true;
    }

    if (!changed) {
      return;
    }

    set({ nodes: nextNodes });

    if (!projectId) {
      return;
    }

    touchedNodeIds.forEach((nodeId) => {
      _enqueueBatchUpdate(projectId, {
        id: nodeId,
        metadata: {
          last_activity_at: activityAt,
        },
      });
    });
  },
  
  updateShotArtboard: (shotId, artboardUpdates) => {
    const { nodes, projectId } = get();
    const nodeIndex = nodes.findIndex((node) => node.id === shotId);
    if (nodeIndex === -1) {
      return;
    }

    const currentNode = nodes[nodeIndex];
    const nextArtboard = { ...(currentNode.artboard || DEFAULT_ARTBOARD), ...artboardUpdates } as Artboard;
    if (areArtboardsEqual(currentNode.artboard, nextArtboard)) {
      return;
    }

    const nextNodes = nodes.slice();
    nextNodes[nodeIndex] = { ...currentNode, artboard: nextArtboard };
    set({ nodes: nextNodes });

    if (projectId) {
      _enqueueBatchUpdate(projectId, {
        id: shotId,
        metadata: { artboard: nextArtboard },
      });
    }
  },
  
  updateShotViewport: (shotId, viewportTransform) => {
    const { nodes, projectId } = get();
    const nodeIndex = nodes.findIndex((node) => node.id === shotId);
    if (nodeIndex === -1) {
      return;
    }

    const currentNode = nodes[nodeIndex];
    const nextViewportTransform = [...viewportTransform];
    if (areViewportTransformsEqual(currentNode.viewportTransform, nextViewportTransform)) {
      return;
    }

    const nextNodes = nodes.slice();
    nextNodes[nodeIndex] = { ...currentNode, viewportTransform: nextViewportTransform };
    set({ nodes: nextNodes });

    if (projectId) {
      _enqueueBatchUpdate(projectId, {
        id: shotId,
        metadata: { viewport_transform: nextViewportTransform },
      });
    }
  },
  
  updateShotMask: (shotId, foregroundMaskUrl) => {
    const { nodes, projectId } = get();
    const nodeIndex = nodes.findIndex((node) => node.id === shotId);
    if (nodeIndex === -1) {
      return;
    }

    const currentNode = nodes[nodeIndex];
    if (Object.is(currentNode.foregroundMaskUrl, foregroundMaskUrl)) {
      return;
    }

    const nextNodes = nodes.slice();
    nextNodes[nodeIndex] = { ...currentNode, foregroundMaskUrl };
    set({ nodes: nextNodes });

    if (projectId) {
      _enqueueBatchUpdate(projectId, {
        id: shotId,
        metadata: { foreground_mask_url: foregroundMaskUrl || null },
      });
    }
  },
  
  // ==========================================
  // ★ 统一添加节点（替代 addFreeNodes / addPromptNode）
  // ==========================================
  
  addNodes: async (newNodes, options) => {
    const { nodes, projectId } = get();
    const context = { projectId, epoch: _canvasHistoryEpoch };
    const owner = useAuthStore.getState().user;
    const isCurrent = () => isCanvasHistoryRestoreContextCurrent(get, context)
      && useAuthStore.getState().user === owner;
    const historySnapshot = options?.historyLabel
      ? captureCanvasHistorySnapshot(get())
      : null;
    const normalizedNodes = newNodes.map((node, index) => ensureNodePersistenceFields(node, `addNodes[${index}]`));
    // ★ 去重：跳过 store 中已存在的 ID，防止 race condition / StrictMode 导致 duplicate key
    const existingIds = new Set(nodes.map(n => n.id));
    const existingMaterializationKeys = new Set(
      nodes
        .map((node) => buildCanvasNodeMaterializationKey(node))
        .filter((key): key is string => Boolean(key)),
    );
    const seenIncomingMaterializationKeys = new Set<string>();
    const deduped = normalizedNodes.filter((node) => {
      if (existingIds.has(node.id)) {
        return false;
      }

      const materializationKey = buildCanvasNodeMaterializationKey(node);
      if (!materializationKey) {
        return true;
      }

      if (existingMaterializationKeys.has(materializationKey) || seenIncomingMaterializationKeys.has(materializationKey)) {
        return false;
      }

      seenIncomingMaterializationKeys.add(materializationKey);
      return true;
    });
    if (deduped.length < normalizedNodes.length) {
      console.warn(`[VisualEditorStore] addNodes 跳过 ${normalizedNodes.length - deduped.length} 个重复节点`);
    }
    if (deduped.length === 0) return;
    const merged = [...nodes, ...deduped];
    set({ nodes: merged });
    if (options?.alreadyPersisted) {
      _markPersistedCanvasNodeIds(deduped.map((node) => node.id));
    }
    _notifyNewNodesAdded(deduped, options);
    
    if (projectId && options?.persist !== false) {
      const { batchCreateCanvasNodes } = await import('@/lib/api/canvas-nodes');
      if (!isCurrent()) return;
      const addedIds = new Set(deduped.map(n => n.id));
      // Re-read on every attempt: completion and deletion can happen while awaiting IO.
      const buildPayload = (ids = addedIds) => get().nodes.filter(n => ids.has(n.id)).map(n => {
        const nodeType = isPromptNode(n) ? 'prompt' as const
          : isSequenceNode(n) ? 'sequence' as const
          : 'free' as const;
        return {
          id: n.id,
          asset_id: n.assetId || null,
          node_type: nodeType,
          media_type: (n.mediaType === 'prompt' || n.mediaType === 'sequence' ? 'image' : n.mediaType) as 'video' | 'image' | '3d_model',
          order_index: 0,
          start_time: n.startTime ?? 0,
          end_time: n.endTime ?? n.duration,
          duration: n.duration,
          source_start: n.sourceStart ?? 0,
          source_end: n.sourceEnd ?? Math.round((n.duration || 0) * 1000),
          canvas_position: n.position,
          video_url: n.videoUrl || null,
          thumbnail_url: getPersistableThumbnailUrl(n.thumbnailUrl || n.thumbnail || null, n.mediaType) ?? null,
          metadata: buildCanvasNodePersistenceMetadata(n),
        };
      });
      
      let persisted = false;
      let lastPersistenceError: Error | null = null;
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          if (!isCurrent()) return;
          const payload = buildPayload();
          if (!payload.length) return;
          const response = await batchCreateCanvasNodes(projectId, payload, { respectDeleteIntents: true });
          if (!isCurrent()) return;
          set((state) => ({
            nodes: hydratePersistedNodeMetadata(state.nodes, response.nodes),
          }));
          flushDeferredEdgeAdds(projectId);
          console.log('[VisualEditorStore] ✅ 节点持久化成功');
          persisted = true;
          lastPersistenceError = null;
          break;
        } catch (error) {
          if (!isCurrent()) return;
          if (isProjectQuotaError(error)) {
            // Only remove this rejected addition; keep changes made during IO.
            set(state => ({ nodes: state.nodes.flatMap(node => {
              if (!addedIds.has(node.id)) return [node];
              if (!options?.strictPersistence && (node.generatingTaskId || node.generatedByTaskId)) {
                return [{ ...node, entitlementFailure: readEntitlementFailure(error) ?? undefined,
                  generatingError: getUserFacingErrorMessage(error, 'canvas') }];
              }
              return [];
            }) }));
            if (options?.strictPersistence) throw error;
            if (!options?.background) toast.showError(error, 'canvas');
            return;
          }
          lastPersistenceError = error instanceof Error
            ? error
            : new Error(typeof error === 'string' ? error : 'Failed to batch create canvas nodes');
          console.error(`[VisualEditorStore] 节点持久化失败 (attempt ${attempt}/3):`, lastPersistenceError);
          // 数据格式错误（4xx/5xx）不重试，只重试网络瞬断
          const errMsg = String(lastPersistenceError);
          const isDataError = errMsg.includes('400') || errMsg.includes('422') || errMsg.includes('500');
          if (isDataError || attempt >= 3) break;
          await new Promise(r => setTimeout(r, 500 * attempt));
        }
      }
      if (!isCurrent()) return;
      if (!persisted) {
        const nodeIds = new Set(deduped.map(n => n.id));
        const currentNodes = get().nodes;
        const rollbackSet = new Set(nodeIds);

        if (options?.strictPersistence) {
          set({ nodes: currentNodes.filter(n => !rollbackSet.has(n.id)) });
          throw lastPersistenceError || new Error('Failed to batch create canvas nodes');
        }

        // Task ownership survives completion; a failed create cannot erase its output.
        // 这些节点是 AI 任务流程的一部分，删除它们会导致任务完成后找不到节点
        // 任务完成时 updateNode 会触发 batchUpdate 再次尝试持久化
        const generatingNodeIds = new Set(
          currentNodes.filter(n => nodeIds.has(n.id) && (n.generatingTaskId || n.generatedByTaskId)).map(n => n.id)
        );
        const toRollback = [...nodeIds].filter(id => !generatingNodeIds.has(id));
        if (toRollback.length > 0) {
          const rollbackSet = new Set(toRollback);
          set({ nodes: currentNodes.filter(n => !rollbackSet.has(n.id)) });
          console.error(`[VisualEditorStore] ❌ 节点持久化 3 次均失败，已回滚 ${toRollback.length} 个非任务节点`);
        }
        if (generatingNodeIds.size > 0) {
          console.warn(`[VisualEditorStore] ⚠️ ${generatingNodeIds.size} 个生成节点持久化失败，保留内存状态，5s 后重试`);
          // 延迟重试：给网络/auth 恢复时间
          setTimeout(async () => {
            try {
              if (!isCurrent()) return;
              const { batchCreateCanvasNodes: retryCreate } = await import('@/lib/api/canvas-nodes');
              if (!isCurrent()) return;
              const retryPayload = buildPayload(generatingNodeIds);
              if (!retryPayload.length) return;
              const response = await retryCreate(projectId, retryPayload, { respectDeleteIntents: true });
              if (!isCurrent()) return;
              set((state) => ({
                nodes: hydratePersistedNodeMetadata(state.nodes, response.nodes),
              }));
              flushDeferredEdgeAdds(projectId!);
              console.log('[VisualEditorStore] ✅ 生成节点延迟持久化成功');
            } catch (retryErr) {
              console.error('[VisualEditorStore] ❌ 生成节点延迟持久化仍失败:', retryErr);
            }
          }, 5000);
        }
      }
    }

    if (isCurrent() && historySnapshot && options?.historyLabel) {
      rememberCanvasHistorySnapshotIfChanged(options.historyLabel, historySnapshot, get());
    }
  },
  
  // ★ 统一删除节点
  removeNode: async (nodeId) => {
    await _deleteNodesInternal(get, set, [nodeId], { rememberForUndo: false });
  },

  deleteNodeWithUndo: async (nodeId, options) => {
    if (!nodeId) {
      return false;
    }
    const deleted = await _deleteNodesInternal(get, set, [nodeId], {
      rememberForUndo: false,
      recordDeleteIntent: options?.recordDeleteIntent,
      historyLabel: options?.historyLabel || 'delete-node',
    });
    return deleted;
  },
  
  // ★ 统一位置更新
  updateNodePosition: (nodeId, position) => {
    const { nodes, projectId } = get();
    const idx = nodes.findIndex(n => n.id === nodeId);
    if (idx === -1) return;
    const node = nodes[idx];
    // Store 已是该位置时不重渲染，但仍允许调用方确认后端 authority
    //（例如 API 已预留行、前端随后提交首次避碰位置）。
    if (node.position.x === position.x && node.position.y === position.y) {
      if (projectId) {
        _debouncedPositionSave(projectId, nodeId, position);
      }
      return;
    }
    const newNodes = nodes.slice();
    newNodes[idx] = { ...node, position };
    set({ nodes: newNodes });
    if (projectId) {
      _debouncedPositionSave(projectId, nodeId, position);
    }
  },
  
  // ★ 兼容旧调用名：所有持久节点的位置都必须先更新 store，再写后端。
  persistNodePosition: (nodeId, position) => {
    get().updateNodePosition(nodeId, position);
  },
  
  // ★ 节点锁定
  isNodeLocked: (nodeId) => {
    return get().lockedNodeIds.includes(nodeId);
  },
  toggleNodeLock: (nodeId, locked) => {
    const { lockedNodeIds } = get();
    if (locked) {
      if (!lockedNodeIds.includes(nodeId)) {
        set({ lockedNodeIds: [...lockedNodeIds, nodeId] });
      }
    } else {
      set({ lockedNodeIds: lockedNodeIds.filter(id => id !== nodeId) });
    }
  },

  // ★ 画布连线操作
  setCanvasEdges: (edges) => {
    applyCanvasEdgesState(get, set, edges);
  },

  waitForCanvasEdgeWrites: async (projectId, signal) => {
    if (get().projectId !== projectId) {
      return null;
    }
    // Every authoritative refresh creates a new inactive lease synchronously,
    // freezing debounce/retry intents before this function reaches its first await.
    _freezeCanvasEdgeQueueOwner();
    const context = { projectId, epoch: _canvasHistoryEpoch };
    const token = _claimCanvasEdgeQueueOwner(context);
    if (!token) return null;
    try {
      await _waitForCanvasEdgeWriteDrain(projectId, signal);
    } catch (error) {
      // An aborted waiter owns only this hydration lease, never the writer.
      // Resume the frozen queue when this is still the current token.
      _activateCanvasEdgeHydration(projectId, token);
      throw error;
    }
    const currentLease = _canvasEdgeHydrationLease;
    if (
      signal?.aborted
      || !currentLease
      || currentLease.active
      || currentLease.token !== token
      || currentLease.projectId !== projectId
      || get().projectId !== projectId
      || _canvasHistoryEpoch !== currentLease.epoch
    ) {
      _activateCanvasEdgeHydration(projectId, token);
      return null;
    }
    return currentLease.token;
  },

  isCanvasEdgeHydrationCurrent: (projectId, hydrationToken) => (
    _isCanvasEdgeHydrationTokenCurrent(projectId, hydrationToken)
  ),

  completeCanvasEdgeHydration: (projectId, hydrationToken) => (
    _activateCanvasEdgeHydration(projectId, hydrationToken)
  ),

  cancelCanvasEdgeHydration: (projectId, hydrationToken) => (
    _activateCanvasEdgeHydration(projectId, hydrationToken)
  ),

  replaceCanvasEdges: async (edges, options) => {
    const result = applyCanvasEdgesState(get, set, edges);
    if (!options?.sync || !result.projectId || (!result.changed && !options.forceSync)) {
      return;
    }

    await _runFullCanvasEdgeSyncForState(result.projectId, result.nextNodes, result.normalizedEdges, {
      extraPersistedNodeIds: options.extraPersistedNodeIds,
    });
  },
  
  addCanvasEdge: (edge) => {
    const { canvasEdges, projectId, nodes } = get();
    // ★ 防御：空 source/target 不允许入库（否则后端 UUID 解析报错）
    if (!edge.source || !edge.target) {
      console.warn('[VisualEditorStore] addCanvasEdge: 忽略空 source/target 的边', edge);
      return;
    }
    const nodeById = new Map(nodes.map((node) => [node.id, node] as const));
    const edgeIdentity = buildNormalizedCanvasEdgeIdentity(nodeById, edge);
    const nextMergeKey = buildCanvasEdgeSemanticKey(edgeIdentity);
    const hasSemanticDuplicate = canvasEdges.some((existingEdge) => buildCanvasEdgeMergeKey(nodeById, existingEdge) === nextMergeKey);
    if (hasSemanticDuplicate) {
      return;
    }

    const nextEdge: CanvasEdge = {
      ...edge,
      id: buildCanonicalCanvasEdgeId(edgeIdentity),
      sourceHandle: edgeIdentity.sourceHandle || undefined,
      targetHandle: edgeIdentity.targetHandle || undefined,
      relationType: edgeIdentity.relationType || undefined,
      relationLabel: edgeIdentity.relationLabel || undefined,
    };

    const merged = [...canvasEdges, nextEdge];
    const reconciliation = reconcileTemplateWorkflowBindings(nodes, merged);
    set({
      canvasEdges: merged,
      ...(reconciliation.changedNodeIds.length > 0 ? { nodes: reconciliation.nodes } : {}),
    });
    // ★ 增量持久化：只添加新边
    if (projectId) {
      _addEdge(projectId, {
        id: nextEdge.id,
        source_node_id: nextEdge.source,
        target_node_id: nextEdge.target,
        source_handle: nextEdge.sourceHandle || null,
        target_handle: nextEdge.targetHandle || null,
        relation_type: nextEdge.relationType || null,
        relation_label: nextEdge.relationLabel || null,
      });
      persistTemplateWorkflowBindingCleanup(projectId, reconciliation.nodes, reconciliation.changedNodeIds);
    }
  },
  
  removeCanvasEdge: (edgeId) => {
    const { canvasEdges, projectId, nodes } = get();
    if (!canvasEdges.some((edge) => edge.id === edgeId)) {
      return;
    }
    const filtered = canvasEdges.filter(e => e.id !== edgeId);
    const reconciliation = reconcileTemplateWorkflowBindings(nodes, filtered);
    set({
      canvasEdges: filtered,
      ...(reconciliation.changedNodeIds.length > 0 ? { nodes: reconciliation.nodes } : {}),
    });
    // ★ 增量持久化：只删除指定边
    if (projectId) {
      _removeEdgeById(projectId, edgeId);
      persistTemplateWorkflowBindingCleanup(projectId, reconciliation.nodes, reconciliation.changedNodeIds);
    }
  },
  // ★ 便捷 getters（从 nodes 派生）
  getFloatingNodes: () => getFloatingNodes(get().nodes),
  getPromptNodes: () => getPromptNodes(get().nodes),
  
  // ★ 替换分镜的视频 URL
  replaceShotVideo: async (shotId, assetId) => {
    const { projectId, nodes } = get();
    const node = nodes.find(candidate => candidate.id === shotId);
    if (!node || !projectId) return;
    const context = { projectId, epoch: _canvasHistoryEpoch };
    const owner = useAuthStore.getState().user;
    const isCurrent = () => isCanvasHistoryRestoreContextCurrent(get, context) && useAuthStore.getState().user === owner;
    const revision = node.mediaBindingRevision ?? getCanvasBindingRevision(shotId);
    set({ isSaving: true });
    try {
      const { commandCanvasNodeBinding } = await import('@/lib/api/canvas-nodes');
      if (!isCurrent()) return;
      const row = await commandCanvasNodeBinding(shotId, 'replace-media', revision as number, { asset_id: assetId }, projectId);
      if (!isCurrent()) return;
      set(state => ({ nodes: state.nodes.map(current => current.id === shotId ? applyCanvasBindingRow(current, row) : current) }));
    } catch (error) {
      if (isCurrent()) toast.showError(error, 'canvas');
      throw error;
    } finally {
      if (isCurrent()) set({ isSaving: false });
    }
  },

  // ★ 在指定节点之后插入新节点
  insertShotsAfter: async (afterShotId, newShots, options) => {
    const { nodes, projectId } = get();
    const context = { projectId, epoch: _canvasHistoryEpoch };
    const owner = useAuthStore.getState().user;
    const isCurrent = () => isCanvasHistoryRestoreContextCurrent(get, context)
      && useAuthStore.getState().user === owner;
    console.log('[VisualEditorStore] 插入节点:', { afterShotId, count: newShots.length });
    const normalizedNewShots = newShots.map((node, index) => ensureNodePersistenceFields(node, `insertShotsAfter[${index}]`));
    
    // 合并到节点列表（去重）
    const existingIds = new Set(nodes.map(n => n.id));
    const deduped = normalizedNewShots.filter(n => !existingIds.has(n.id));
    if (deduped.length < normalizedNewShots.length) {
      console.warn(`[VisualEditorStore] insertShotsAfter 跳过 ${normalizedNewShots.length - deduped.length} 个重复节点`);
    }
    if (deduped.length === 0) return;
    set({ nodes: [...nodes, ...deduped] });
    _notifyNewNodesAdded(deduped, options);
    
    const shouldPersist = options?.persist !== false;
    if (!shouldPersist) return;

    try {
      if (!projectId) throw new Error('projectId is not set');
      const { batchCreateCanvasNodes } = await import('@/lib/api/canvas-nodes');
      if (!isCurrent()) return;
      const response = await batchCreateCanvasNodes(projectId, deduped.map((shot, idx) => ({
        id: shot.id,
        asset_id: shot.assetId,
        node_type: 'free' as const,
        media_type: (shot.mediaType === 'prompt' || shot.mediaType === 'audio' || shot.mediaType === 'sequence' ? 'video' : shot.mediaType) || 'video',
        order_index: idx,
        start_time: shot.startTime ?? 0,
        end_time: shot.endTime ?? shot.duration,
        duration: shot.duration,
        thumbnail_url: getPersistableThumbnailUrl(shot.thumbnail || null, shot.mediaType) ?? null,
        video_url: shot.videoUrl || null,
        canvas_position: shot.position,
        metadata: {
          entered_canvas_at: shot.enteredCanvasAt || null,
          last_activity_at: shot.lastActivityAt || null,
          ...(shot.transcript ? { transcript: shot.transcript } : {}),
          ...(shot.originalImageUrl ? { original_image_url: shot.originalImageUrl } : {}),
          ...(shot.generationGroupId ? { generation_group_id: shot.generationGroupId } : {}),
          ...(typeof shot.outputIndex === 'number' ? { output_index: shot.outputIndex } : {}),
          ...(shot.sourceLabel ? { source_label: shot.sourceLabel } : {}),
          ...(shot.lineageSourceNodeIds?.length ? { lineage_source_node_ids: shot.lineageSourceNodeIds } : {}),
          ...(shot.lineageRelationType ? { lineage_relation_type: shot.lineageRelationType } : {}),
          ...(shot.lineageRelationLabel ? { lineage_relation_label: shot.lineageRelationLabel } : {}),
          ...(shot.textOverlays?.length ? { text_overlays: shot.textOverlays } : {}),
          ...(shot.stickerOverlays?.length ? { sticker_overlays: shot.stickerOverlays } : {}),
          ...(typeof shot.sourceDuration === 'number' ? { source_duration: shot.sourceDuration } : {}),
          ...(typeof shot.sourceStart === 'number' ? { source_start: shot.sourceStart } : {}),
          ...(typeof shot.sourceEnd === 'number' ? { source_end: shot.sourceEnd } : {}),
        },
      })));
      if (!isCurrent()) return;
      set((state) => ({
        nodes: hydratePersistedNodeMetadata(state.nodes, response.nodes),
      }));
      flushDeferredEdgeAdds(projectId);
      console.log('[VisualEditorStore] 批量创建 canvas_nodes 成功');
    } catch (error) {
      if (!isCurrent()) return;
      const rejectedIds = new Set(deduped.map(node => node.id));
      set(state => state.projectId === projectId
        ? { nodes: state.nodes.filter(node => !rejectedIds.has(node.id)) } : {});
      if (get().projectId === projectId) toast.showError(error, 'canvas');
    }
  },
  
  // ★ 批量删除节点
  batchDeleteNodes: async (nodeIds, options) => {
    return _deleteNodesInternal(get, set, nodeIds, {
      rememberForUndo: false,
      recordDeleteIntent: options?.recordDeleteIntent,
      historyLabel: options?.historyLabel,
    });
  },

  deleteNodesWithUndo: async (nodeIds, options) => {
    const normalizedNodeIds = [...new Set(nodeIds.filter(Boolean))];
    if (normalizedNodeIds.length === 0) {
      return false;
    }
    const deleted = await _deleteNodesInternal(get, set, normalizedNodeIds, {
      rememberForUndo: false,
      recordDeleteIntent: options?.recordDeleteIntent,
      historyLabel: options?.historyLabel || 'delete-nodes',
    });
    return deleted;
  },

  undoLastNodeDeletion: async () => {
    const snapshot = _lastDeletedNodeSnapshot;
    const { projectId } = get();
    if (!snapshot || !projectId || snapshot.nodeIds.length === 0) {
      return false;
    }

    try {
      const context = { projectId, epoch: _canvasHistoryEpoch };
      const owner = useAuthStore.getState().user;
      const isCurrent = () => isCanvasHistoryRestoreContextCurrent(get, context) && useAuthStore.getState().user === owner;
      const revisions = new Map(snapshot.nodes.map(node => [node.id, node.mediaBindingRevision ?? getCanvasBindingRevision(node.id)]));
      const { restoreCanvasNodes, listCanvasNodes } = await import('@/lib/api/canvas-nodes');
      if (!isCurrent()) return false;
      await restoreCanvasNodes(projectId, snapshot.nodeIds, revisions as Map<string, number>, isCurrent);
      if (!isCurrent()) return false;
      const result = await listCanvasNodes(projectId);
      if (!isCurrent()) return false;

      const allMediaNodes = [
        ...(result.sequence_nodes ?? []),
        ...(result.free_nodes ?? []),
      ];
      const restoredNodeIds = new Set(snapshot.nodeIds);
      const restoredNodes = allMediaNodes.filter((row) => restoredNodeIds.has(row.id));

      if (restoredNodes.length === 0) {
        _clearDeletedNodeSnapshot();
        return false;
      }

      _clearConfirmedCanvasDeleteTombstones(projectId, restoredNodes.map((row) => row.id));
      _markPersistedCanvasNodeIds(restoredNodes.map((row) => row.id));

      const currentNodes = get().nodes;
      const existingIds = new Set(currentNodes.map((node) => node.id));
      const snapshotNodeById = new Map(snapshot.nodes.map((node) => [node.id, node]));
      const normalizedNodes = restoredNodes
        .filter((row) => !existingIds.has(row.id))
        .map((row, index) => {
          const snapshotNode = snapshotNodeById.get(row.id);
          if (snapshotNode) {
            return ensureNodePersistenceFields({
              ...applyCanvasBindingRow(snapshotNode, row),
            }, `undoLastNodeDeletion[${index}]`);
          }
          return ensureNodePersistenceFields({
            id: row.id,
            mediaType: row.media_type === '3d_model' ? '3d_model' : row.media_type === 'video' ? 'video' : 'image',
            duration: row.duration,
            position: row.canvas_position || { x: 0, y: 0 },
            thumbnail: row.thumbnail_url || undefined,
            thumbnailUrl: row.thumbnail_url || undefined,
            assetThumbnailUrl: row.asset_thumbnail_url || undefined,
            videoUrl: row.video_url || undefined,
            assetId: row.asset_id || undefined,
            startTime: row.start_time,
            endTime: row.end_time,
            sourceStart: row.source_start,
            sourceEnd: row.source_end,
            createdAt: row.created_at,
            updatedAt: row.updated_at,
          } as CanvasNode, `undoLastNodeDeletionFallback[${index}]`);
        });

      const currentEdges = get().canvasEdges;
      const existingEdgeIds = new Set(currentEdges.map((edge) => edge.id));
      const restoredEdges = (result.canvas_edges ?? [])
        .filter((edge) => (
          restoredNodeIds.has(edge.source_node_id)
          || restoredNodeIds.has(edge.target_node_id)
        ))
        .filter((edge) => !existingEdgeIds.has(edge.id))
        .map((edge) => ({
          id: edge.id,
          source: edge.source_node_id,
          target: edge.target_node_id,
          sourceHandle: edge.source_handle || undefined,
          targetHandle: edge.target_handle || undefined,
          relationType: (edge.relation_type as CanvasEdge['relationType'] | null) || undefined,
          relationLabel: edge.relation_label || undefined,
          createdAt: edge.created_at,
        }));

      const currentState = get();
      const mergedNodes = mergeCanvasNodesById(currentState.nodes, normalizedNodes);
      const mergedEdges = mergeCanvasEdgesForNodes(mergedNodes, currentState.canvasEdges, restoredEdges);
      const reconciliation = reconcileTemplateWorkflowBindings(mergedNodes, mergedEdges);

      set({
        nodes: reconciliation.nodes,
        canvasEdges: mergedEdges,
        selectedClipIdForAI: snapshot.selectedClipIdForAI && restoredNodeIds.has(snapshot.selectedClipIdForAI)
          ? snapshot.selectedClipIdForAI
          : currentState.selectedClipIdForAI,
        templateInspectorNodeId: snapshot.templateInspectorNodeId && restoredNodeIds.has(snapshot.templateInspectorNodeId)
          ? snapshot.templateInspectorNodeId
          : currentState.templateInspectorNodeId,
        smartEditNodeId: snapshot.smartEditNodeId && restoredNodeIds.has(snapshot.smartEditNodeId)
          ? snapshot.smartEditNodeId
          : currentState.smartEditNodeId,
        lockedNodeIds: Array.from(new Set([...currentState.lockedNodeIds, ...snapshot.lockedNodeIds])),
      });

      if (reconciliation.changedNodeIds.length > 0) {
        persistTemplateWorkflowBindingCleanup(currentState.projectId, reconciliation.nodes, reconciliation.changedNodeIds);
      }

      _clearDeletedNodeSnapshot();
      return true;
    } catch (error) {
      console.error('[VisualEditorStore] 撤销删除失败:', error);
      return false;
    }
  },

  // ★ 节点分组：将选中节点折叠为一个 group 容器
  groupNodes: async (nodeIds, label) => {
    if (nodeIds.length < 2) return false;
    const { nodes, projectId } = get();
    const uniqueNodeIds = Array.from(new Set(nodeIds));
    const childNodes = nodes.filter(n => uniqueNodeIds.includes(n.id));
    if (childNodes.length < 2) return false;
    // Group ownership is flat. Never nest a group or re-group an existing
    // member: either would make collapse visibility and deletion scope unclear.
    if (childNodes.some((node) => node.groupId || node.groupChildIds?.length)) return false;

    const cx = childNodes.reduce((s, n) => s + n.position.x, 0) / childNodes.length;
    const cy = childNodes.reduce((s, n) => s + n.position.y, 0) / childNodes.length;
    const thumbs = childNodes.map(n => n.thumbnail).filter((t): t is string => !!t).slice(0, 4);

    const groupId = generateUuidLike();
    const groupNode: CanvasNode = ensureNodePersistenceFields({
      id: groupId,
      mediaType: 'image',
      duration: 0,
      position: { x: cx, y: cy },
      groupChildIds: uniqueNodeIds,
      groupCollapsed: true,
      groupLabel: label || `${childNodes.length} items`,
      thumbnail: thumbs[0],
    }, 'groupNodes');

    const updatedNodes = nodes.map(n => {
      if (uniqueNodeIds.includes(n.id)) return { ...n, groupId };
      return n;
    });

    if (projectId) {
      const { createCanvasGroup } = await import('@/lib/api/canvas-nodes');
      await createCanvasGroup(projectId, {
        id: groupId,
        child_node_ids: uniqueNodeIds,
        canvas_position: { x: cx, y: cy },
        group_label: groupNode.groupLabel,
      });
      _markPersistedCanvasNodeIds([groupId]);
    }

    set({ nodes: [...updatedNodes, groupNode] });

    if (projectId) {
      saveGroup(projectId, groupId, {
        childIds: uniqueNodeIds,
        collapsed: true,
        label: groupNode.groupLabel || `${uniqueNodeIds.length} items`,
        position: { x: cx, y: cy },
        thumbnail: thumbs[0],
      });
    }

    return true;
  },

  // ★ 解散分组：恢复子节点独立状态，移除 group 容器
  ungroupNodes: async (groupNodeId) => {
    const { nodes, projectId } = get();
    const groupNode = nodes.find(n => n.id === groupNodeId);
    if (!groupNode?.groupChildIds) return false;

    const childIds = new Set(groupNode.groupChildIds);
    const updatedNodes = nodes
      .filter(n => n.id !== groupNodeId)
      .map(n => {
        if (childIds.has(n.id)) {
          const rest = { ...n };
          delete rest.groupId;
          return rest;
        }
        return n;
      });
    const normalizedNodes = normalizeCanvasGroupTopology(updatedNodes).nodes;

    if (projectId) {
      const { ungroupCanvasNodes } = await import('@/lib/api/canvas-nodes');
      await ungroupCanvasNodes(projectId, groupNodeId);
      _unmarkPersistedCanvasNodeIds([groupNodeId]);
    }

    set({ nodes: normalizedNodes });

    if (projectId) {
      syncGroupStorageFromNodes(projectId, normalizedNodes);
    }

    return true;
  },

  // ★ 切换 group 折叠/展开状态
  toggleGroupCollapse: async (groupNodeId) => {
    const { nodes, projectId } = get();
    const groupNode = nodes.find(n => n.id === groupNodeId);
    if (!groupNode?.groupChildIds) return false;

    const newCollapsed = !groupNode.groupCollapsed;
    const updatedNodes = nodes.map(n => {
      if (n.id === groupNodeId) {
        return { ...n, groupCollapsed: newCollapsed };
      }
      return n;
    });

    if (projectId) {
      const { updateCanvasNode } = await import('@/lib/api/canvas-nodes');
      await updateCanvasNode(groupNodeId, { metadata: { group_collapsed: newCollapsed } }, projectId);
    }

    set({ nodes: updatedNodes });

    if (projectId) {
      updateGroupCollapsed(projectId, groupNodeId, newCollapsed);
    }

    return true;
  },
  
  
  // ==========================================
  // 图层操作
  // ==========================================
  
  addLayer: (shotId, layer) => {
    const { nodes } = get();
    set({
      nodes: nodes.map(shot => 
        shot.id === shotId 
          ? { ...shot, layers: [...(shot.layers || []), layer] }
          : shot
      ),
    });
  },
  
  removeLayer: (shotId, layerId) => {
    const { nodes } = get();
    set({
      nodes: nodes.map(shot => 
        shot.id === shotId 
          ? { ...shot, layers: (shot.layers || []).filter(l => l.id !== layerId) }
          : shot
      ),
    });
  },
  
  updateLayer: (shotId, layerId, updates) => {
    const { nodes } = get();
    set({
      nodes: nodes.map(shot => 
        shot.id === shotId 
          ? {
              ...shot,
              layers: (shot.layers || []).map(layer => 
                layer.id === layerId ? { ...layer, ...updates } : layer
              ),
            }
          : shot
      ),
    });
  },
  
  reorderLayers: (shotId, layerIds) => {
    const { nodes } = get();
    set({
      nodes: nodes.map(shot => {
        if (shot.id !== shotId) return shot;
        
        const layerMap = new Map((shot.layers || []).map(l => [l.id, l]));
        const reorderedLayers = layerIds
          .map(id => layerMap.get(id))
          .filter((l): l is Layer => l !== undefined);
        
        return { ...shot, layers: reorderedLayers };
      }),
    });
  },
  
  // ==========================================
  // 画布对象操作
  // ==========================================
  
  addObject: (shotId, layerId, object) => {
    const { nodes } = get();
    set({
      nodes: nodes.map(shot => 
        shot.id === shotId 
          ? {
              ...shot,
              layers: (shot.layers || []).map(layer => 
                layer.id === layerId 
                  ? { ...layer, objects: [...layer.objects, object] }
                  : layer
              ),
            }
          : shot
      ),
    });
  },
  
  removeObject: (shotId, layerId, objectId) => {
    const { nodes } = get();
    set({
      nodes: nodes.map(shot => 
        shot.id === shotId 
          ? {
              ...shot,
              layers: (shot.layers || []).map(layer => 
                layer.id === layerId 
                  ? { ...layer, objects: layer.objects.filter(o => o.id !== objectId) }
                  : layer
              ),
            }
          : shot
      ),
    });
  },
  
  updateObject: (shotId, layerId, objectId, updates) => {
    const { nodes } = get();
    set({
      nodes: nodes.map(shot => 
        shot.id === shotId 
          ? {
              ...shot,
              layers: (shot.layers || []).map(layer => 
                layer.id === layerId 
                  ? {
                      ...layer,
                      objects: layer.objects.map(obj => 
                        obj.id === objectId ? { ...obj, ...updates } as CanvasObject : obj
                      ),
                    }
                  : layer
              ),
            }
          : shot
      ),
    });
  },
  
  // ==========================================
  // 选择操作
  // ==========================================
  
  selectObjects: (objectIds) => {
    set(state => {
      const currentIds = state.canvas.selectedObjectIds;
      if (hasSameSelectedObjectIds(currentIds, objectIds)) {
        return state;
      }

      return {
        canvas: { ...state.canvas, selectedObjectIds: objectIds },
      };
    });
  },
  
  clearSelection: () => {
    set(state => {
      if (state.canvas.selectedObjectIds.length === 0) {
        return state;
      }

      return {
        canvas: { ...state.canvas, selectedObjectIds: [] },
      };
    });
  },
  
  // ==========================================
  // 工具操作
  // ==========================================
  
  setActiveTool: (activeTool) => {
    set(state => ({
      tool: { ...state.tool, activeTool },
    }));
  },
  
  setBrushColor: (brushColor) => {
    set(state => ({
      tool: { ...state.tool, brushColor },
    }));
  },
  
  setBrushSize: (brushSize) => {
    set(state => ({
      tool: { ...state.tool, brushSize },
    }));
  },
  
  setFillColor: (fillColor) => {
    set(state => ({
      tool: { ...state.tool, fillColor },
    }));
  },
  
  setStrokeColor: (strokeColor) => {
    set(state => ({
      tool: { ...state.tool, strokeColor },
    }));
  },
  
  setStrokeWidth: (strokeWidth) => {
    set(state => ({
      tool: { ...state.tool, strokeWidth },
    }));
  },
  
  // ==========================================
  // 画布操作
  // ==========================================
  
  setZoom: (zoom) => {
    const clampedZoom = Math.max(0.1, Math.min(3, zoom));
    set((state) => {
      if (state.canvas.zoom === clampedZoom) {
        return state;
      }

      return {
        canvas: { ...state.canvas, zoom: clampedZoom },
      };
    });
  },
  
  setPan: (panX, panY) => {
    set((state) => {
      if (state.canvas.panX === panX && state.canvas.panY === panY) {
        return state;
      }

      return {
        canvas: { ...state.canvas, panX, panY },
      };
    });
  },
  
  resetView: () => {
    set(state => ({
      canvas: { ...state.canvas, zoom: 1, panX: 0, panY: 0 },
    }));
  },
  
  // ==========================================
  // UI 状态
  // ==========================================
  
  setLeftPanelTab: (leftPanelTab) => {
    set({ leftPanelTab });
  },
  
  setRightPanelTab: (rightPanelTab) => {
    set({ rightPanelTab });
  },
  
  setIsPlaying: (isPlaying) => {
    set({ isPlaying });
  },
  setTimelineState: (timeline) => {
    set({ timeline });
  },
  setTimelinePanelState: (panelState) => {
    set((state) => ({
      timeline: {
        ...state.timeline,
        panelState,
      },
    }));
  },
  buildTimelineFromSequence: (_sequenceId, sequenceData) => {
    const nodeMap = new Map(get().nodes.map((node) => [node.id, node]));
    const segments: TimelineSegment[] = sequenceData.clips.map((clip, index) => {
      const sourceNode = nodeMap.get(clip.sourceNodeId);
      return {
        id: clip.id,
        sourceNodeId: clip.sourceNodeId,
        order: index,
        mediaType: clip.mediaType,
        durationMs: clip.duration,
        thumbnail: clip.thumbnail || sourceNode?.thumbnail,
        label: clip.label || clip.prompt || `Shot ${index + 1}`,
        mediaUrl: clip.videoUrl || sourceNode?.videoUrl || sourceNode?.originalImageUrl || sourceNode?.thumbnail,
        transcript: clip.prompt,
      };
    });
    const totalDurationMs = segments.reduce((sum, segment) => sum + segment.durationMs, 0);
    set((state) => ({
      timeline: {
        segments,
        totalDurationMs,
        panelState: segments.length > 0 && state.timeline.panelState === 'collapsed'
          ? 'half'
          : state.timeline.panelState,
      },
    }));
  },
  
  // ==========================================
  // Chat 侧边栏 — 默认独立开关；进入 Smart Edit 时显式收起
  // ==========================================
  
  openChatSidebar: () => set((state) => ({
    chatSidebarOpen: true,
    ...(state.activeSidebar === 'templateInspector'
      ? {
          activeSidebar: null,
          templateInspectorNodeId: null,
        }
      : {}),
  })),
  closeChatSidebar: () => set({ chatSidebarOpen: false }),

  // ★ Sequence 聚焦模式
  openSequenceEditor: (nodeId) => set({
    activeSequenceEditorId: nodeId,
    sequenceFocusMode: true,
    chatSidebarOpen: false,
  }),
  closeSequenceEditor: () => set({
    activeSequenceEditorId: null,
    sequenceFocusMode: false,
  }),
  enterSequenceFocus: () => set({ sequenceFocusMode: true, chatSidebarOpen: false }),
  exitSequenceFocus: () => set({ sequenceFocusMode: false, activeSequenceEditorId: null }),
  setCanvasInteractionMode: (mode) => set({ canvasInteractionMode: mode }),
  
  // ==========================================
  // 侧边栏统一管理 - 同时只显示一个（非 Chat 面板）
  // ==========================================
  
  openSidebar: async (sidebar, clipId) => {
    // openSidebar('chat') → 转为 openChatSidebar()，保持向后兼容
    if (sidebar === 'chat') {
      get().openChatSidebar();
      return;
    }
    const { activeSidebar } = get();
    if (activeSidebar === 'smartEdit' && sidebar !== 'smartEdit' && !(await _shouldCloseSmartEditSession())) {
      return;
    }
    set({
      activeSidebar: sidebar,
      selectedClipIdForAI: clipId || null,
      templateInspectorNodeId: null,
      ...(sidebar === 'smartEdit'
        ? {}
        : {
            ..._getSmartEditSessionResetState(),
          }),
    });
    if (sidebar !== 'smartEdit') {
      _smartEditSubmitHandler = null;
      _smartEditCloseHandler = null;
    }
  },
  
  closeSidebar: async () => {
    if (get().activeSidebar === 'smartEdit' && !(await _shouldCloseSmartEditSession())) {
      return;
    }
    // ★ Smart Edit 收起后回到底部 ChatInput，不恢复 chat 侧栏
    set((state) => ({
      activeSidebar: null,
      selectedClipIdForAI: null,
      templateInspectorNodeId: null,
      ..._getSmartEditSessionResetState(),
      ...(state.activeSidebar === 'smartEdit' ? { chatSidebarOpen: false } : {}),
    }));
    _smartEditSubmitHandler = null;
    _smartEditCloseHandler = null;
  },

  exitSmartEditToChat: async () => {
    if (get().activeSidebar === 'smartEdit' && !(await _shouldCloseSmartEditSession())) {
      return;
    }
    set((state) => ({
      activeSidebar: null,
      selectedClipIdForAI: null,
      templateInspectorNodeId: null,
      ..._getSmartEditSessionResetState(),
      chatSidebarOpen: state.activeSidebar === 'smartEdit' ? true : state.chatSidebarOpen,
    }));
    _smartEditSubmitHandler = null;
    _smartEditCloseHandler = null;
  },

  beginDedicatedSmartEditSession: (nodeId: string) => {
    _smartEditSubmitHandler = null;
    _smartEditCloseHandler = null;
    set({
      // 独立编辑页由 URL 决定是否打开，不能再借用 Canvas 的 sidebar/focus 状态。
      activeSidebar: null,
      ..._getSmartEditSessionResetState(),
      smartEditNodeId: nodeId,
      templateInspectorNodeId: null,
      selectedClipIdForAI: null,
    });
  },

  closeDedicatedSmartEditSession: async () => {
    if (!(await _shouldCloseSmartEditSession())) {
      return false;
    }
    set({
      ..._getSmartEditSessionResetState(),
      activeSidebar: null,
      templateInspectorNodeId: null,
      selectedClipIdForAI: null,
    });
    _smartEditSubmitHandler = null;
    _smartEditCloseHandler = null;
    return true;
  },
  
  toggleSidebar: async (sidebar, clipId) => {
    if (sidebar === 'chat') {
      const state = get();
      if (state.activeSidebar === 'templateInspector') {
        set({
          activeSidebar: null,
          templateInspectorNodeId: null,
          chatSidebarOpen: true,
        });
        return;
      }
      // 读取当前值，明确切换
      set({ chatSidebarOpen: !state.chatSidebarOpen });
      return;
    }
    const { activeSidebar } = get();
    if (activeSidebar === 'smartEdit' && !(await _shouldCloseSmartEditSession())) {
      return;
    }
    if (activeSidebar === sidebar) {
      set({
        activeSidebar: null,
        selectedClipIdForAI: null,
        templateInspectorNodeId: null,
        ..._getSmartEditSessionResetState(),
      });
      if (sidebar === 'smartEdit') {
        _smartEditSubmitHandler = null;
        _smartEditCloseHandler = null;
      }
    } else {
      set({
        activeSidebar: sidebar,
        selectedClipIdForAI: clipId || null,
        templateInspectorNodeId: null,
        ...(sidebar === 'smartEdit'
          ? {}
          : {
              ..._getSmartEditSessionResetState(),
            }),
      });
      if (sidebar !== 'smartEdit') {
        _smartEditSubmitHandler = null;
        _smartEditCloseHandler = null;
      }
    }
  },

  openSmartEdit: (nodeId: string) => {
    _smartEditSubmitHandler = null;
    _smartEditCloseHandler = null;
    set({
      activeSidebar: 'smartEdit',
      chatSidebarOpen: false,
      ..._getSmartEditSessionResetState(),
      smartEditNodeId: nodeId,
      templateInspectorNodeId: null,
      selectedClipIdForAI: null,
    });
  },

  openTemplateInspector: async (nodeId: string) => {
    if (!nodeId) {
      return;
    }
    if (get().activeSidebar === 'smartEdit' && !(await _shouldCloseSmartEditSession())) {
      return;
    }
    _smartEditSubmitHandler = null;
    _smartEditCloseHandler = null;
    set({
      activeSidebar: 'templateInspector',
      chatSidebarOpen: false,
      selectedClipIdForAI: null,
      templateInspectorNodeId: nodeId,
      ..._getSmartEditSessionResetState(),
    });
  },

  setSmartEditTool: (tool) => set({ smartEditTool: tool }),
  setSmartEditBrushSize: (size) => set({ smartEditBrushSize: size }),
  setSmartEditBrushColor: (color) => set({ smartEditBrushColor: color }),
  setSmartEditMaskDataUrl: (url) => set({ smartEditMaskDataUrl: url }),
  setSmartEditPrompt: (prompt) => set({ smartEditPrompt: prompt }),
  setSmartEditGlobalInstruction: (prompt) => set({ smartEditGlobalInstruction: prompt }),
  syncSmartEditComposerState: ({ pendingCount, hasLocalChanges, localEditsDone, submitting }) => set({
    smartEditPendingCount: pendingCount,
    smartEditHasLocalChanges: hasLocalChanges,
    smartEditLocalEditsDone: localEditsDone,
    smartEditSubmitting: submitting,
  }),
  registerSmartEditSubmitHandler: (handler) => {
    _smartEditSubmitHandler = handler;
  },
  registerSmartEditCloseHandler: (handler) => {
    _smartEditCloseHandler = handler;
  },
  submitSmartEdit: () => {
    void _smartEditSubmitHandler?.();
  },

  setDraggingToChat: (dragging) => {
    set({ isDraggingToChat: dragging });
  },
  
  
  // ==========================================
  // 加载状态
  // ==========================================
  
  setIsLoading: (isLoading) => {
    set({ isLoading });
  },
  
  setIsAnalyzing: (isAnalyzing) => {
    set({ isAnalyzing });
  },
  
  setIsSaving: (isSaving) => {
    set({ isSaving });
  },
  
  setError: (error) => {
    set({ error });
  },
}));

if (typeof window !== 'undefined') {
  (window as Window & { __octolab_visualEditorStore?: typeof useVisualEditorStore }).__octolab_visualEditorStore = useVisualEditorStore;
}

// ==========================================
// 选择器 Hooks
// ==========================================

export function useCurrentShot(): CanvasNode | null {
  const { nodes, currentShotId } = useVisualEditorStore();
  return nodes.find(s => s.id === currentShotId) || null;
}

export function useSelectedObjects(): CanvasObject[] {
  const { nodes, currentShotId, canvas } = useVisualEditorStore();
  const currentShot = nodes.find(s => s.id === currentShotId);
  
  if (!currentShot) return [];
  
  const allObjects: CanvasObject[] = [];
  for (const layer of (currentShot.layers || [])) {
    allObjects.push(...layer.objects);
  }
  
  return allObjects.filter(obj => canvas.selectedObjectIds.includes(obj.id));
}
